import React, { useEffect, useRef, useState } from "react";
import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Button,
  createStyles,
  Divider,
  FileInput,
  Flex,
  Grid,
  Group,
  Loader,
  Modal,
  NumberInput,
  Paper,
  Progress,
  RingProgress,
  ScrollArea,
  Select,
  SimpleGrid,
  Switch,
  Table,
  Text,
  TextInput,
  Textarea,
  Tooltip,
  Popover,
  Drawer,
  Menu,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useDisclosure } from "@mantine/hooks";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import store from "../../../store";
import { setSelectedDocModal, setUploadDocModal } from "../../../store/reducers/document";
import {
  IconSearch, IconEdit, IconTrash, IconFileDownload,
  IconShieldCheck, IconClipboardCheck, IconCheckupList,
  IconAlertTriangle, IconCalendar, IconBuildingHospital,
  IconChevronDown, IconChevronRight, IconChevronLeft, IconSettings, IconFileAnalytics, IconReportAnalytics, IconClipboardList,
  IconCircleFilled, IconCircleCheck, IconClock, IconChartBar,
  IconChecklist, IconUrgent, IconUsers,
  IconX, IconCoin, IconLayoutDashboard, IconCheck, IconDownload, IconPill, IconDotsVertical,
  IconTrendingUp, IconTrendingDown,
  IconFileInvoice, IconReceipt, IconAlertCircle,
} from "@tabler/icons-react";
import { FaPlus } from "react-icons/fa6";
import moment from "moment";
import swal from "sweetalert";
import RecordNotFound from "../../../components/RecordNotFound";
import { NewAddButton } from "../../../components/AddButton/NewAddButton";
import PendingActionsDrawer from "./PendingActionsDrawer";
import FacilityHealthDrawer from "./FacilityHealthDrawer";
import StaffTasksDrawer from "./StaffTasksDrawer";
import CommandCenterSetupWizard from "./CommandCenterSetupWizard";
import ComplianceTaskModal from "../../../components/ComplianceTaskModal";
import NoteModalNew from "../../../components/NoteModal/NoteModalNew";
import {
  getComplianceDashboard,
  getCredentialTypes,
  getStaffCredentialsOverview,
  getFacilityDocumentsOverview,
  getClientDocumentsOverview,
  getClientComplianceItems,
  getFacilityComplianceItems,
  getComplianceSettings,
  updateComplianceSettings,
  addCredentialType,
  addClientComplianceItem,
  updateClientComplianceItem,
  deleteClientComplianceItem,
  addFacilityComplianceItem,
  updateFacilityComplianceItem,
  deleteFacilityComplianceItem,
  runComplianceAudit,
  getOperationalTasks,
  deleteOperationalTask,
  updateOperationalTaskStatus,
  getOverdueStaffTasks,
  getWeeklyTaskBoard,
} from "../../../services/compliance";
import { getAllFacilitiesModal } from "../../../services/facility";
import { getAllNoteTypes } from "../../../services/note";
import { getTaskTypes } from "../../../services/task";
import { getAllStaff, updateAttendenceStaff } from "../../../services/staff";
import { uploadFileToS3Api } from "../../../utils/PresignedUrlFunction";
import CustomBillingPagination from "../../../components/Pagination/CustomBillingPagination";
import { https } from "../../../http";
import { decrypt } from "../../../utils";
import { generateAuditPDF } from "../../../utils/auditPdfGenerator";

const useStyles = createStyles(() => ({
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "12px",
    padding: "0 4px",
  },
  headerTitle: {
    fontFamily: "Inter",
    fontWeight: 700,
    fontSize: "20px",
    color: "#171717",
    lineHeight: "28px",
  },
  newBadge: {
    background: "linear-gradient(135deg, #FFAF4A 0%, #FF6B35 100%)",
    color: "#FFF",
    fontFamily: "Inter",
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    padding: "3px 10px",
    borderRadius: "6px",
    lineHeight: "16px",
  },
  learnMoreLink: {
    fontFamily: "Inter",
    fontSize: "13px",
    fontWeight: 500,
    color: "#FFAF4A",
    cursor: "pointer",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
      color: "#FF6B35",
    },
  },
  tabsBarWrapper: {
    borderRadius: "12px",
    marginBottom: "20px",
    background: "#FFF",
    border: "1px solid #E5E7EB",
    padding: "8px 16px 0",
    "@media (max-width: 768px)": {
      padding: "8px 10px 0",
      borderRadius: "10px",
    },
  },
  tabsBar: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    "@media (max-width: 768px)": {
      flexDirection: "column",
      alignItems: "stretch",
      gap: "8px",
    },
  },
  tabContainer: {
    display: "flex",
    alignItems: "center",
    gap: "0px",
    overflowX: "auto",
    "&::-webkit-scrollbar": { display: "none" },
    msOverflowStyle: "none",
    scrollbarWidth: "none",
  },
  activeTabBg: {
    color: "#171717 !important",
    fontWeight: "600 !important",
    borderBottom: "2px solid #171717 !important",
  },
  tabBtn: {
    color: "#6B7280",
    fontFamily: "Inter",
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "20px",
    cursor: "pointer",
    padding: "20px 24px",
    borderBottom: "2px solid transparent",
    transition: "all 0.15s ease",
    userSelect: "none",
    whiteSpace: "nowrap",
    flexShrink: 0,
    "&:hover": {
      color: "#374151",
    },
    "@media (max-width: 480px)": {
      padding: "14px 16px",
      fontSize: "13px",
    },
  },
  tabsBarActions: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
    paddingBottom: "8px",
    "@media (max-width: 768px)": {
      justifyContent: "flex-end",
    },
    "@media (max-width: 480px)": {
      "& .tab-btn-text": { display: "none" },
    },
  },
  settingsBtn: {
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 34,
    height: 34,
    borderRadius: "8px",
    color: "#9CA3AF",
    border: "1px solid transparent",
    transition: "all 0.15s ease",
    "&:hover": {
      background: "#FFF",
      color: "#374151",
      border: "1px solid #E5E7EB",
      boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
    },
  },
  // Learn More Modal
  learnMoreSection: {
    padding: "20px 0",
    borderBottom: "1px solid #F3F4F6",
    "&:last-child": { borderBottom: "none" },
  },
  learnMoreIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  learnMoreSectionTitle: {
    fontFamily: "Inter",
    fontSize: "16px",
    fontWeight: 600,
    color: "#171717",
    marginBottom: 4,
  },
  learnMoreSectionDesc: {
    fontFamily: "Inter",
    fontSize: "13px",
    fontWeight: 400,
    color: "#6B7280",
    lineHeight: "20px",
  },
  title: {
    color: "#171717",
    fontFamily: "Inter",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
    marginTop: "15px",
  },
  table: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    borderRadius: "12px",
    padding: "0 10px",
    background: "#FFFFFF",
    border: "1px solid #E5E5E5",
    marginTop: "10px",
  },
  search: {
    background: "#FFF",
    marginBottom: "10px",
    borderRadius: "12px",
  },
  searchInput: {
    width: "100%",
    marginBlock: "10px",
    borderRadius: "12px",
    "& :focus-within": { border: "1px solid #D0D5DD" },
    "& :focus": { border: "1px solid #D0D5DD" },
    "& :hover": { border: "1px solid #D0D5DD" },
    "& :active": { border: "1px solid #D0D5DD" },
    "& :focus-visible": { border: "1px solid #D0D5DD" },
  },
  statCard: {
    padding: 20,
    borderRadius: 12,
    border: "1px solid #E5E5E5",
    backgroundColor: "#FFF",
  },
  statValue: {
    fontSize: 28,
    fontWeight: 700,
    fontFamily: "Inter",
    color: "#262626",
  },
  statLabel: {
    fontSize: 13,
    fontWeight: 500,
    fontFamily: "Inter",
    color: "#737373",
  },
  statusBadge: {
    textTransform: "capitalize",
    fontFamily: "Inter",
    fontWeight: 500,
  },
  sectionTitle: {
    color: "#171717",
    fontFamily: "Inter",
    fontSize: "16px",
    fontWeight: 600,
    lineHeight: "24px",
    marginTop: "15px",
    marginBottom: "12px",
  },
  settingsCard: {
    background: "#fff",
    margin: "0",
    borderRadius: "12px",
    padding: "12px 26px",
  },
  settingsTitle: {
    color: "#262626",
    textAlign: "left",
    fontFamily: "Inter, sans-serif",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
  },
  settingsLabel: {
    color: "#344054",
    fontFamily: "Inter",
    fontSize: "14px",
    fontWeight: 500,
    marginBottom: 4,
  },
  settingsDescription: {
    color: "#737373",
    fontFamily: "Inter",
    fontSize: "13px",
    fontWeight: 400,
    marginBottom: 8,
  },
  settingsBtnGroup: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "10px",
    margin: "1rem 0",
  },
  // ── Dashboard Styles ──
  dashCard: {
    padding: 24,
    borderRadius: 16,
    border: "1px solid #E5E7EB",
    backgroundColor: "#FFF",
    transition: "box-shadow 0.2s",
    "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.06)" },
  },
  overallScoreCard: {
    padding: 28,
    borderRadius: 16,
    background: "linear-gradient(135deg, #1E293B 0%, #334155 100%)",
    color: "#FFF",
    position: "relative",
    overflow: "hidden",
  },
  scoreCardTitle: {
    fontFamily: "Inter",
    fontSize: 13,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: "#6B7280",
    marginBottom: 8,
  },
  scoreCardValue: {
    fontFamily: "Inter",
    fontSize: 32,
    fontWeight: 700,
    lineHeight: 1.1,
  },
  scoreCardSub: {
    fontFamily: "Inter",
    fontSize: 13,
    fontWeight: 500,
    color: "#9CA3AF",
    marginTop: 4,
  },
  scoreBar: {
    height: 8,
    borderRadius: 4,
    backgroundColor: "#F3F4F6",
    overflow: "hidden",
    marginTop: 12,
  },
  alertItem: {
    padding: "12px 16px",
    borderRadius: 10,
    backgroundColor: "#F9FAFB",
    border: "1px solid #F3F4F6",
    marginBottom: 8,
    "&:last-child": { marginBottom: 0 },
  },
  severityBadge: {
    fontFamily: "Inter",
    fontWeight: 600,
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  facilityBar: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "10px 0",
    borderBottom: "1px solid #F3F4F6",
    "&:last-child": { borderBottom: "none" },
  },
  summaryStatCard: {
    padding: 20,
    borderRadius: 14,
    border: "1px solid #E5E7EB",
    backgroundColor: "#FFF",
    textAlign: "center",
  },
  // ── Operational Tab Styles ──
  opStatCard: {
    padding: 20,
    borderRadius: 14,
    border: "1px solid #E5E7EB",
    backgroundColor: "#FFF",
    position: "relative",
    overflow: "hidden",
    transition: "box-shadow 0.2s",
    "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.06)" },
  },
  opStatAccent: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: 4,
    borderRadius: "14px 0 0 14px",
  },
  opStatIconWrap: {
    width: 40,
    height: 40,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  "@keyframes pulseRed": {
    "0%": { boxShadow: "0 0 0 0 rgba(239,68,68,0.25)" },
    "70%": { boxShadow: "0 0 0 8px rgba(239,68,68,0)" },
    "100%": { boxShadow: "0 0 0 0 rgba(239,68,68,0)" },
  },
  opOverduePulse: {
    animation: "$pulseRed 2s infinite",
  },
  opStatusPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 12px",
    borderRadius: 20,
    fontFamily: "Inter",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    userSelect: "none",
    transition: "all 0.15s ease",
    "&:hover": {
      filter: "brightness(0.95)",
      transform: "scale(1.03)",
    },
  },
  opTaskTypeBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "3px 8px",
    borderRadius: 6,
    fontFamily: "Inter",
    fontSize: 10,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.02em",
    whiteSpace: "nowrap",
  },
  opStaffAvatar: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "3px 10px 3px 3px",
    borderRadius: 20,
    border: "1px solid #E5E7EB",
    background: "#F9FAFB",
    fontFamily: "Inter",
    fontSize: 12,
    fontWeight: 500,
    color: "#374151",
  },
  opStaffInitial: {
    width: 24,
    height: 24,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "Inter",
    fontSize: 10,
    fontWeight: 700,
    color: "#FFF",
    flexShrink: 0,
  },
  opTableRow: {
    transition: "background 0.1s",
  },
  opOverdueRow: {
    background: "#FAFAFA !important",
  },
  opRelativeTime: {
    fontFamily: "Inter",
    fontSize: 11,
    fontWeight: 500,
    marginLeft: 4,
  },
}));

const statusColor = (status) => {
  switch (status) {
    case "compliant": return "green";
    case "expiring": return "yellow";
    case "expired": return "red";
    case "missing": return "gray";
    case "non_compliant": return "red";
    default: return "gray";
  }
};

const categoryColor = (cat) => {
  switch (cat) {
    case "document": return "violet";
    case "notes": return "blue";
    case "task": return "teal";
    case "mar": return "orange";
    case "license": return "indigo";
    case "inspection": return "cyan";
    default: return "gray";
  }
};

const getScoreColor = (score) => {
  if (score >= 80) return "#10B981";
  if (score >= 60) return "#F59E0B";
  if (score >= 40) return "#F97316"; // orange for moderate
  return "#EF4444"; // red only for critically low (< 40)
};

const getSeverityColor = (severity) => {
  switch (severity) {
    case "critical": return "red";
    case "high": return "orange";
    case "medium": return "yellow";
    default: return "gray";
  }
};

const getFacilityDotColor = (status) => {
  switch (status) {
    case "good": return "#10B981";
    case "warning": return "#F59E0B";
    case "critical": return "#EF4444";
    default: return "#9CA3AF";
  }
};

const getDocStatus = (doc) => {
  const hasFile = doc?.fileUrl && doc.fileUrl !== "";
  if (!hasFile) return "empty";
  if (doc?.expired) return "expired";
  if (doc?.expiry) {
    const exp = new Date(doc.expiry);
    const thirtyDays = new Date();
    thirtyDays.setDate(thirtyDays.getDate() + 30);
    if (exp <= thirtyDays && exp >= new Date()) return "expiring";
  }
  return "uploaded";
};

const FACILITY_CATEGORY_OPTIONS = [
  { value: "license", label: "License" },
  { value: "inspection", label: "Inspection" },
  { value: "drill", label: "Drill" },
  { value: "insurance", label: "Insurance" },
  { value: "training", label: "Training" },
  { value: "document", label: "Document" },
  { value: "other", label: "Other" },
];

const CLIENT_CATEGORY_OPTIONS = [
  { value: "document", label: "Document" },
  { value: "notes", label: "Notes" },
  { value: "task", label: "Task" },
  { value: "mar", label: "MAR" },
];

const ITEM_TYPE_OPTIONS = [
  { value: "staff", label: "Staff Document" },
  { value: "facility", label: "Facility Document" },
  { value: "client", label: "Client Document" },
];

const AddComplianceItemModal = ({ opened, onClose, addLoading, facilities, noteTypes, taskTypes }) => {
  const [itemType, setItemType] = useState("staff");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  // Facility fields
  const [category, setCategory] = useState("other");
  const [facility, setFacility] = useState(null);
  const [expirationDate, setExpirationDate] = useState(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  // Client fields
  const [clientCategory, setClientCategory] = useState("document");
  const [threshold, setThreshold] = useState(95);
  const [frequencyMonths, setFrequencyMonths] = useState(12);
  const [gracePeriodDays, setGracePeriodDays] = useState(30);
  const [noteType, setNoteType] = useState(null);
  const [taskType, setTaskType] = useState(null);

  const resetForm = () => {
    setName(""); setDescription(""); setCategory("other"); setFacility(null);
    setExpirationDate(null); setFile(null); setUploading(false);
    setClientCategory("document"); setThreshold(95); setFrequencyMonths(12);
    setGracePeriodDays(30); setNoteType(null); setTaskType(null);
  };

  const handleSubmit = async () => {
    if (!name.trim()) return;
    let success;

    if (itemType === "staff") {
      success = await addCredentialType({ name: name.trim(), description: description.trim() });
    } else if (itemType === "facility") {
      let documentUrl = "";
      let fileName = "";
      if (file) {
        try {
          setUploading(true);
          const result = await uploadFileToS3Api({ file });
          documentUrl = result.uploadURL || result.path;
          fileName = file.name;
          setUploading(false);
        } catch (err) {
          setUploading(false);
          swal({ title: "File upload failed", text: err.message, icon: "error" });
          return;
        }
      }
      success = await addFacilityComplianceItem({
        name: name.trim(), description: description.trim(), category,
        facility: facility || undefined, documentUrl, fileName,
        expirationDate: expirationDate ? expirationDate.toISOString() : undefined,
      });
    } else if (itemType === "client") {
      const payload = { name: name.trim(), description: description.trim(), category: clientCategory };
      if (clientCategory === "document") {
        payload.frequencyMonths = frequencyMonths;
        payload.gracePeriodDays = gracePeriodDays;
      } else {
        payload.threshold = threshold;
      }
      if (clientCategory === "notes" && noteType) payload.noteType = noteType;
      if (clientCategory === "task" && taskType) payload.taskType = taskType;
      success = await addClientComplianceItem(payload);
    }

    if (success) { resetForm(); onClose(); }
  };

  const isLoading = addLoading || uploading;

  const labelStyle = { label: { fontFamily: "Inter", fontWeight: 500, marginBottom: 4 } };

  return (
    <Modal opened={opened} onClose={() => { resetForm(); onClose(); }} title="Add Compliance Item" centered size="lg" zIndex={1000}
      styles={{ title: { fontFamily: "Inter", fontWeight: 600, fontSize: 18 } }}>
      <Select label="Item Type" placeholder="Select type" data={ITEM_TYPE_OPTIONS} value={itemType}
        onChange={(v) => { setItemType(v); resetForm(); }} styles={labelStyle} mb={12} />

      <TextInput label="Name" placeholder="Enter name" required value={name}
        onChange={(e) => setName(e.currentTarget.value)} styles={labelStyle} mb={12} />

      {itemType === "facility" && (
        <>
          <Grid mb={12}>
            <Grid.Col span={6}>
              <Select label="Category" placeholder="Select category" data={FACILITY_CATEGORY_OPTIONS}
                value={category} onChange={setCategory} styles={labelStyle} />
            </Grid.Col>
            <Grid.Col span={6}>
              <Select label="Facility" placeholder="Select facility (optional)" clearable
                data={(facilities || []).map((f) => ({ value: f._id || f.id, label: f.name }))}
                value={facility} onChange={setFacility} styles={labelStyle} />
            </Grid.Col>
          </Grid>
        </>
      )}

      {itemType === "client" && (
        <>
          <Select label="Category" placeholder="Select category" data={CLIENT_CATEGORY_OPTIONS}
            value={clientCategory} onChange={setClientCategory} required styles={labelStyle} mb={12} />
          {clientCategory === "notes" && (
            <Select label="Note Type" placeholder="Select note type" searchable required
              data={(noteTypes || []).map((nt) => ({ value: nt._id || nt.id, label: nt.name }))}
              value={noteType} onChange={setNoteType} styles={labelStyle} mb={12} />
          )}
          {clientCategory === "task" && (
            <Select label="Task Type" placeholder="Select task type" searchable required
              data={(taskTypes || []).map((tt) => ({ value: tt._id || tt.id, label: tt.name }))}
              value={taskType} onChange={setTaskType} styles={labelStyle} mb={12} />
          )}
        </>
      )}

      <Textarea label="Description" placeholder="Enter description (optional)" value={description}
        onChange={(e) => setDescription(e.currentTarget.value)} styles={labelStyle} mb={12} minRows={2} />

      {itemType === "facility" && (
        <Grid mb={12}>
          <Grid.Col span={6}>
            <FileInput label="Upload Document" placeholder="Choose file" value={file} onChange={setFile} clearable
              accept="application/pdf,image/*,.doc,.docx,.xls,.xlsx" styles={labelStyle} />
          </Grid.Col>
          <Grid.Col span={6}>
            <DatePickerInput label="Expiration Date" placeholder="Select date" value={expirationDate}
              onChange={setExpirationDate} clearable styles={labelStyle} />
          </Grid.Col>
        </Grid>
      )}

      {itemType === "client" && clientCategory === "document" && (
        <Grid mb={12}>
          <Grid.Col span={6}>
            <NumberInput label="Renewal Frequency (months)" placeholder="12" min={1} max={120}
              value={frequencyMonths} onChange={setFrequencyMonths} radius="md" styles={labelStyle} />
          </Grid.Col>
          <Grid.Col span={6}>
            <NumberInput label="Grace Period (days)" placeholder="30" min={0} max={365}
              value={gracePeriodDays} onChange={setGracePeriodDays} radius="md" styles={labelStyle} />
          </Grid.Col>
        </Grid>
      )}

      {itemType === "client" && clientCategory !== "document" && (
        <NumberInput label="Completion Threshold (%)" placeholder="95" min={0} max={100}
          value={threshold} onChange={setThreshold} radius="md" mb={12}
          description={"Clients below " + threshold + "% completion will be flagged as non-compliant"}
          styles={labelStyle} />
      )}

      <Flex justify="flex-end" gap={12} mt={16}>
        <Button variant="filled" onClick={() => { resetForm(); onClose(); }}
          bg="#F9FAFB" c="#1D2939" radius="md" size="md"
          style={{ border: "1px solid #D0D5DD", fontFamily: "Inter" }}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} loading={isLoading} disabled={!name.trim()}
          variant="filled" color="yellow" c="#000" radius="md" size="md"
          style={{ fontFamily: "Inter" }}>
          Save
        </Button>
      </Flex>
    </Modal>
  );
};

const FacilityRequirementModal = ({ opened, onClose, editItem, facilities, addLoading }) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("other");
  const [facility, setFacility] = useState(null);
  const [expirationDate, setExpirationDate] = useState(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [existingDocUrl, setExistingDocUrl] = useState("");
  const [existingFileName, setExistingFileName] = useState("");

  useEffect(() => {
    if (editItem) {
      setName(editItem.name || "");
      setDescription(editItem.description || "");
      setCategory(editItem.category || "other");
      setFacility(editItem.facility?._id || editItem.facility || null);
      setExpirationDate(editItem.expirationDate ? new Date(editItem.expirationDate) : null);
      setExistingDocUrl(editItem.documentUrl || "");
      setExistingFileName(editItem.fileName || "");
      setFile(null);
    } else {
      resetForm();
    }
  }, [editItem, opened]);

  const resetForm = () => {
    setName("");
    setDescription("");
    setCategory("other");
    setFacility(null);
    setExpirationDate(null);
    setFile(null);
    setExistingDocUrl("");
    setExistingFileName("");
    setUploading(false);
  };

  const handleSubmit = async () => {
    if (!name.trim()) return;

    let documentUrl = existingDocUrl;
    let fileName = existingFileName;

    if (file) {
      try {
        setUploading(true);
        const result = await uploadFileToS3Api({ file });
        documentUrl = result.uploadURL || result.path;
        fileName = file.name;
        setUploading(false);
      } catch (err) {
        setUploading(false);
        swal({ title: "File upload failed", text: err.message, icon: "error" });
        return;
      }
    }

    const payload = {
      name: name.trim(),
      description: description.trim(),
      category,
      facility: facility || undefined,
      documentUrl,
      fileName,
      expirationDate: expirationDate ? expirationDate.toISOString() : undefined,
    };

    let success;
    if (editItem) {
      success = await updateFacilityComplianceItem(editItem._id || editItem.id, payload);
    } else {
      success = await addFacilityComplianceItem(payload);
    }

    if (success) {
      resetForm();
      onClose();
    }
  };

  const isLoading = addLoading || uploading;

  return (
    <Modal opened={opened} onClose={() => { resetForm(); onClose(); }}
      title={editItem ? "Edit Requirement" : "Add Facility Requirement"} centered size="lg" zIndex={1000}
      styles={{ title: { fontFamily: "Inter", fontWeight: 600, fontSize: 18 } }}>
      <TextInput label="Requirement Name" placeholder="e.g. Fire Inspection Certificate" required value={name}
        onChange={(e) => setName(e.currentTarget.value)}
        styles={{ label: { fontFamily: "Inter", fontWeight: 500, marginBottom: 4 } }} mb={12} />
      <Grid mb={12}>
        <Grid.Col span={6}>
          <Select label="Category" placeholder="Select category" data={FACILITY_CATEGORY_OPTIONS} value={category}
            onChange={setCategory}
            styles={{ label: { fontFamily: "Inter", fontWeight: 500, marginBottom: 4 } }} />
        </Grid.Col>
        <Grid.Col span={6}>
          <Select label="Facility" placeholder="Select facility (optional)" clearable
            data={(facilities || []).map((f) => ({ value: f._id || f.id, label: f.name }))}
            value={facility} onChange={setFacility}
            styles={{ label: { fontFamily: "Inter", fontWeight: 500, marginBottom: 4 } }} />
        </Grid.Col>
      </Grid>
      <Textarea label="Description" placeholder="Enter description (optional)" value={description}
        onChange={(e) => setDescription(e.currentTarget.value)}
        styles={{ label: { fontFamily: "Inter", fontWeight: 500, marginBottom: 4 } }} mb={12} minRows={2} />
      <Grid mb={12}>
        <Grid.Col span={6}>
          <FileInput label="Upload Document" placeholder={existingFileName || "Choose file"} value={file}
            onChange={setFile} clearable
            accept="application/pdf,image/*,.doc,.docx,.xls,.xlsx"
            styles={{ label: { fontFamily: "Inter", fontWeight: 500, marginBottom: 4 } }} />
          {existingFileName && !file && (
            <Text fz={12} color="dimmed" mt={4}>Current: {existingFileName}</Text>
          )}
        </Grid.Col>
        <Grid.Col span={6}>
          <DatePickerInput label="Expiration Date" placeholder="Select date" value={expirationDate}
            onChange={setExpirationDate} clearable
            styles={{ label: { fontFamily: "Inter", fontWeight: 500, marginBottom: 4 } }} />
        </Grid.Col>
      </Grid>
      <Flex justify="flex-end" gap={12} mt={16}>
        <Button variant="filled" onClick={() => { resetForm(); onClose(); }}
          bg="#F9FAFB" c="#1D2939" radius="md" size="md"
          style={{ border: "1px solid #D0D5DD", fontFamily: "Inter" }}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} loading={isLoading} disabled={!name.trim()}
          variant="filled" color="yellow" c="#000" radius="md" size="md"
          style={{ fontFamily: "Inter" }}>
          {editItem ? "Update" : "Save"}
        </Button>
      </Flex>
    </Modal>
  );
};

const ClientComplianceItemModal = ({ opened, onClose, editItem, addLoading, noteTypes, taskTypes, defaultCategory }) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("document");
  const [threshold, setThreshold] = useState(95);
  const [frequencyMonths, setFrequencyMonths] = useState(12);
  const [gracePeriodDays, setGracePeriodDays] = useState(30);
  const [noteType, setNoteType] = useState(null);
  const [taskType, setTaskType] = useState(null);

  useEffect(() => {
    if (editItem) {
      setName(editItem.name || "");
      setDescription(editItem.description || "");
      setCategory(editItem.category || "document");
      setThreshold(editItem.threshold ?? 95);
      setFrequencyMonths(editItem.frequencyMonths ?? 12);
      setGracePeriodDays(editItem.gracePeriodDays ?? 30);
      setNoteType(editItem.noteType?._id || editItem.noteType || null);
      setTaskType(editItem.taskType?._id || editItem.taskType || null);
    } else {
      setName("");
      setDescription("");
      setCategory(defaultCategory || "document");
      setThreshold(95);
      setFrequencyMonths(12);
      setGracePeriodDays(30);
      setNoteType(null);
      setTaskType(null);
    }
  }, [editItem, opened, defaultCategory]);

  const resetForm = () => {
    setName("");
    setDescription("");
    setCategory(defaultCategory || "document");
    setThreshold(95);
    setFrequencyMonths(12);
    setGracePeriodDays(30);
    setNoteType(null);
    setTaskType(null);
  };

  const handleSubmit = async () => {
    if (!name.trim() || !category) return;

    const payload = {
      name: name.trim(),
      description: description.trim(),
      category,
    };

    if (category === "document") {
      payload.frequencyMonths = frequencyMonths;
      payload.gracePeriodDays = gracePeriodDays;
    } else {
      payload.threshold = threshold;
    }

    if (category === "notes" && noteType) {
      payload.noteType = noteType;
    }
    if (category === "task" && taskType) {
      payload.taskType = taskType;
    }

    let success;
    if (editItem) {
      success = await updateClientComplianceItem(editItem._id || editItem.id, payload);
    } else {
      success = await addClientComplianceItem(payload);
    }

    if (success) {
      resetForm();
      onClose();
    }
  };

  const isDocument = category === "document";
  const isNotes = category === "notes";
  const isTask = category === "task";

  return (
    <Modal opened={opened} onClose={() => { resetForm(); onClose(); } }
      title={editItem ? "Edit Compliance Requirement" : defaultCategory === "document" ? "Add Client Document Requirement" : "Add Compliance Requirement"} centered size="md" zIndex={1000}
      styles={{ title: { fontFamily: "Inter", fontWeight: 600, fontSize: 18 } }}>
      <TextInput label="Requirement Name" placeholder="e.g. Progress Notes, TB Test, Daily Tasks" required value={name}
        onChange={(e) => setName(e.currentTarget.value)}
        styles={{ label: { fontFamily: "Inter", fontWeight: 500, marginBottom: 4 } }} mb={12} />
      <Select label="Category" placeholder="Select category"
        data={defaultCategory === "document"
          ? CLIENT_CATEGORY_OPTIONS.filter(o => o.value === "document")
          : defaultCategory && defaultCategory !== "document"
            ? CLIENT_CATEGORY_OPTIONS.filter(o => ["notes", "task", "mar"].includes(o.value))
            : CLIENT_CATEGORY_OPTIONS}
        value={category} onChange={setCategory} required
        styles={{ label: { fontFamily: "Inter", fontWeight: 500, marginBottom: 4 } }} mb={12} />

      {isNotes && (
        <Select label="Note Type" placeholder="Select note type to track" searchable required
          data={(noteTypes || []).map((nt) => ({ value: nt._id || nt.id, label: nt.name }))}
          value={noteType} onChange={setNoteType}
          styles={{ label: { fontFamily: "Inter", fontWeight: 500, marginBottom: 4 } }} mb={12} />
      )}

      {isTask && (
        <Select label="Task Type" placeholder="Select task type to track" searchable required
          data={(taskTypes || []).map((tt) => ({ value: tt._id || tt.id, label: tt.name }))}
          value={taskType} onChange={setTaskType}
          styles={{ label: { fontFamily: "Inter", fontWeight: 500, marginBottom: 4 } }} mb={12} />
      )}

      <Textarea label="Description" placeholder="Enter description (optional)" value={description}
        onChange={(e) => setDescription(e.currentTarget.value)}
        styles={{ label: { fontFamily: "Inter", fontWeight: 500, marginBottom: 4 } }} mb={12} minRows={2} />

      {isDocument ? (
        <Grid mb={12}>
          <Grid.Col span={6}>
            <NumberInput label="Renewal Frequency (months)" placeholder="12" min={1} max={120}
              value={frequencyMonths} onChange={setFrequencyMonths} radius="md"
              styles={{ label: { fontFamily: "Inter", fontWeight: 500, marginBottom: 4 } }} />
          </Grid.Col>
          <Grid.Col span={6}>
            <NumberInput label="Grace Period (days)" placeholder="30" min={0} max={365}
              value={gracePeriodDays} onChange={setGracePeriodDays} radius="md"
              styles={{ label: { fontFamily: "Inter", fontWeight: 500, marginBottom: 4 } }} />
          </Grid.Col>
        </Grid>
      ) : (
        <NumberInput label="Completion Threshold (%)" placeholder="95" min={0} max={100}
          value={threshold} onChange={setThreshold} radius="md" mb={12}
          description={`Clients below ${threshold}% completion will be flagged as non-compliant`}
          styles={{ label: { fontFamily: "Inter", fontWeight: 500, marginBottom: 4 } }} />
      )}

      <Flex justify="flex-end" gap={12} mt={16}>
        <Button variant="filled" onClick={() => { resetForm(); onClose(); }}
          bg="#F9FAFB" c="#1D2939" radius="md" size="md"
          style={{ border: "1px solid #D0D5DD", fontFamily: "Inter" }}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} loading={addLoading} disabled={!name.trim() || !category}
          variant="filled" color="yellow" c="#000" radius="md" size="md"
          style={{ fontFamily: "Inter" }}>
          {editItem ? "Update" : "Save"}
        </Button>
      </Flex>
    </Modal>
  );
};

/* ComplianceTaskModal — now imported from components/ComplianceTaskModal (shared) */

const getCurrentWeekLabel = () => {
  const now = new Date();
  const day = now.getDay();
  const mon = new Date(now);
  mon.setDate(now.getDate() - (day === 0 ? 6 : day - 1));
  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  return `${mon.toLocaleDateString("en-US", { month: "short", day: "numeric" })} \u2013 ${sun.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
};

const ComplianceTabs = ({ onOpenWorkLog, onOpenClockIn }) => {
  const { classes } = useStyles();
  const [activeTab, setActiveTab] = useState("overview");
  const [currentPage, setCurrentPage] = useState(1);
  const [facilityDocPage, setFacilityDocPage] = useState(1);
  const [facilityDocSearch, setFacilityDocSearch] = useState("");
  const [expandedStaff, setExpandedStaff] = useState(null);
  const [expandedStaffDocs, setExpandedStaffDocs] = useState([]);
  const [expandedStaffLoading, setExpandedStaffLoading] = useState(false);
  const [expandedFacility, setExpandedFacility] = useState(null);
  const [expandedFacilityDocs, setExpandedFacilityDocs] = useState([]);
  const [expandedFacilityLoading, setExpandedFacilityLoading] = useState(false);
  const [expandedClient, setExpandedClient] = useState(null);
  const [expandedClientDocs, setExpandedClientDocs] = useState([]);
  const [expandedClientLoading, setExpandedClientLoading] = useState(false);
  const [regSectionOpen, setRegSectionOpen] = useState({ staff: false, facility: false, client: false });
  const [clientDocPage, setClientDocPage] = useState(1);
  const [clientDocSearch, setClientDocSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [facilityModalOpened, { open: openFacilityModal, close: closeFacilityModal }] = useDisclosure(false);
  const [editingFacilityItem, setEditingFacilityItem] = useState(null);
  const [facilitySearch, setFacilitySearch] = useState("");
  const [clientModalOpened, { open: openClientModal, close: closeClientModal }] = useDisclosure(false);
  const [editingClientItem, setEditingClientItem] = useState(null);
  const [clientSearch, setClientSearch] = useState("");
  const [clientItemCategory, setClientItemCategory] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  // Auto-show Learn More on first visit
  const LEARN_MORE_KEY = "ogiso_cc_learn_more_seen";
  const isFirstVisit = (() => { try { return !localStorage.getItem(LEARN_MORE_KEY); } catch { return false; } })();
  const [learnMoreOpened, setLearnMoreOpened] = useState(isFirstVisit);
  const [pendingDrawerOpened, setPendingDrawerOpened] = useState(false);
  const [facilityHealthDrawerOpened, setFacilityHealthDrawerOpened] = useState(false);
  const [staffTasksDrawerOpened, setStaffTasksDrawerOpened] = useState(false);
  const [pendingSelectedNote, setPendingSelectedNote] = useState(null);
  const [pendingNoteOpen, setPendingNoteOpen] = useState(false);

  // ─── Action Center Resolution Drawers ─────────────────────────
  const [resolutionType, setResolutionType] = useState(null); // "expired_docs" | "expiring_docs" | "missing_notes" | "missing_mar" | "missing_tasks" | "action_center_all"
  const [prevResolutionType, setPrevResolutionType] = useState(null); // for back navigation
  const actionItemsRef = React.useRef([]);
  const [resolutionData, setResolutionData] = useState([]);
  const [resolutionLoading, setResolutionLoading] = useState(false);

  const openResolution = async (type) => {
    setResolutionType(type);
    setResolutionLoading(true);
    try {
      if (type === "expired_docs") {
        // Use pending actions as primary source (has populated staff/client/facility for upload modal)
        const pendingExpired = (dashboardPendingActions?.data || dashboardPendingActions || []).filter(item => item?.type === "document");
        // Also include dashboard expiredFiles that aren't in pending actions
        const dashExpired = (dashboard?.expiredFiles || []).map(f => ({
          ...f,
          id: f._id || f.id,
          type: "document",
          isTemplate: true,
          staff: f.type === "staff" ? { id: f.entityId } : undefined,
          client: f.type === "client" ? { id: f.entityId } : undefined,
          facility: f.type === "facility" ? { id: f.entityId } : undefined,
        }));
        const pendingIds = new Set(pendingExpired.map(d => (d.id || d._id)?.toString()));
        const combined = [...pendingExpired, ...dashExpired.filter(d => !pendingIds.has((d.id || d._id)?.toString()))];
        setResolutionData(combined);
      } else if (type === "action_center_all") {
        // Show all action items in the side panel — reuse the actionItems built in renderOverview
        // We pass actionItems via state since they're computed in render
        setResolutionData([]); // will be populated by the render
      } else if (type === "expiring_docs") {
        // Use dashboard's expiring file data
        const expiringFiles = (dashboard?.expiringFiles || []).map(f => ({
          ...f,
          id: f._id || f.id,
          type: "document",
        }));
        setResolutionData(expiringFiles);
      } else if (type === "missing_notes" || type === "missing_mar" || type === "missing_tasks") {
        // Call the specific audit endpoint for the type
        const auditType = type === "missing_notes" ? "notes" : type === "missing_mar" ? "mar" : "tasks";
        const res = await https().get(`/compliance/audit/${auditType}`);
        const clients = res?.data?.clients || [];
        const mapped = clients.map(c => ({
          clientId: c.clientId || c._id,
          clientName: c.clientName || "Unknown",
          facilityName: c.facilityName || "",
          facilityId: c.facilityId || "",
          missingCount: c.missingCount || 0,
          missingDates: c.missingDates || [],
          totalMissing: c.missingCount || 0,
        }));
        setResolutionData(mapped);
      }
    } catch (e) {
      console.error("Resolution data load failed:", e);
      setResolutionData([]);
    }
    setResolutionLoading(false);
  };

  const handleResolutionNoteClick = (client) => {
    // Navigate to Notes module with client pre-selected
    setResolutionType(null);
    localStorage.setItem("preSelectedClient", JSON.stringify({ id: client.clientId, _id: client.clientId, name: client.clientName }));
    navigate("/home/note");
  };

  const handleResolutionMarClick = (client) => {
    // Navigate to MAR page with client pre-selected
    setResolutionType(null);
    localStorage.setItem("tab", "mar");
    localStorage.setItem("selectedClient", JSON.stringify({ id: client.clientId, _id: client.clientId, name: client.clientName }));
    navigate("/home/medication");
  };

  const handleResolutionDocClick = (doc) => {
    console.log('[ResolutionDocClick] doc:', JSON.stringify({ id: doc?.id, _id: doc?._id, name: doc?.name, staff: doc?.staff?.id, client: doc?.client?.id, isTemplate: doc?.isTemplate }));
    // Ensure id is always set (upload modal uses .id not ._id)
    const docId = doc?.id || doc?._id;
    const entityId = doc?.client?.id || doc?.staff?.id || doc?.facility?.id || doc?.entityId;
    store.dispatch(setSelectedDocModal({
      ...doc,
      id: docId,
      userId: entityId,
      folder: doc?.folder || doc?.name,
      isTemplate: doc?.isTemplate !== undefined ? doc.isTemplate : true,
    }));
    store.dispatch(setUploadDocModal(true));
  };

  // ─── Overdue Staff Tasks state ─────────────────────────────────
  const [overdueTasksList, setOverdueTasksList] = useState([]);
  const [overdueTasksCount, setOverdueTasksCount] = useState(0);
  const [overdueTasksLoading, setOverdueTasksLoading] = useState(false);

  // Staff task summary for overview
  const [staffTaskSummary, setStaffTaskSummary] = useState([]);
  const [staffTaskSummaryLoading, setStaffTaskSummaryLoading] = useState(false);

  // Finance tab state
  const [financeData, setFinanceData] = useState(null);
  const [financeLoading, setFinanceLoading] = useState(false);

  // Pending panel sub-tab: "actions" or "alerts"
  const [pendingPanelTab, setPendingPanelTab] = useState("actions");
  const [expandedOpsStaff, setExpandedOpsStaff] = useState(null);

  // Operations sub-tab: "staffTask" or "workLog"
  const [opsSubTab, setOpsSubTab] = useState("staffTask");
  const [workLogData, setWorkLogData] = useState([]);
  const [workLogLoading, setWorkLogLoading] = useState(false);
  const [approvingLogId, setApprovingLogId] = useState(null);

  const handleApproveTimesheet = async (log) => {
    const newStatus = log.approvedStatus === "approved" ? "pending" : "approved";
    setApprovingLogId(log._id);
    try {
      await updateAttendenceStaff([{ id: log._id, approvedStatus: newStatus }]);
      setWorkLogData((prev) =>
        prev.map((item) =>
          item._id === log._id ? { ...item, approvedStatus: newStatus } : item
        )
      );
    } catch (e) {
      console.error("Failed to update timesheet:", e);
      swal({ title: "Error", text: "Failed to update timesheet status.", icon: "error" });
    }
    setApprovingLogId(null);
  };

  const loadStaffTaskSummary = async () => {
    setStaffTaskSummaryLoading(true);
    try {
      // Fetch staff status (login + tasks) from dedicated endpoint
      const res = await https().get("/compliance/staff-status");
      const staffList = res?.data?.staff || [];
      const summary = staffList.map((s) => {
        let photoUrl = null;
        if (s?.photo) {
          try { const d = decrypt(s.photo); photoUrl = d || s.photo; } catch { photoUrl = s.photo; }
        }
        return {
          id: s.id,
          name: s.firstName ? `${s.firstName} ${s.lastName || ""}`.trim() : "Unknown Staff",
          photo: photoUrl,
          position: s.position || "",
          loggedIn: s.loggedIn || false,
          total: s.totalTasks || 0,
          completed: s.completedTasks || 0,
          incompleteTasks: s.incompleteTasks || 0,
          completedThisWeek: s.completedThisWeek || 0,
          incompleteTodayCount: s.incompleteTodayCount || 0,
          missedDays: s.missedDays || [],
          missedDaysCount: s.missedDaysCount || 0,
          missedInstances: s.missedInstances || [],
          todayStats: s.todayStats || { total: 0, completed: 0 },
          weeklyStats: s.weeklyStats || { total: 0, completed: 0, pending: 0, completionRate: 0 },
          overdue: 0,
        };
      }).sort((a, b) => {
        // Sort: not logged in first, then by completion % ascending
        if (a.loggedIn !== b.loggedIn) return a.loggedIn ? 1 : -1;
        const aPct = a.total > 0 ? a.completed / a.total : 1;
        const bPct = b.total > 0 ? b.completed / b.total : 1;
        return aPct - bPct;
      });
      setStaffTaskSummary(summary);
    } catch (e) {
      console.error("Failed to load staff task summary:", e);
    }
    setStaffTaskSummaryLoading(false);
  };

  const loadOverdueTasks = async () => {
    setOverdueTasksLoading(true);
    try {
      const data = await getOverdueStaffTasks();
      setOverdueTasksList(data?.tasks || []);
      setOverdueTasksCount(data?.count || 0);
    } catch (e) {
      console.error("Failed to load overdue tasks:", e);
    }
    setOverdueTasksLoading(false);
  };

  const loadWorkLog = async () => {
    setWorkLogLoading(true);
    try {
      const weekStart = moment().startOf("isoWeek").toISOString();
      const weekEnd = moment().endOf("isoWeek").toISOString();
      const res = await https().post("/staff/manage-attendance", {
        page: 1,
        range: [weekStart, weekEnd],
        search: "",
      });
      const logs = res?.data?.staffs || [];
      if (logs.length > 0) console.log("Work log sample:", { facility: logs[0].facility, client: logs[0].client, staff: logs[0].staff?.firstName, checkin_type: logs[0].checkin_type });
      setWorkLogData(logs);
    } catch (e) {
      console.error("Failed to load work log:", e);
      setWorkLogData([]);
    }
    setWorkLogLoading(false);
  };

  const loadFinanceSummary = async () => {
    setFinanceLoading(true);
    try {
      const res = await https().get("/compliance/finance-summary");
      setFinanceData(res?.data || null);
    } catch (e) {
      console.error("Failed to load finance summary:", e);
    }
    setFinanceLoading(false);
  };

  const handlePendingActionClick = (item) => {
    if (item?.type === "document") {
      store.dispatch(setSelectedDocModal({
        ...item,
        userId: item?.client?.id || item?.staff?.id || item?.facility?.id,
      }));
      store.dispatch(setUploadDocModal(true));
    } else if (item?.type === "note") {
      setPendingSelectedNote(item);
      setPendingNoteOpen(true);
    }
  };

  // ─── Operational Tasks state ─────────────────────────────────
  const [opTaskPage, setOpTaskPage] = useState(1);
  const [opTaskSearch, setOpTaskSearch] = useState("");
  const [opTaskStatusFilter, setOpTaskStatusFilter] = useState("all");
  const [staffTaskView, setStaffTaskView] = useState("calendar"); // "calendar" | "all"
  const [opsTabSubTab, setOpsTabSubTab] = useState("all"); // "all" | "calendar" | "workLog"
  const [opTaskModalOpened, { open: openOpTaskModal, close: closeOpTaskModal }] = useDisclosure(false);
  const [editingOpTask, setEditingOpTask] = useState(null);

  // ─── Audit (AI Regulatory Assistant) state ─────────────────────────────────
  const [chatModal, setChatModal] = useState({ open: false, item: null });
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatProgress, setChatProgress] = useState(0);

  const handleEditFacilityItem = (item) => {
    setEditingFacilityItem(item);
    openFacilityModal();
  };

  const handleAddFacilityItem = () => {
    setEditingFacilityItem(null);
    openFacilityModal();
  };

  const handleDeleteFacilityItem = async (item) => {
    const willDelete = await swal({
      title: "Delete Requirement?",
      text: `Are you sure you want to delete "${item.name}"?`,
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    });
    if (willDelete) {
      await deleteFacilityComplianceItem(item._id || item.id);
    }
  };

  const handleEditClientItem = (item) => {
    setEditingClientItem(item);
    setClientItemCategory(item.category || null);
    openClientModal();
  };

  const handleAddClientItem = (defaultCat = null) => {
    setEditingClientItem(null);
    setClientItemCategory(defaultCat);
    openClientModal();
  };

  const handleDeleteClientItem = async (item) => {
    const willDelete = await swal({
      title: "Delete Compliance Item?",
      text: `Are you sure you want to delete "${item.name}"?`,
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    });
    if (willDelete) {
      await deleteClientComplianceItem(item._id || item.id);
    }
  };

  // Settings form state
  const [settingsForm, setSettingsForm] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [togglingRule, setTogglingRule] = useState(null);

  // Audit modal state
  const [auditModalOpened, { open: openAuditModal, close: closeAuditModal }] = useDisclosure(false);
  const [setupWizardOpen, setSetupWizardOpen] = useState(false);
  const [auditData, setAuditData] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditType, setAuditType] = useState(null);

  const [auditActiveSection, setAuditActiveSection] = useState("overview");
  const [auditTimeframeModal, setAuditTimeframeModal] = useState(false);
  const [pendingAuditType, setPendingAuditType] = useState(null);
  const [auditDays, setAuditDays] = useState(30);
  const [auditCustomRange, setAuditCustomRange] = useState([null, null]);
  const [auditRangeMode, setAuditRangeMode] = useState("preset"); // "preset" | "custom"

  // YTD = number of calendar days from Jan 1 through today, inclusive.
  // Use moment.dayOfYear() so Jan 1 counts as day 1, Jan 2 as day 2, etc. —
  // this avoids the Math.ceil rounding that previously added an extra day
  // and caused the audit to start from Dec 31 instead of Jan 1.
  const ytdDays = moment().dayOfYear();

  const promptAuditTimeframe = (type) => {
    setPendingAuditType(type);
    setAuditDays(30);
    setAuditRangeMode("preset");
    // Pre-populate date range for default 30 days
    setAuditCustomRange([moment().subtract(30, "days").startOf("day").toDate(), moment().endOf("day").toDate()]);
    setAuditTimeframeModal(true);
  };

  const confirmAndRunAudit = () => {
    setAuditTimeframeModal(false);
    if (auditRangeMode === "custom" && auditCustomRange[0] && auditCustomRange[1]) {
      const from = moment(auditCustomRange[0]).startOf("day").toISOString();
      const to = moment(auditCustomRange[1]).endOf("day").toISOString();
      handleRunAudit(pendingAuditType, null, from, to);
    } else {
      handleRunAudit(pendingAuditType, auditDays);
    }
  };

  const handleRunAudit = async (type, days = 30, from = null, to = null) => {
    setAuditType(type);
    setAuditData(null);
    setAuditLoading(true);
    setAuditActiveSection("overview");
    openAuditModal();

    if (type === "full") {
      try {
        const [notesData, tasksData, marData] = await Promise.all([
          runComplianceAudit("notes", days, from, to),
          runComplianceAudit("tasks", days, from, to),
          runComplianceAudit("mar", days, from, to),
        ]);

        // Document data from dashboard
        const reg = dashboard?.regulatory || {};
        const docMissing = (reg.staffDocs?.missing || 0) + (reg.facilityDocs?.missing || 0) + (reg.clientDocs?.missing || 0);
        const docExpired = (reg.staffDocs?.expired || 0) + (reg.facilityDocs?.expired || 0) + (reg.clientDocs?.expired || 0);
        const docExpiring = (reg.staffDocs?.expiring || 0) + (reg.facilityDocs?.expiring || 0) + (reg.clientDocs?.expiring || 0);
        // Get actual expired doc items from pending actions
        const expiredDocItems = (dashboardPendingActions?.data || dashboardPendingActions || []).filter(item => item?.type === "document");

        setAuditData({
          type: "full",
          period: notesData?.period || tasksData?.period || marData?.period,
          // Separated by category for tabbed display
          documents: {
            missing: docMissing, expired: docExpired, expiring: docExpiring,
            staffDocs: reg.staffDocs || {}, facilityDocs: reg.facilityDocs || {}, clientDocs: reg.clientDocs || {},
            expiredItems: expiredDocItems,
          },
          notes: notesData,
          tasks: tasksData,
          mar: marData,
          // Totals
          totalIssues: (notesData?.total || 0) + (tasksData?.total || 0) + (marData?.total || 0) + docExpired + docMissing,
        });
      } catch (e) {
        console.error("Full audit error:", e);
        setAuditData({ type: "full", totalIssues: 0, error: "Audit failed" });
      }
    } else {
      const data = await runComplianceAudit(type, days, from, to);
      // Nest the result under its type key so the modal renders it correctly
      setAuditData({
        type,
        period: data?.period,
        [type]: data,
        totalIssues: data?.total || 0,
      });
    }
    setAuditLoading(false);
  };

  const getAuditTitle = (type) => {
    switch (type) {
      case "notes": return "Note Audit";
      case "tasks": return "Task (ADL) Audit";
      case "mar": return "MAR Audit";
      case "full": return "Organization Health Audit";
      default: return "Compliance Audit";
    }
  };

  const getAuditMissingLabel = (type) => {
    switch (type) {
      case "notes": return "Missing Notes";
      case "tasks": return "Missing Tasks";
      case "mar": return "Missed MAR Entries";
      case "full": return "Issues";
      default: return "Missing Items";
    }
  };

  const handleExportAuditPDF = async () => {
    if (!auditData) return;
    try {
      await generateAuditPDF(auditData, auditType, getAuditTitle(auditType));
    } catch (error) {
      console.error("Error generating audit PDF:", error);
      swal({ title: "Export Failed", text: error?.message || "Could not generate PDF.", icon: "error" });
    }
  };

  const handleToggleDefaultRule = async (field, currentValue) => {
    setTogglingRule(field);
    try {
      await updateComplianceSettings({ [field]: !currentValue });
      await getComplianceDashboard();
    } finally {
      setTogglingRule(null);
    }
  };

  const compliance = useSelector((state) => state.compliance) || {};
  const dashboard = compliance.dashboard || null;
  const facilityHealthList = dashboard?.facilityHealth ?? [];
  const loading = compliance.loading || false;
  const addLoading = compliance.addLoading || false;
  const settings = compliance.settings || null;
  // Master toggle — when false, Command Center scoring UI is visually disabled.
  // Prefer the settings value (what the toggle writes to) so the disabled
  // state applies immediately after the user flips the switch. Fall back to
  // the dashboard flag on first render, before settings has loaded.
  const scoringEnabled = settings
    ? settings.commandCenterScoringEnabled !== false
    : dashboard?.commandCenterScoringEnabled !== false;
  // Track whether the admin has previously completed the wizard. Used to
  // surface a one-click re-activate toggle when scoring is currently off.
  // New admins (or anyone who never finished the wizard) don't see it —
  // they get the "Setup Command Center" entry point instead.
  const setupCompleted = settings?.commandCenterSetupCompleted === true;
  const disabledCardStyle = scoringEnabled ? undefined : { opacity: 0.5, pointerEvents: "none", userSelect: "none" };
  const credentialTypes = compliance.credentialTypes || [];
  const clientItems = compliance.clientItems || [];
  const facilityItems = compliance.facilityItems || [];
  const alertsCount = compliance.alertsCount || 0;
  const staffOverview = compliance.staffOverview || [];
  const staffOverviewCount = compliance.staffOverviewCount || 0;
  const facilityOverview = compliance.facilityOverview || [];
  const facilityOverviewCount = compliance.facilityOverviewCount || 0;
  const clientDocOverview = compliance.clientDocOverview || [];
  const clientDocOverviewCount = compliance.clientDocOverviewCount || 0;
  const alerts = compliance.alerts || [];
  const operationalTasks = compliance.operationalTasks || [];
  const operationalTasksCount = compliance.operationalTasksCount || 0;
  const operationalTasksLoading = compliance.operationalTasksLoading || false;

  const staffState = useSelector((state) => state.staff) || {};
  const allStaff = Array.isArray(staffState.allStaff) ? staffState.allStaff : (staffState.allStaff?.staff || []);

  const facilityState = useSelector((state) => state.facility) || {};
  const rawFacilities = facilityState.allFacilities;
  const facilities = Array.isArray(rawFacilities) ? rawFacilities : (rawFacilities?.facilities || []);
  const noteState = useSelector((state) => state.note) || {};
  const rawNoteTypes = noteState.allNotesTypes;
  const noteTypes = Array.isArray(rawNoteTypes) ? rawNoteTypes : (rawNoteTypes?.ntypes || []);
  const taskState = useSelector((state) => state.task) || {};
  const taskTypes = taskState.taskTypes || [];
  const { profile } = useSelector((state) => state.profile) || {};
  const { dashboardPendingActionsCount, dashboardPendingActions, dashboardPendingActionsLoading } = useSelector((state) => state?.dashboard) || {};
  const pendingActionsCount = dashboardPendingActionsCount || 0;
  const { uploadDocModal } = useSelector((state) => state?.document) || {};
  const prevUploadDocModal = useRef(false);

  // Refresh the expanded document list and overview scores when the upload modal closes
  useEffect(() => {
    if (prevUploadDocModal.current && !uploadDocModal && lastUploadContext.current) {
      const { entityType, entityId, activeTemplateNames } = lastUploadContext.current;
      refreshExpandedDocs(entityType, entityId, activeTemplateNames);
      // Refresh the overview row scores
      if (entityType === "staff") getStaffCredentialsOverview(currentPage);
      else if (entityType === "facility") getFacilityDocumentsOverview(facilityDocPage, facilityDocSearch);
      else if (entityType === "client") getClientDocumentsOverview(clientDocPage, clientDocSearch);
      lastUploadContext.current = null;
    }
    prevUploadDocModal.current = uploadDocModal;
  }, [uploadDocModal]);

  useEffect(() => {
    // Critical data for Overview tab — load in parallel, highest priority
    Promise.all([
      getComplianceDashboard(),
      loadStaffTaskSummary(),
      loadWorkLog(),
    ]);
    // Supporting data — load in parallel, needed for modals/filters
    Promise.all([
      getAllFacilitiesModal("all"),
      getAllNoteTypes(false),
      getTaskTypes("all", false),
      getAllStaff("all", false),
      getComplianceSettings(),
    ]);
    // Deferred data — needed for compliance/operations tabs, not overview
    setTimeout(() => {
      Promise.all([
        getCredentialTypes(),
        getClientComplianceItems(),
        getFacilityComplianceItems(),
        loadOverdueTasks(),
        loadFinanceSummary(),
      ]);
    }, 100);
  }, []);

  // Auto-start product tour for first-time users — DISABLED for now
  // useEffect(() => {
  //   if (!hasCompletedTour()) {
  //     const timer = setTimeout(() => {
  //       startComplianceTour(navigate, setActiveTab);
  //     }, 800);
  //     return () => clearTimeout(timer);
  //   }
  // }, []);

  // Listen for custom event from page title "Learn More" link
  useEffect(() => {
    const handler = () => setLearnMoreOpened(true);
    window.addEventListener("openCommandCenterLearnMore", handler);
    return () => window.removeEventListener("openCommandCenterLearnMore", handler);
  }, []);

  // Close Learn More when user navigates away (clicks another module in sidebar)
  useEffect(() => {
    if (learnMoreOpened && !location.pathname.includes("/home/dashboard")) {
      setLearnMoreOpened(false);
      try { localStorage.setItem(LEARN_MORE_KEY, "true"); } catch {}
    }
  }, [location.pathname]);

  useEffect(() => {
    if (activeTab === "regulatory" || activeTab === "compliance") getStaffCredentialsOverview(currentPage);
  }, [activeTab, currentPage]);

  useEffect(() => {
    if (activeTab === "regulatory" || activeTab === "compliance") getFacilityDocumentsOverview(facilityDocPage, facilityDocSearch);
  }, [activeTab, facilityDocPage, facilityDocSearch]);

  useEffect(() => {
    if (activeTab === "regulatory" || activeTab === "compliance") getClientDocumentsOverview(clientDocPage, clientDocSearch);
  }, [activeTab, clientDocPage, clientDocSearch]);

  useEffect(() => {
    if (activeTab === "operational" || (activeTab === "operations" && opsTabSubTab === "all")) getOperationalTasks(opTaskPage, 10, opTaskStatusFilter, opTaskSearch);
  }, [activeTab, opTaskPage, opTaskStatusFilter, opTaskSearch, opsTabSubTab]);

  useEffect(() => {
    if (settings && !settingsForm) {
      const credW = settings.credentialWeight ?? 40;
      const notesW = settings.notesWeight ?? 30;
      const marW = settings.marWeight ?? 20;
      const taskW = settings.taskWeight ?? 10;
      const validWeights = credW + notesW + marW + taskW === 100;
      setSettingsForm({
        credentialExpiryAlertDays: settings.credentialExpiryAlertDays ?? 30,
        clientComplianceAlertDays: settings.clientComplianceAlertDays ?? 30,
        facilityComplianceAlertDays: settings.facilityComplianceAlertDays ?? 30,
        notesThreshold: settings.notesThreshold ?? 95,
        marThreshold: settings.marThreshold ?? 95,
        taskThreshold: settings.taskThreshold ?? 90,
        credentialWeight: validWeights ? credW : 40,
        notesWeight: validWeights ? notesW : 30,
        marWeight: validWeights ? marW : 20,
        taskWeight: validWeights ? taskW : 10,
      });
    }
  }, [settings]);

  const handleSettingsChange = (field, value) => {
    setSettingsForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveSettings = async () => {
    if (!settingsForm) return;
    const totalWeight = (settingsForm.credentialWeight || 0) + (settingsForm.notesWeight || 0) +
      (settingsForm.marWeight || 0) + (settingsForm.taskWeight || 0);
    if (totalWeight !== 100) {
      const swal = (await import("sweetalert")).default;
      swal({ title: "Score weights must add up to 100%", text: `Current total: ${totalWeight}%`, icon: "warning" });
      return;
    }
    setSavingSettings(true);
    await updateComplianceSettings(settingsForm);
    setSavingSettings(false);
  };

  const handleResetSettings = () => {
    if (settings) {
      const credW = settings.credentialWeight ?? 40;
      const notesW = settings.notesWeight ?? 30;
      const marW = settings.marWeight ?? 20;
      const taskW = settings.taskWeight ?? 10;
      const validWeights = credW + notesW + marW + taskW === 100;
      setSettingsForm({
        credentialExpiryAlertDays: settings.credentialExpiryAlertDays ?? 30,
        clientComplianceAlertDays: settings.clientComplianceAlertDays ?? 30,
        facilityComplianceAlertDays: settings.facilityComplianceAlertDays ?? 30,
        notesThreshold: settings.notesThreshold ?? 95,
        marThreshold: settings.marThreshold ?? 95,
        taskThreshold: settings.taskThreshold ?? 90,
        credentialWeight: validWeights ? credW : 40,
        notesWeight: validWeights ? notesW : 30,
        marWeight: validWeights ? marW : 20,
        taskWeight: validWeights ? taskW : 10,
      });
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setSearchTerm("");
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleExpandStaff = async (staffId, activeTemplateNames) => {
    if (expandedStaff === staffId) { setExpandedStaff(null); return; }
    setExpandedStaff(staffId);
    setExpandedStaffLoading(true);
    try {
      const res = await https().get(`/document/file/get?staff=${staffId}&complianceFilter=true`);
      const files = res?.data?.activeFiles || [];
      const expired = res?.data?.expiredFiles || [];
      // Only show template files that are in the active template set
      const hasActiveList = Array.isArray(activeTemplateNames) && activeTemplateNames.length > 0;
      const activeSet = hasActiveList ? new Set(activeTemplateNames) : null;
      const allFiles = [...files, ...expired].filter(f => f.isTemplate && (!activeSet || activeSet.has(f.name) || activeSet.has(f.fileDescription)));
      // Deduplicate by template description
      const templateMap = {};
      allFiles.forEach(f => {
        const key = f.fileDescription || f.name;
        const hasFile = f.fileUrl && f.fileUrl !== '';
        if (!templateMap[key] || (hasFile && !templateMap[key].hasFile)) {
          templateMap[key] = { ...f, hasFile, templateName: key };
        }
      });
      setExpandedStaffDocs(Object.values(templateMap));
    } catch { setExpandedStaffDocs([]); }
    setExpandedStaffLoading(false);
  };

  const handleExpandFacility = async (facilityId, activeTemplateNames) => {
    if (expandedFacility === facilityId) { setExpandedFacility(null); return; }
    setExpandedFacility(facilityId);
    setExpandedFacilityLoading(true);
    try {
      const res = await https().get(`/document/file/get?facility=${facilityId}&complianceFilter=true`);
      const files = res?.data?.activeFiles || [];
      const expired = res?.data?.expiredFiles || [];
      const hasActiveList = Array.isArray(activeTemplateNames) && activeTemplateNames.length > 0;
      const activeSet = hasActiveList ? new Set(activeTemplateNames) : null;
      const allFiles = [...files, ...expired].filter(f => f.isTemplate && (!activeSet || activeSet.has(f.name) || activeSet.has(f.fileDescription)));
      const templateMap = {};
      allFiles.forEach(f => {
        const key = f.fileDescription || f.name;
        const hasFile = f.fileUrl && f.fileUrl !== '';
        if (!templateMap[key] || (hasFile && !templateMap[key].hasFile)) {
          templateMap[key] = { ...f, hasFile, templateName: key };
        }
      });
      setExpandedFacilityDocs(Object.values(templateMap));
    } catch { setExpandedFacilityDocs([]); }
    setExpandedFacilityLoading(false);
  };

  const handleExpandClient = async (clientId, activeTemplateNames) => {
    if (expandedClient === clientId) { setExpandedClient(null); return; }
    setExpandedClient(clientId);
    setExpandedClientLoading(true);
    try {
      const res = await https().get(`/document/file/get?client=${clientId}&complianceFilter=true`);
      const files = res?.data?.activeFiles || [];
      const expired = res?.data?.expiredFiles || [];
      const hasActiveList = Array.isArray(activeTemplateNames) && activeTemplateNames.length > 0;
      const activeSet = hasActiveList ? new Set(activeTemplateNames) : null;
      const allFiles = [...files, ...expired].filter(f => f.isTemplate && (!activeSet || activeSet.has(f.name) || activeSet.has(f.fileDescription)));
      const templateMap = {};
      allFiles.forEach(f => {
        const key = f.fileDescription || f.name;
        const hasFile = f.fileUrl && f.fileUrl !== '';
        if (!templateMap[key] || (hasFile && !templateMap[key].hasFile)) {
          templateMap[key] = { ...f, hasFile, templateName: key };
        }
      });
      setExpandedClientDocs(Object.values(templateMap));
    } catch { setExpandedClientDocs([]); }
    setExpandedClientLoading(false);
  };

  // ── Upload to template ──
  // uploadingFileId removed — upload now uses the UploadStaffDocDashboard modal
  const lastUploadContext = useRef(null); // tracks { entityType, entityId } for post-upload refresh

  const refreshExpandedDocs = async (entityType, entityId, activeTemplateNames) => {
    try {
      const param = entityType === "staff" ? "staff" : entityType === "facility" ? "facility" : "client";
      const res = await https().get(`/document/file/get?${param}=${entityId}&complianceFilter=true`);
      const files = res?.data?.activeFiles || [];
      const expired = res?.data?.expiredFiles || [];
      const hasActiveList = Array.isArray(activeTemplateNames) && activeTemplateNames.length > 0;
      const activeSet = hasActiveList ? new Set(activeTemplateNames) : null;
      const allFiles = [...files, ...expired].filter(f => f.isTemplate && (!activeSet || activeSet.has(f.name) || activeSet.has(f.fileDescription)));
      const templateMap = {};
      allFiles.forEach(f => {
        const key = f.fileDescription || f.name;
        const hasFile = f.fileUrl && f.fileUrl !== '';
        if (!templateMap[key] || (hasFile && !templateMap[key].hasFile)) {
          templateMap[key] = { ...f, hasFile, templateName: key };
        }
      });
      const dedupedDocs = Object.values(templateMap);
      if (entityType === "staff") setExpandedStaffDocs(dedupedDocs);
      else if (entityType === "facility") setExpandedFacilityDocs(dedupedDocs);
      else if (entityType === "client") setExpandedClientDocs(dedupedDocs);
    } catch { /* ignore */ }
  };

  const handleUploadToTemplate = (fileId, entityType, entityId, folderId) => {
    // Build the doc metadata shape that UploadStaffDocDashboard expects
    const docData = {
      id: fileId,
      userId: entityId,
      folder: folderId || null,
      isTemplate: true,
    };
    // Set the entity type key so UploadStaffDocDashboard can detect it
    if (entityType === "staff") docData.staff = entityId;
    else if (entityType === "client") docData.client = entityId;
    else if (entityType === "facility") docData.facility = entityId;

    // Track context so we can refresh after upload completes
    // Find activeTemplateNames from the overview data
    let atNames = null;
    if (entityType === "staff") atNames = (staffOverview || []).find(s => s._id === entityId)?.activeTemplateNames;
    else if (entityType === "facility") atNames = (facilityOverview || []).find(f => f._id === entityId)?.activeTemplateNames;
    else if (entityType === "client") atNames = (clientDocOverview || []).find(c => c._id === entityId)?.activeTemplateNames;
    lastUploadContext.current = { entityType, entityId, activeTemplateNames: atNames };

    store.dispatch(setSelectedDocModal(docData));
    store.dispatch(setUploadDocModal(true));
  };

  // ── Filtered lists per tab ──
  const regulatoryClientItems = clientItems.filter((ci) => ci.category === "document");
  const chartingItems = clientItems.filter((ci) => ["notes", "task", "mar"].includes(ci.category));

  const filteredFacilityItems = facilityItems.filter((fi) => {
    if (!facilitySearch.trim()) return true;
    const term = facilitySearch.toLowerCase();
    return (
      (fi.name || "").toLowerCase().includes(term) ||
      (fi.category || "").toLowerCase().includes(term) ||
      (fi.facility?.name || "").toLowerCase().includes(term)
    );
  });

  const filteredChartingItems = chartingItems.filter((ci) => {
    if (!clientSearch.trim()) return true;
    const term = clientSearch.toLowerCase();
    return (
      (ci.name || "").toLowerCase().includes(term) ||
      (ci.category || "").toLowerCase().includes(term)
    );
  });

  // --- Shared helpers ---
  const formatCurrency = (val) => {
    const num = Number(val) || 0;
    return num.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 });
  };

  // --- Audit helper components ---

  const AuditClientRow = ({ client, auditType }) => {
    const [showAll, setShowAll] = useState(false);
    const dates = client.missingDates || [];
    const visibleDates = showAll ? dates : dates.slice(0, 5);
    return (
      <tr>
        <td style={{ fontFamily: "Inter", fontSize: 13, fontWeight: 500, padding: "10px 12px", color: "#1F2937" }}>
          {client.clientName}
        </td>
        <td style={{ textAlign: "center", padding: "10px 12px" }}>
          <Badge size="lg" variant="light" color={client.missingCount > 20 ? "red" : client.missingCount > 10 ? "orange" : "yellow"} radius="sm" ff="Inter">
            {client.missingCount}
          </Badge>
        </td>
        <td style={{ padding: "10px 12px" }}>
          <Flex wrap="wrap" gap={4} align="center">
            {visibleDates.map((date) => (
              <Badge key={date} size="sm" variant="outline" color="gray" radius="sm" ff="Inter">
                {moment(date).format("MMM D")}
                {auditType === "mar" && client.missedPerDate?.[date] ? ` (${client.missedPerDate[date]})` : ""}
              </Badge>
            ))}
            {dates.length > 5 && !showAll && (
              <Text ff="Inter" fz={12} fw={600} c="#3B82F6" style={{ cursor: "pointer" }} onClick={() => setShowAll(true)}>
                Show all ({dates.length})
              </Text>
            )}
            {showAll && dates.length > 5 && (
              <Text ff="Inter" fz={12} fw={600} c="#6B7280" style={{ cursor: "pointer" }} onClick={() => setShowAll(false)}>
                Show less
              </Text>
            )}
          </Flex>
        </td>
      </tr>
    );
  };

  const AuditSectionRow = ({ section }) => {
    const [showAll, setShowAll] = useState(false);
    const dates = section.missingDates || [];
    const visibleDates = showAll ? dates : dates.slice(0, 5);
    const typeColor = section.type === "Notes" ? "#3B82F6" : section.type === "Tasks" ? "#F59E0B" : "#EF4444";
    return (
      <Flex align="flex-start" gap={12} style={{ padding: "10px 16px", borderTop: "1px solid #F3F4F6" }}>
        <Badge variant="light" color={section.type === "Notes" ? "blue" : section.type === "Tasks" ? "yellow" : "red"} size="sm" radius="sm" ff="Inter" style={{ flexShrink: 0, marginTop: 2 }}>
          {section.type}
        </Badge>
        <Box style={{ flex: 1 }}>
          <Text ff="Inter" fz={13} fw={600} c={typeColor} mb={4}>{section.missingCount} missing</Text>
          <Flex wrap="wrap" gap={4} align="center">
            {visibleDates.map((date) => (
              <Badge key={date} size="xs" variant="outline" color="gray" radius="sm" ff="Inter">
                {moment(date).format("MMM D")}
              </Badge>
            ))}
            {dates.length > 5 && !showAll && (
              <Text ff="Inter" fz={11} fw={600} c="#3B82F6" style={{ cursor: "pointer" }} onClick={() => setShowAll(true)}>
                Show all ({dates.length})
              </Text>
            )}
            {showAll && dates.length > 5 && (
              <Text ff="Inter" fz={11} fw={600} c="#6B7280" style={{ cursor: "pointer" }} onClick={() => setShowAll(false)}>
                Show less
              </Text>
            )}
          </Flex>
        </Box>
      </Flex>
    );
  };

  // --- Tab content renderers ---

  const renderOverview = () => {
    const overall = dashboard?.overallScore ?? 0;
    const reg = dashboard?.regulatory ?? {};
    const chart = dashboard?.charting ?? {};
    const ops = dashboard?.operational ?? {};
    const alertsList = dashboard?.alerts ?? [];
    const summary = dashboard?.summary ?? {};

    // Compute combined scores per category (default + extra items)
    const extraScores = chart.extraScores || {};
    const computeCategoryAvg = (category, defaultScore, defaultActive) => {
      const parts = [];
      if (defaultActive) parts.push(defaultScore ?? 0);
      chartingItems.filter(ci => ci.category === category).forEach(ci => {
        const s = extraScores[ci._id || ci.id];
        if (s) parts.push(s.score ?? 0);
      });
      return parts.length > 0 ? Math.round(parts.reduce((a, b) => a + b, 0) / parts.length) : 0;
    };
    const combinedNotesScore = computeCategoryAvg("notes", chart.notes?.score, chart.notes?.active);
    const combinedTasksScore = computeCategoryAvg("task", chart.tasks?.score, chart.tasks?.active);
    const combinedMarScore = computeCategoryAvg("mar", chart.mar?.score, chart.mar?.active);

    // ── Build Action Center items from compliance data ──
    const actionItems = [];
    // Count expired docs from both sources — dashboard regulatory data + pending actions
    const expiredDocItems = (dashboardPendingActions?.data || dashboardPendingActions || []).filter(item => item?.type === "document");
    const summaryExpiredStaff = reg.staffDocs?.expired || 0;
    const summaryExpiredFac = reg.facilityDocs?.expired || 0;
    const summaryExpiredClient = reg.clientDocs?.expired || 0;
    const totalExpired = Math.max(expiredDocItems.length, summaryExpiredStaff + summaryExpiredFac + summaryExpiredClient);
    if (totalExpired > 0) actionItems.push({ severity: "critical", icon: "doc", title: `${totalExpired} expired document${totalExpired > 1 ? "s" : ""}`, desc: `Documents requiring renewal`, cta: "Resolve", action: () => openResolution("expired_docs") });
    // Scoring-derived items — only show when Command Center scoring is enabled.
    // When disabled, missing notes / MAR / tasks / care-plan alerts are
    // suppressed since they depend on the scoring/charting logic.
    const noteDefaultOn = chart.notes?.defaultRuleActive !== false;
    const taskDefaultOn = chart.tasks?.defaultRuleActive !== false;
    const sumExtraIncomplete = (category) =>
      chartingItems
        .filter(ci => ci.category === category)
        .reduce((sum, ci) => sum + (extraScores[ci._id || ci.id]?.incomplete ?? 0), 0);
    const missingNotes = (noteDefaultOn ? (chart.notes?.incomplete || 0) : 0) + sumExtraIncomplete("notes");
    if (scoringEnabled && missingNotes > 0) actionItems.push({ severity: "critical", icon: "note", title: `${missingNotes} missing note entr${missingNotes > 1 ? "ies" : "y"}`, desc: "Required charting not completed today", cta: "Add Notes", action: () => openResolution("missing_notes") });
    const missingMar = (chart.mar?.incomplete || 0) + sumExtraIncomplete("mar");
    if (scoringEnabled && missingMar > 0) actionItems.push({ severity: "critical", icon: "mar", title: `${missingMar} missing MAR entr${missingMar > 1 ? "ies" : "y"}`, desc: "Medication administration not recorded", cta: "Mark MAR", action: () => openResolution("missing_mar") });
    const missingTasks = (taskDefaultOn ? (chart.tasks?.incomplete || 0) : 0) + sumExtraIncomplete("task");
    if (scoringEnabled && missingTasks > 0) actionItems.push({ severity: "critical", icon: "task", title: `${missingTasks} missing task${missingTasks > 1 ? "s" : ""}`, desc: "Required tasks not completed", cta: "View Tasks", action: () => openResolution("missing_tasks") });
    const carePlanMissing = reg.clientDocs?.carePlan?.missing || 0;
    if (scoringEnabled && carePlanMissing > 0) actionItems.push({ severity: "at_risk", icon: "doc", title: `${carePlanMissing} client${carePlanMissing > 1 ? "s" : ""} missing care plan`, desc: "Active care plan required for compliance", cta: "View Clients", action: () => handleTabChange("compliance") });
    // Expiring documents (at risk — about to expire soon)
    const expiringStaff = reg.staffDocs?.expiring || 0;
    const expiringFac = reg.facilityDocs?.expiring || 0;
    const expiringClient = reg.clientDocs?.expiring || 0;
    const totalExpiring = expiringStaff + expiringFac + expiringClient;
    if (totalExpiring > 0) actionItems.push({ severity: "at_risk", icon: "doc_expiring", title: `${totalExpiring} document${totalExpiring > 1 ? "s" : ""} expiring soon`, desc: "Documents approaching expiration date", cta: "View", action: () => openResolution("expiring_docs") });
    // Pending actions (at_risk only — no informational items in Action Center)
    if (pendingActionsCount > 0) actionItems.push({ severity: "at_risk", icon: "pending", title: `${pendingActionsCount} pending action${pendingActionsCount > 1 ? "s" : ""}`, desc: "Documents and notes awaiting review", cta: "Resolve", action: () => setPendingDrawerOpened(true) });

    actionItemsRef.current = actionItems;
    const criticalCount = actionItems.filter(i => i.severity === "critical").length;
    const atRiskCount = actionItems.filter(i => i.severity === "at_risk").length;
    const totalIssues = actionItems.length;

    // ── Compliance breakdown data ──
    const docScore = reg.score ?? 0;
    const chartScore = chart.score ?? 0;

    // ── Organization Health — letter grade system ──
    const orgHealth = dashboard?.orgHealthScore ?? overall;
    const compScore = dashboard?.complianceScore ?? Math.round(docScore * 0.5 + chartScore * 0.5);
    const opsScore = ops?.score ?? 0;
    const rawGrade = dashboard?.letterGrade || (orgHealth >= 90 ? "A" : orgHealth >= 80 ? "B" : orgHealth >= 70 ? "C" : orgHealth >= 60 ? "D" : "F");
    const grade = rawGrade === "E" ? "F" : rawGrade; // E → F
    const gradeColor = grade === "A" ? "#10B981" : grade === "B" ? "#22C55E" : grade === "C" ? "#F59E0B" : grade === "D" ? "#F97316" : "#EF4444";
    const gradeMessages = {
      A: "Your organization is in great standing",
      B: "Minor improvements needed across your organization",
      C: "Several areas need attention",
      D: "Significant issues require action",
      F: "Multiple areas require immediate attention",
    };
    const prev = dashboard?.previousScores;
    const overallTrend = prev ? orgHealth - (prev.orgHealthScore || prev.overallScore) : null;
    const compTrend = prev ? compScore - (prev.complianceScore || prev.documentScore || 0) : null;
    const opsTrend = prev ? opsScore - (prev.operationalScore || 0) : null;

    return (
      <Box>
        {loading ? (
          <Flex align="center" justify="center" py={60} sx={{ height: "100vh" }}><Loader color="#FFAF4A" /></Flex>
        ) : (
          <>
            {!scoringEnabled && (
              <Paper mb={16} p="md" radius={12} style={{ background: "#FFF8EB", border: "1px solid #FFAF4A" }}>
                <Flex align="center" gap={10}>
                  <Box style={{ width: 8, height: 8, borderRadius: "50%", background: "#FFAF4A" }} />
                  <Text ff="Inter" fz={13} fw={600} color="#92400E" style={{ flex: 1 }}>
                    Command Center scoring is disabled. Organization Health Scores, Compliance, Operations, and Finance views are inactive.
                    {!setupCompleted && (
                      <>
                        {" "}
                        <Text
                          component="span"
                          fw={700}
                          color="#B45309"
                          style={{ cursor: "pointer", textDecoration: "underline" }}
                          onClick={() => navigate("/home/settings", { state: { selectedTab: "compliance", complianceSubTab: "settings" } })}
                        >
                          Click here to activate command center
                        </Text>
                        .
                      </>
                    )}
                  </Text>
                </Flex>
              </Paper>
            )}
            {/* ═══ HERO — Organization Health ═══ */}
            <Paper mb={20} p={0} radius={16} style={{ overflow: "hidden", border: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
              <Box style={{
                background: "linear-gradient(145deg, #000000 0%, #171717 60%, #1F1F1F 100%)",
                padding: "32px 36px",
                display: "flex", alignItems: "center", flexWrap: "wrap", gap: 28,
                position: "relative", overflow: "hidden",
              }}>
                <Box style={{
                  position: "absolute", top: 0, right: 0, width: 300, height: "100%",
                  background: "radial-gradient(circle at 80% 50%, rgba(255,186,69,0.06) 0%, transparent 60%)",
                  pointerEvents: "none",
                }} />

                {/* Organization Score */}
                <Box style={{ flexShrink: 0, position: "relative", zIndex: 1 }}>
                  <Box style={{
                    width: 100, height: 100, borderRadius: 20,
                    background: `linear-gradient(145deg, ${gradeColor}22, ${gradeColor}08)`,
                    border: `2px solid ${gradeColor}44`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <Text ff="Inter" fw={900} fz={42} color={gradeColor} lh={1}>{scoringEnabled ? orgHealth : ""}</Text>
                  </Box>
                  <Text ff="Inter" fz={10} fw={600} color="rgba(255,255,255,0.35)" mt={8}
                    style={{ textTransform: "uppercase", letterSpacing: "0.12em" }}>
                    Organization Score
                  </Text>
                  {scoringEnabled && overallTrend !== null && overallTrend !== 0 && (
                    <Flex align="center" justify="center" gap={3} mt={4}>
                      {overallTrend > 0
                        ? <><IconTrendingUp size={12} color="#10B981" /><Text ff="Inter" fz={11} fw={600} c="#10B981">+{overallTrend}%</Text></>
                        : <><IconTrendingDown size={12} color="#EF4444" /><Text ff="Inter" fz={11} fw={600} c="#EF4444">{overallTrend}%</Text></>
                      }
                    </Flex>
                  )}
                </Box>

                {/* Message */}
                <Box style={{ flex: 1, position: "relative", zIndex: 1 }}>
                  <Text ff="Inter" fz={22} fw={700} color="#FFF" mb={4} lh={1.3}>Organization Health</Text>
                  <Text ff="Inter" fz={14} color="rgba(255,255,255,0.6)" mb={14} lh={1.6}>{scoringEnabled ? gradeMessages[grade] : ""}</Text>

                  {scoringEnabled && totalIssues > 0 && orgHealth < 100 && (
                    <Box style={{
                      background: "rgba(255,186,69,0.1)", borderRadius: 12, padding: "14px 18px",
                      border: "1px solid rgba(255,186,69,0.2)",
                    }}>
                      <Flex align="center" gap={10}>
                        <Box style={{
                          width: 32, height: 32, borderRadius: 8, backgroundColor: "rgba(255,186,69,0.15)",
                          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                        }}>
                          <IconAlertTriangle size={16} color="#FFBA45" />
                        </Box>
                        <Box>
                          <Text ff="Inter" fz={12} fw={600} color="#FFBA45">
                            Resolve {criticalCount > 0 ? `${criticalCount} critical` : ""}{criticalCount > 0 && atRiskCount > 0 ? " and " : ""}{atRiskCount > 0 ? `${atRiskCount} at-risk` : ""} issue{totalIssues > 1 ? "s" : ""}
                          </Text>
                        </Box>
                      </Flex>
                    </Box>
                  )}
                </Box>

                {/* Run Audit / Setup Command Center, with the Reactivate
                    switch sitting to the LEFT of the button for admins
                    who've already completed setup at least once. */}
                <Flex align="center" gap={12} style={{ flexShrink: 0, position: "relative", zIndex: 1 }}>
                  {!scoringEnabled && setupCompleted && (
                    <Box
                      style={{
                        background: "rgba(255,255,255,0.08)",
                        border: "1px solid rgba(255,186,69,0.35)",
                        borderRadius: 999,
                        padding: "6px 14px",
                      }}
                    >
                      <Switch
                        size="md"
                        checked={false}
                        label={<Text ff="Inter" fz={12} fw={600} color="#FFBA45">Activate Command Center</Text>}
                        labelPosition="left"
                        onChange={async () => {
                          try {
                            await updateComplianceSettings({ commandCenterScoringEnabled: true });
                            await getComplianceSettings();
                            await getComplianceDashboard();
                          } catch (err) {
                            swal("Couldn't reactivate", "Please try again from Settings.", "error");
                          }
                        }}
                        styles={{
                          track: {
                            backgroundColor: "rgba(255,255,255,0.15)",
                            borderColor: "rgba(255,186,69,0.4)",
                            "input:checked+&": { backgroundColor: "#FFAF4A", borderColor: "#FFAF4A" },
                          },
                          label: { paddingRight: 8 },
                        }}
                      />
                    </Box>
                  )}
                  <Button
                    size="md" radius={10} ff="Inter" fw={600} fz={14}
                    styles={{
                      root: {
                        color: "#0F172A", background: "linear-gradient(135deg, #FFDA7A, #FFBA45)",
                        padding: "12px 28px", boxShadow: "0 4px 14px rgba(255,186,69,0.3)",
                        "&:hover": { background: "linear-gradient(135deg, #FFBA45, #FFAF4A)", transform: "translateY(-1px)" },
                        transition: "all 0.15s ease",
                      },
                    }}
                    onClick={() => scoringEnabled ? promptAuditTimeframe("full") : setSetupWizardOpen(true)}
                    leftIcon={scoringEnabled ? <IconSearch size={16} /> : null}
                  >
                    {scoringEnabled ? "Run Audit" : "Setup Command Center"}
                  </Button>
                </Flex>
              </Box>
            </Paper>

            {/* ═══ THREE SECTION CARDS — Compliance, Operations, Finance ═══ */}
            <Grid mb={20} gutter={16}>
              {/* Compliance Card */}
              <Grid.Col span={12} sm={4}>
                <Paper radius={14} p={20} style={{
                  border: "1px solid #E5E7EB", boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                  cursor: "pointer", transition: "all 0.15s", height: "100%",
                }}
                  onClick={() => handleTabChange("compliance")}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#FFBA45"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(255,186,69,0.12)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#E5E7EB"; e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.04)"; }}
                >
                  <Flex align="center" gap={10} mb={12}>
                    <Box style={{ width: 30, height: 30, borderRadius: 8, background: "#FFF8EB", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <IconShieldCheck size={16} color="#FFAF4A" />
                    </Box>
                    <Text ff="Inter" fw={600} fz={14} color="#374151" style={{ flex: 1 }}>Compliance</Text>
                  </Flex>
                  {scoringEnabled ? (
                    <Flex align="center" gap={16}>
                      <RingProgress
                        size={72} thickness={6} roundCaps
                        sections={[{ value: compScore, color: getScoreColor(compScore) }]}
                        label={<Text ff="Inter" fw={800} fz={18} color={getScoreColor(compScore)} ta="center" lh={1}>{compScore}</Text>}
                      />
                      <Box style={{ flex: 1 }}>
                        <Flex align="center" gap={0}>
                          <Box style={{ flex: 1, textAlign: "center" }}>
                            <Text ff="Inter" fz={18} fw={700} color="#374151">{docScore}%</Text>
                            <Text ff="Inter" fz={11} color="#9CA3AF" mt={2}>Documents</Text>
                          </Box>
                          <Box style={{ width: 1, height: 32, background: "#E5E7EB", flexShrink: 0 }} />
                          <Box style={{ flex: 1, textAlign: "center" }}>
                            <Text ff="Inter" fz={18} fw={700} color="#374151">{chartScore}%</Text>
                            <Text ff="Inter" fz={11} color="#9CA3AF" mt={2}>Charting</Text>
                          </Box>
                        </Flex>
                      </Box>
                    </Flex>
                  ) : (
                    <Flex align="center" justify="center" py={24}>
                      <Text ff="Inter" fz={13} fw={500} color="#9CA3AF" ta="center">
                        Activate Command Center to view insight
                      </Text>
                    </Flex>
                  )}
                </Paper>
              </Grid.Col>

              {/* Operations Card */}
              <Grid.Col span={12} sm={4}>
                <Paper radius={14} p={20} style={{
                  border: "1px solid #E5E7EB", boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                  cursor: "pointer", transition: "all 0.15s", height: "100%",
                }}
                  onClick={() => handleTabChange("operations")}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#FFBA45"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(255,186,69,0.12)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#E5E7EB"; e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.04)"; }}
                >
                  <Flex align="center" gap={10} mb={12}>
                    <Box style={{ width: 30, height: 30, borderRadius: 8, background: "#FFF8EB", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <IconUsers size={16} color="#FFAF4A" />
                    </Box>
                    <Text ff="Inter" fw={600} fz={14} color="#374151" style={{ flex: 1 }}>Operations</Text>
                  </Flex>
                  {scoringEnabled ? (
                    <Flex align="center" gap={16}>
                      <RingProgress
                        size={72} thickness={6} roundCaps
                        sections={[{ value: opsScore, color: getScoreColor(opsScore) }]}
                        label={<Text ff="Inter" fw={800} fz={18} color={getScoreColor(opsScore)} ta="center" lh={1}>{opsScore}</Text>}
                      />
                      <Box style={{ flex: 1 }}>
                        <Flex align="center" gap={0}>
                          <Box style={{ flex: 1, textAlign: "center" }}>
                            <Text ff="Inter" fz={18} fw={700} color="#374151">{`${ops.weeklyCompleted || 0}/${ops.weeklyTotal || 0}`}</Text>
                            <Text ff="Inter" fz={11} color="#9CA3AF" mt={2}>This Week</Text>
                          </Box>
                          <Box style={{ width: 1, height: 32, background: "#E5E7EB", flexShrink: 0 }} />
                          <Box style={{ flex: 1, textAlign: "center" }}>
                            <Text ff="Inter" fz={18} fw={700} color={ops.overdueTasks > 0 ? "#EF4444" : "#374151"}>{ops.overdueTasks || 0}</Text>
                            <Text ff="Inter" fz={11} color="#9CA3AF" mt={2}>Overdue</Text>
                          </Box>
                        </Flex>
                      </Box>
                    </Flex>
                  ) : (
                    <Flex align="center" justify="center" py={24}>
                      <Text ff="Inter" fz={13} fw={500} color="#9CA3AF" ta="center">
                        Activate Command Center to view insight
                      </Text>
                    </Flex>
                  )}
                </Paper>
              </Grid.Col>

              {/* Finance Card */}
              <Grid.Col span={12} sm={4}>
                <Paper radius={14} p={20} style={{
                  border: "1px solid #E5E7EB", boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                  cursor: "pointer", transition: "all 0.15s", height: "100%",
                }}
                  onClick={() => handleTabChange("finance")}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#FFBA45"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(255,186,69,0.12)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#E5E7EB"; e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.04)"; }}
                >
                  <Flex align="center" gap={10} mb={14}>
                    <Box style={{ width: 34, height: 34, borderRadius: 9, background: "#FFF8EB", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <IconCoin size={18} color="#FFAF4A" />
                    </Box>
                    <Text ff="Inter" fw={600} fz={14} color="#374151">Finance</Text>
                  </Flex>
                  {scoringEnabled ? (
                    <>
                      <Text ff="Inter" fz={28} fw={800} color="#171717" mb={6}>
                        {financeLoading ? "..." : formatCurrency(financeData?.monthly?.totalCollected || 0)}
                      </Text>
                      <Text ff="Inter" fz={12} color="#9CA3AF">
                        {`${Math.max(financeData?.monthly?.invoices?.count || 0, financeData?.monthly?.invoices?.paidCount || 0)} invoices · ${financeData?.monthly?.claims?.count || 0} claims`}
                      </Text>
                    </>
                  ) : (
                    <Flex align="center" justify="center" py={24}>
                      <Text ff="Inter" fz={13} fw={500} color="#9CA3AF" ta="center">
                        Activate Command Center to view insight
                      </Text>
                    </Flex>
                  )}
                </Paper>
              </Grid.Col>
            </Grid>

            {/* ═══ ROW 2: Action Center + Staff Impact + Pending ═══ */}
            <Grid mb={20} gutter={16}>
              {/* Action Center — primary interaction */}
              <Grid.Col span={12} sm={4}>
                <Paper radius={14} p={20} style={{
                  height: 480, display: "flex", flexDirection: "column",
                  border: "1px solid #E5E7EB", boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                }}>
                  <Group position="apart" mb={14}>
                    <Flex align="center" gap={8}>
                      <Box style={{
                        width: 28, height: 28, borderRadius: 8, backgroundColor: "#FFF8EB",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        <IconChecklist size={15} color="#FFAF4A" />
                      </Box>
                      <Text ff="Inter" fw={700} fz={15} color="#1F2937">Action Center</Text>
                    </Flex>
                    {actionItems.length > 0 && (
                      <Text ff="Inter" fz={11} fw={600} c="#9CA3AF">{actionItems.length} item{actionItems.length !== 1 ? "s" : ""}</Text>
                    )}
                  </Group>

                  <ScrollArea style={{ flex: 1 }} offsetScrollbars>
                    {actionItems.length > 0 ? (
                      <Box>
                        {actionItems.map((item, idx) => {
                          const iconConfig = {
                            doc: { icon: <IconAlertTriangle size={16} />, bg: "#F3F4F6", color: "#374151" },
                            doc_expiring: { icon: <IconCalendar size={16} />, bg: "#F3F4F6", color: "#374151" },
                            note: { icon: <IconFileAnalytics size={16} />, bg: "#F3F4F6", color: "#374151" },
                            mar: { icon: <IconPill size={16} />, bg: "#F3F4F6", color: "#374151" },
                            task: { icon: <IconChecklist size={16} />, bg: "#F3F4F6", color: "#374151" },
                            pending: { icon: <IconClock size={16} />, bg: "#F3F4F6", color: "#374151" },
                          }[item.icon] || { icon: <IconAlertTriangle size={16} />, bg: "#F3F4F6", color: "#374151" };

                          return (
                            <Box key={idx}
                              onClick={item.action}
                              style={{
                                padding: "12px 0",
                                borderBottom: idx < actionItems.length - 1 ? "1px solid #F3F4F6" : "none",
                                cursor: "pointer",
                                transition: "background 0.1s",
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#FAFAFA"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
                            >
                              <Flex align="center" gap={12}>
                                <Box style={{
                                  width: 36, height: 36, borderRadius: 10, backgroundColor: iconConfig.bg,
                                  display: "flex", alignItems: "center", justifyContent: "center", color: iconConfig.color, flexShrink: 0,
                                }}>
                                  {iconConfig.icon}
                                </Box>
                                <Box style={{ flex: 1, minWidth: 0 }}>
                                  <Text ff="Inter" fz={13} fw={600} color="#171717" lineClamp={1}>{item.title}</Text>
                                  <Flex align="center" gap={6} mt={2}>
                                    <Badge size="xs" variant="light" color={item.severity === "critical" ? "red" : "yellow"} radius="sm" ff="Inter" fw={500} styles={{ root: { textTransform: "capitalize" } }}>
                                      {item.severity === "critical" ? "Critical" : "At Risk"}
                                    </Badge>
                                    <Text ff="Inter" fz={10} color="#9CA3AF">{item.desc}</Text>
                                  </Flex>
                                </Box>
                                <ActionIcon size="sm" variant="subtle" color="gray" onClick={(e) => { e.stopPropagation(); item.action(); }}>
                                  <IconChevronRight size={14} />
                                </ActionIcon>
                              </Flex>
                            </Box>
                          );
                        })}
                      </Box>
                    ) : (
                      <Flex direction="column" align="center" justify="center" style={{ flex: 1 }} gap={8}>
                        <Box style={{ width: 56, height: 56, borderRadius: "50%", background: "#ECFDF5", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <IconShieldCheck size={28} color="#374151" />
                        </Box>
                        <Text ff="Inter" fz={15} fw={600} color="#374151">All Clear</Text>
                        <Text ff="Inter" fz={12} color="#9CA3AF">No compliance issues detected</Text>
                      </Flex>
                    )}
                  </ScrollArea>

                  {actionItems.length > 3 && (
                    <Box style={{ borderTop: "1px solid #F3F4F6", paddingTop: 10, marginTop: "auto" }}>
                      <Text ff="Inter" fz={13} fw={600} color="#6B7280" align="center" style={{ cursor: "pointer" }}
                        onClick={() => openResolution("action_center_all")} sx={{ "&:hover": { color: "#374151" } }}>
                        View All
                      </Text>
                    </Box>
                  )}
                </Paper>
              </Grid.Col>

              {/* Operations — Staff Task + Work Log */}
              <Grid.Col span={12} sm={8}>
                <Paper radius={14} p={20} style={{
                  height: 480, display: "flex", flexDirection: "column",
                  border: "1px solid #E5E7EB", boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                }}>
                  <Group position="apart" mb={14}>
                    <Flex align="center" gap={8}>
                      <Box style={{
                        width: 28, height: 28, borderRadius: 8, backgroundColor: "#FFF8EB",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        <IconUsers size={15} color="#FFAF4A" />
                      </Box>
                      <Text ff="Inter" fw={700} fz={15} color="#1F2937">Operations</Text>
                    </Flex>
                    <Flex gap={0} style={{ borderBottom: "1px solid #F3F4F6" }}>
                      {[
                        { key: "staffTask", label: "Staff Task" },
                        { key: "workLog", label: "Work Log" },
                      ].map(t => (
                        <Box key={t.key}
                          onClick={() => setOpsSubTab(t.key)}
                          style={{
                            padding: "6px 14px", cursor: "pointer",
                            borderBottom: opsSubTab === t.key ? "2px solid #FFAF4A" : "2px solid transparent",
                          }}>
                          <Text ff="Inter" fw={opsSubTab === t.key ? 600 : 500} fz={13}
                            color={opsSubTab === t.key ? "#1F2937" : "#9CA3AF"}>
                            {t.label}
                          </Text>
                        </Box>
                      ))}
                    </Flex>
                  </Group>

                  <ScrollArea style={{ flex: 1 }} offsetScrollbars>
                    {opsSubTab === "staffTask" ? (
                      staffTaskSummaryLoading ? (
                        <Flex align="center" justify="center" py={40}><Loader size="sm" color="#FFAF4A" /></Flex>
                      ) : staffTaskSummary.length > 0 ? staffTaskSummary.map((staff) => {
                        const ts = staff.todayStats || {};
                        const todayTotal = ts.total || 0;
                        const todayCompleted = ts.completed || 0;
                        const todayPct = todayTotal > 0 ? Math.round((todayCompleted / todayTotal) * 100) : 0;
                        const isExpanded = expandedOpsStaff === staff.id;
                        const hasMissed = staff.missedDaysCount > 0;
                        const hasIncomplete = todayTotal > 0 && todayCompleted < todayTotal;
                        return (
                          <Box key={staff.id}>
                            <Box
                              style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 10px", borderRadius: 8, cursor: (hasMissed || hasIncomplete) ? "pointer" : "default", transition: "background 0.15s" }}
                              onClick={() => { if (hasMissed || hasIncomplete) setExpandedOpsStaff(isExpanded ? null : staff.id); }}
                              onMouseEnter={(e) => { if (hasMissed || hasIncomplete) e.currentTarget.style.backgroundColor = "#F9FAFB"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
                            >
                              <Box style={{ position: "relative", flexShrink: 0 }}>
                                <Avatar size={32} radius="xl" src={staff.photo || null} color="orange"
                                  styles={{ placeholder: { backgroundColor: "#FFF8EB", color: "#FFAF4A", fontFamily: "Inter", fontSize: 13, fontWeight: 600 } }}>
                                  {staff.name?.charAt(0)?.toUpperCase() || "?"}
                                </Avatar>
                                <Box style={{ position: "absolute", bottom: -1, right: -1, width: 10, height: 10, borderRadius: "50%", backgroundColor: staff.loggedIn ? "#10B981" : "#D1D5DB", border: "2px solid #fff" }} />
                              </Box>
                              <Box style={{ flex: 1, minWidth: 0 }}>
                                <Flex justify="space-between" align="center" mb={2}>
                                  <Flex align="center" gap={6}>
                                    <Text ff="Inter" fz={13} fw={500} color="#374151" lineClamp={1}>{staff.name}</Text>
                                    {staff.loggedIn
                                      ? <Badge variant="outline" color="green" size="xs" radius="sm" ff="Inter" styles={{ root: { textTransform: "none", fontWeight: 500 } }}>Logged in</Badge>
                                      : <Badge variant="outline" color="gray" size="xs" radius="sm" ff="Inter" styles={{ root: { textTransform: "none", fontWeight: 500 } }}>Not logged in</Badge>
                                    }
                                  </Flex>
                                  <Flex align="center" gap={6} style={{ flexShrink: 0 }}>
                                    <Text ff="Inter" fz={12} fw={600} color="#374151">
                                      {todayTotal > 0 ? `${todayCompleted}/${todayTotal} today` : "No tasks due"}
                                    </Text>
                                    {(hasMissed || hasIncomplete) && (
                                      isExpanded ? <IconChevronDown size={14} color="#9CA3AF" /> : <IconChevronRight size={14} color="#9CA3AF" />
                                    )}
                                  </Flex>
                                </Flex>
                                {todayTotal > 0 && <Progress value={todayPct} color="dark" size="xs" radius="xl" />}
                                <Flex gap={4} mt={4} wrap="wrap">
                                  {hasMissed && <Badge size="xs" variant="light" radius="sm" ff="Inter" fw={500} styles={{ root: { backgroundColor: "#F3F4F6", color: "#374151", border: "none" } }}>Missed {staff.missedDaysCount} day{staff.missedDaysCount > 1 ? "s" : ""}</Badge>}
                                  {hasIncomplete && <Badge size="xs" variant="light" radius="sm" ff="Inter" fw={500} styles={{ root: { backgroundColor: "#F3F4F6", color: "#374151", border: "none" } }}>{todayTotal - todayCompleted} incomplete today</Badge>}
                                  {staff.loggedIn
                                    ? <Badge size="xs" variant="light" radius="sm" ff="Inter" fw={500} styles={{ root: { backgroundColor: "#ECFDF5", color: "#10B981", border: "none" } }}>Logged in today</Badge>
                                    : staff.total > 0 && <Badge size="xs" variant="light" radius="sm" ff="Inter" fw={500} styles={{ root: { backgroundColor: "#F3F4F6", color: "#6B7280", border: "none" } }}>Not logged in</Badge>
                                  }
                                </Flex>
                              </Box>
                            </Box>

                            {isExpanded && (
                              <Box style={{ padding: "8px 12px 12px 54px", background: "#F9FAFB", borderRadius: "0 0 8px 8px", marginTop: -2, marginBottom: 4 }}>
                                {hasMissed && staff.missedDays?.length > 0 && (
                                  <Box mb={hasIncomplete ? 10 : 0}>
                                    <Text ff="Inter" fz={11} fw={600} color="#6B7280" mb={6} style={{ textTransform: "uppercase", letterSpacing: "0.05em" }}>Missed Days</Text>
                                    <Flex gap={4} wrap="wrap">
                                      {(Array.isArray(staff.missedDays) ? staff.missedDays : []).map((day, di) => (
                                        <Badge key={di} size="sm" variant="light" color="gray" radius="sm" ff="Inter" fw={500}
                                          styles={{ root: { cursor: "pointer" } }}
                                          onClick={(e) => { e.stopPropagation(); navigate("/home/tasks?tab=staff"); }}>
                                          {moment(day?.date || day).format("ddd, MMM D")}
                                        </Badge>
                                      ))}
                                    </Flex>
                                  </Box>
                                )}
                                {hasIncomplete && (
                                  <Box>
                                    <Text ff="Inter" fz={11} fw={600} color="#6B7280" mb={6} style={{ textTransform: "uppercase", letterSpacing: "0.05em" }}>Incomplete Today ({todayTotal - todayCompleted})</Text>
                                    <Button size="xs" variant="filled" radius="md" ff="Inter"
                                      onClick={(e) => { e.stopPropagation(); navigate("/home/tasks?tab=staff"); }}
                                      styles={{ root: { backgroundColor: "#FFAF4A", color: "#171717", "&:hover": { backgroundColor: "#F5A623" } } }}>
                                      View in Task Module
                                    </Button>
                                  </Box>
                                )}
                              </Box>
                            )}
                          </Box>
                        );
                      }) : (
                        <Flex direction="column" align="center" justify="center" style={{ flex: 1 }} gap={8}>
                          <Box style={{ width: 48, height: 48, borderRadius: "50%", background: "#FFF8EB", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <IconCheck size={22} color="#FFAF4A" />
                          </Box>
                          <Text ff="Inter" fz={14} fw={600} color="#374151">All staff on track</Text>
                          <Text ff="Inter" fz={11} color="#9CA3AF">No action items from staff tasks</Text>
                        </Flex>
                      )
                    ) : (
                      /* ── Work Log tab ── */
                      workLogLoading ? (
                        <Flex align="center" justify="center" style={{ flex: 1 }}><Loader size="sm" color="#FFAF4A" /></Flex>
                      ) : workLogData.length > 0 ? (
                        <Table fontSize="xs" verticalSpacing="xs" highlightOnHover
                          styles={{ root: { borderRadius: 8, overflow: "hidden" } }}>
                          <thead>
                            <tr>
                              <th style={{ fontSize: 11, color: "#6B7280", fontWeight: 600 }}>Staff</th>
                              <th style={{ fontSize: 11, color: "#6B7280", fontWeight: 600 }}>Facility</th>
                              <th style={{ fontSize: 11, color: "#6B7280", fontWeight: 600 }}>Client</th>
                              <th style={{ fontSize: 11, color: "#6B7280", fontWeight: 600 }}>Date</th>
                              <th style={{ fontSize: 11, color: "#6B7280", fontWeight: 600 }}>Clock In</th>
                              <th style={{ fontSize: 11, color: "#6B7280", fontWeight: 600 }}>Clock Out</th>
                              <th style={{ fontSize: 11, color: "#6B7280", fontWeight: 600 }}>Hours</th>
                              <th style={{ fontSize: 11, color: "#6B7280", fontWeight: 600 }}>Status</th>
                              <th style={{ fontSize: 11, color: "#6B7280", fontWeight: 600, width: 50 }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {workLogData.map((log, idx) => {
                              const tz = log.facility?.timeZone || log.staff?.timeZone || "America/New_York";
                              const staffName = log.staff?.firstName ? `${log.staff.firstName} ${log.staff.lastName || ""}`.trim() : "Unknown";
                              const facName = log.facility?.name || "—";
                              const clientName = log.client ? (log.client.form1?.firstName ? `${log.client.form1.firstName} ${log.client.form1.lastName || ""}`.trim() : (() => { try { return decrypt(log.client.name); } catch { return "—"; } })()) : "—";
                              const dateStr = log.date?.date ? moment.tz(log.date.date, tz).format("MM/DD/YY") : "—";
                              const clockIn = log.startTime ? moment.tz(log.startTime, tz).format("hh:mm A") : "—";
                              const clockOut = log.endTime ? moment.tz(log.endTime, tz).format("hh:mm A") : "—";
                              const hrs = log.total_work_time ? `${Math.floor(log.total_work_time / 3600)}h ${Math.floor((log.total_work_time % 3600) / 60)}m` : "—";
                              const status = log.approvedStatus || "pending";
                              return (
                                <tr key={log._id || idx}>
                                  <td><Text fz={12} fw={500} ff="Inter">{staffName}</Text></td>
                                  <td><Text fz={11} color="#6B7280" ff="Inter">{facName}</Text></td>
                                  <td><Text fz={11} color="#6B7280" ff="Inter">{clientName}</Text></td>
                                  <td><Text fz={11} ff="Inter">{dateStr}</Text></td>
                                  <td><Text fz={11} ff="Inter">{clockIn}</Text></td>
                                  <td><Text fz={11} ff="Inter">{clockOut}</Text></td>
                                  <td><Text fz={11} fw={500} ff="Inter">{hrs}</Text></td>
                                  <td><Badge size="xs" color={status === "approved" ? "green" : "violet"} variant="light" ff="Inter">{status}</Badge></td>
                                  <td>
                                    <Menu position="bottom-end" withinPortal shadow="md">
                                      <Menu.Target>
                                        <ActionIcon size="sm" variant="subtle" color="gray">
                                          {approvingLogId === log._id ? <Loader size={12} /> : <IconDotsVertical size={14} />}
                                        </ActionIcon>
                                      </Menu.Target>
                                      <Menu.Dropdown>
                                        <Menu.Item
                                          icon={<IconCheck size={14} />}
                                          onClick={() => handleApproveTimesheet(log)}
                                          style={{ fontFamily: "Inter", fontSize: 12, textTransform: "capitalize" }}
                                        >
                                          {status === "approved" ? "Mark as Pending" : "Approve"}
                                        </Menu.Item>
                                        <Menu.Item
                                          icon={<IconEdit size={14} />}
                                          onClick={() => {
                                            onOpenWorkLog?.();
                                          }}
                                          style={{ fontFamily: "Inter", fontSize: 12 }}
                                        >
                                          Edit Timesheet
                                        </Menu.Item>
                                      </Menu.Dropdown>
                                    </Menu>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      ) : (
                        <Flex direction="column" align="center" justify="center" style={{ flex: 1 }} gap={8}>
                          <Box style={{ width: 48, height: 48, borderRadius: "50%", background: "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <IconClock size={22} color="#6B7280" />
                          </Box>
                          <Text ff="Inter" fz={14} fw={600} color="#6B7280">No attendance records</Text>
                          <Text ff="Inter" fz={11} color="#9CA3AF">No work log entries found for this week</Text>
                        </Flex>
                      )
                    )}
                  </ScrollArea>

                  <Box style={{ borderTop: "1px solid #F3F4F6", paddingTop: 10, marginTop: "auto" }}>
                    <Text ff="Inter" fz={13} fw={600} color="#6B7280" align="center" style={{ cursor: "pointer" }}
                      onClick={() => {
                        if (opsSubTab === "staffTask") setStaffTasksDrawerOpened(true);
                        else navigate("/home/staff/manage-attendance/all");
                      }}
                      sx={{ "&:hover": { color: "#374151" } }}>
                      View All
                    </Text>
                  </Box>
                </Paper>
              </Grid.Col>
            </Grid>
          </>
        )}
      </Box>
    );
  };

  // ═══════════════════════════════════════════════
  //  REGULATORY TAB (Staff credentials + Facility docs + Client docs)
  // ═══════════════════════════════════════════════
  const renderRegulatory = () => {
    const reg = dashboard?.regulatory ?? {};
    const staffDocs = reg.staffDocs ?? {};
    const facilityDocs = reg.facilityDocs ?? {};
    const clientDocs = reg.clientDocs ?? {};

    const totalOverdue = (staffDocs.expired || 0) + (staffDocs.missing || 0)
      + (facilityDocs.expired || 0) + (facilityDocs.missing || 0)
      + (clientDocs.expired || 0) + (clientDocs.missing || 0);
    const totalDueSoon = (staffDocs.expiring || 0) + (facilityDocs.expiring || 0) + (clientDocs.expiring || 0);
    const totalCompliant = (staffDocs.compliant || 0) + (facilityDocs.compliant || 0) + (clientDocs.compliant || 0);
    const totalDocs = (staffDocs.total || 0) + (facilityDocs.total || 0) + (clientDocs.total || 0);

    const toggleSection = (key) => setRegSectionOpen((prev) => ({ ...prev, [key]: !prev[key] }));

    // Section overview card renderer
    const renderSectionCard = (key, title, icon, iconBg, accentColor, docs, entityCount, entityLabel) => {
      const isOpen = regSectionOpen[key];
      const score = docs.total ? Math.round(((docs.compliant || 0) / docs.total) * 100) : 0;
      const scoreColor = score >= 80 ? "#10B981" : score >= 50 ? "#F59E0B" : "#EF4444";


      return (
        <Box mb={16} key={key}>
          <Box style={{
            backgroundColor: "#fff",
            borderRadius: 14,
            border: "1px solid #E5E7EB",
            overflow: "hidden",
            transition: "all 0.2s ease",
            boxShadow: isOpen ? "0 2px 8px rgba(0,0,0,0.04)" : "0 1px 3px rgba(0,0,0,0.04)",
          }}
            onMouseEnter={(e) => { if (!isOpen) e.currentTarget.style.borderColor = "#D1D5DB"; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#E5E7EB"; }}
          >
            {/* ── Header (always visible) ── */}
            <Box
              onClick={() => toggleSection(key)}
              style={{
                padding: "18px 22px",
                cursor: "pointer",
                backgroundColor: isOpen ? "#FAFAFA" : "#fff",
                borderBottom: isOpen ? "1px solid #F3F4F6" : "none",
              }}
            >
              <Flex align="center" justify="space-between">
                <Flex align="center" gap={14}>
                  <Box style={{
                    width: 44, height: 44, borderRadius: 12, backgroundColor: iconBg,
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}>
                    {icon}
                  </Box>
                  <Box>
                    <Text ff="Inter" fz={16} fw={700} c="#1F2937">{title}</Text>
                    <Text ff="Inter" fz={12} c="#9CA3AF" fw={500} mt={2}>
                      {entityCount} {entityLabel} &middot; {docs.total || 0} total documents
                    </Text>
                  </Box>
                </Flex>

                <Flex align="center" gap={20}>
                  {/* Mini stats (hidden when open) */}
                  {!isOpen && (
                    <Flex gap={16} align="center">
                      <Box style={{ textAlign: "center" }}>
                        <Text ff="Inter" fz={18} fw={700} c="#171717">{docs.compliant || 0}</Text>
                        <Text ff="Inter" fz={10} c="#9CA3AF" fw={600}>Compliant</Text>
                      </Box>
                      <Box style={{ textAlign: "center" }}>
                        <Text ff="Inter" fz={18} fw={700} c="#374151">{docs.expiring || 0}</Text>
                        <Text ff="Inter" fz={10} c="#9CA3AF" fw={600}>Expiring</Text>
                      </Box>
                      <Box style={{ textAlign: "center" }}>
                        <Text ff="Inter" fz={18} fw={700} c="#EF4444">{(docs.expired || 0) + (docs.missing || 0)}</Text>
                        <Text ff="Inter" fz={10} c="#9CA3AF" fw={600}>Overdue</Text>
                      </Box>
                      <Box style={{ width: 1, height: 32, backgroundColor: "#E5E7EB" }} />
                      <Flex align="center" gap={6}>
                        <Box style={{ position: "relative", width: 40, height: 40 }}>
                          <svg width="40" height="40" viewBox="0 0 40 40">
                            <circle cx="20" cy="20" r="16" fill="none" stroke="#F3F4F6" strokeWidth="4" />
                            <circle cx="20" cy="20" r="16" fill="none" stroke={scoreColor} strokeWidth="4"
                              strokeDasharray={`${(score / 100) * 100.5} 100.5`}
                              strokeLinecap="round" transform="rotate(-90 20 20)" />
                          </svg>
                          <Text ff="Inter" fz={10} fw={700} c={scoreColor}
                            style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}>
                            {score}%
                          </Text>
                        </Box>
                      </Flex>
                    </Flex>
                  )}
                  <Box style={{
                    width: 30, height: 30, borderRadius: 8,
                    backgroundColor: "#F3F4F6",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {isOpen ? <IconChevronDown size={16} color="#6B7280" /> : <IconChevronRight size={16} color="#6B7280" />}
                  </Box>
                </Flex>
              </Flex>
            </Box>

            {/* ── Expanded content (inside same card) ── */}
            {isOpen && (
              <Box style={{ backgroundColor: "#fff" }}>
                {/* Required Credential Types section removed — all active templates are required */}
                <ScrollArea style={{ padding: "8px 18px 4px" }} type="auto">
                  <Table withBorder fontSize="sm" verticalSpacing="md" highlightOnHover
                    style={{ minWidth: 700 }}
                    styles={{ root: { borderRadius: 10, overflow: "hidden" } }}>
                    <thead>
                      <tr>
                        <th style={{ minWidth: 200 }}>{key === "staff" ? "Staff Name" : key === "facility" ? "Facility Name" : "Client Name"}</th>
                        {key === "staff" && <th style={{ minWidth: 120 }}>Position</th>}
                        <th style={{ minWidth: 90 }}>Score</th>
                        <th style={{ minWidth: 100 }}>Status</th>
                        <th style={{ minWidth: 100 }}>Documents</th>
                        <th style={{ minWidth: 80 }}>Compliant</th>
                        <th style={{ minWidth: 80 }}>Expiring</th>
                        <th style={{ minWidth: 80 }}>Expired</th>
                        <th style={{ minWidth: 80 }}>Missing</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr><td colSpan={key === "staff" ? 9 : 8}><Flex justify="center" py={30}><Loader color="#FFAF4A" size="sm" /></Flex></td></tr>
                      ) : key === "staff" ? (
                        staffOverview.length > 0 ? staffOverview.map((s, i) => (
                          <React.Fragment key={s._id || i}>
                            <tr onClick={() => handleExpandStaff(s._id, s.activeTemplateNames)} style={{ cursor: "pointer" }}>
                              <td><Group spacing={6}>{expandedStaff === s._id ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}<Text fz={13}>{s.firstName} {s.lastName}</Text></Group></td>
                              <td><Text fz={12} c="#6B7280">{s.position || "\u2014"}</Text></td>
                              <td><Group spacing={6}><Progress value={s.credentialSummary?.score || 0} color={s.credentialSummary?.score >= 80 ? "green" : s.credentialSummary?.score >= 50 ? "yellow" : "red"} size="sm" style={{ width: 50 }} /><Text fz={12} fw={500}>{s.credentialSummary?.score || 0}%</Text></Group></td>
                              <td><Badge color={statusColor(s.credentialSummary?.overallStatus)} className={classes.statusBadge} size="sm">{(s.credentialSummary?.overallStatus || "").replace("_", " ")}</Badge></td>
                              <td><Text fz={12}>{s.documentSummary?.uploaded || 0}/{s.documentSummary?.total || 0}</Text></td>
                              <td><Text fz={12}>{s.credentialSummary?.compliant || 0}</Text></td>
                              <td><Text fz={12}>{s.credentialSummary?.expiring || 0}</Text></td>
                              <td><Text fz={12}>{s.credentialSummary?.expired || 0}</Text></td>
                              <td><Text fz={12}>{s.credentialSummary?.missing || 0}</Text></td>
                            </tr>
                            {expandedStaff === s._id && (
                              <tr><td colSpan={9} style={{ padding: 0, backgroundColor: "#F9FAFB" }}><Box p="sm">
                                <Text fz={13} fw={600} ff="Inter" mb={8}>Documents for {s.firstName} {s.lastName}</Text>
                                {expandedStaffLoading ? <Flex justify="center" py={16}><Loader color="#FFAF4A" size="xs" /></Flex>
                                : expandedStaffDocs.length > 0 ? (
                                  <Table fontSize="xs" verticalSpacing="xs">
                                    <thead><tr><th>Template</th><th>Folder</th><th>Uploaded File</th><th>Expiry</th><th>Status</th><th style={{ width: 100 }}>Action</th></tr></thead>
                                    <tbody>{expandedStaffDocs.map((doc) => { const st = getDocStatus(doc); const docId = doc._id || doc.id; return (
                                      <tr key={docId}><td><Text fz={12}>{doc.fileDescription || doc.name}</Text></td><td><Text fz={12} color="dimmed">{doc.folder?.name || "\u2014"}</Text></td><td><Text fz={12} color="dimmed">{doc.fileUrl ? doc.name : "\u2014"}</Text></td>
                                      <td><Text fz={12}>{doc.expiry ? moment(doc.expiry).format("MMM DD, YYYY") : "\u2014"}</Text></td>
                                      <td><Badge size="xs" color={st === "uploaded" ? "green" : st === "expiring" ? "yellow" : st === "expired" ? "red" : "gray"}>{st}</Badge></td>
                                      <td>{(st === "empty" || st === "expired") && doc.isTemplate && <Button size="xs" variant="light" color="yellow" compact onClick={(e) => { e.stopPropagation(); handleUploadToTemplate(docId, "staff", s._id, doc.folder?._id || doc.folder); }} styles={{ root: { fontFamily: "Inter", fontSize: 11 } }}>Upload</Button>}
                                      {(st === "uploaded" || st === "expiring") && doc.fileUrl && <ActionIcon size="sm" variant="subtle" color="dark" component="a" href={doc.fileUrl} target="_blank" rel="noopener noreferrer"><IconFileDownload size={14} /></ActionIcon>}</td></tr>
                                    ); })}</tbody>
                                  </Table>
                                ) : <Text fz={12} color="dimmed">No documents found</Text>}
                              </Box></td></tr>
                            )}
                          </React.Fragment>
                        )) : <tr><td colSpan={9}><RecordNotFound /></td></tr>
                      ) : key === "facility" ? (
                        facilityOverview.length > 0 ? facilityOverview.map((f, i) => (
                          <React.Fragment key={f._id || i}>
                            <tr onClick={() => handleExpandFacility(f._id, f.activeTemplateNames)} style={{ cursor: "pointer" }}>
                              <td><Group spacing={6}>{expandedFacility === f._id ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}<Text fz={13} fw={500}>{f.name}</Text></Group></td>
                              <td><Group spacing={6}><Progress value={f.documentSummary?.score || 0} color={f.documentSummary?.score >= 80 ? "green" : f.documentSummary?.score >= 50 ? "yellow" : "red"} size="sm" style={{ width: 50 }} /><Text fz={12} fw={500}>{f.documentSummary?.score || 0}%</Text></Group></td>
                              <td><Badge color={statusColor(f.documentSummary?.overallStatus)} className={classes.statusBadge} size="sm">{(f.documentSummary?.overallStatus || "").replace("_", " ")}</Badge></td>
                              <td><Text fz={12}>{f.documentSummary?.uploaded || 0}/{f.documentSummary?.total || 0}</Text></td>
                              <td><Text fz={12}>{f.documentSummary?.compliant || 0}</Text></td>
                              <td><Text fz={12}>{f.documentSummary?.expiring || 0}</Text></td>
                              <td><Text fz={12}>{f.documentSummary?.expired || 0}</Text></td>
                              <td><Text fz={12}>{f.documentSummary?.missing || 0}</Text></td>
                            </tr>
                            {expandedFacility === f._id && (
                              <tr><td colSpan={8} style={{ padding: 0, backgroundColor: "#F9FAFB" }}><Box p="sm">
                                <Text fz={13} fw={600} ff="Inter" mb={8}>Documents for {f.name}</Text>
                                {expandedFacilityLoading ? <Flex justify="center" py={16}><Loader color="#FFAF4A" size="xs" /></Flex>
                                : expandedFacilityDocs.length > 0 ? (
                                  <Table fontSize="xs" verticalSpacing="xs">
                                    <thead><tr><th>Document Name</th><th>Folder</th><th>Description</th><th>Expiry</th><th>Status</th><th style={{ width: 100 }}>Action</th></tr></thead>
                                    <tbody>{expandedFacilityDocs.map((doc) => { const st = getDocStatus(doc); const docId = doc._id || doc.id; return (
                                      <tr key={docId}><td><Text fz={12}>{doc.name}</Text></td><td><Text fz={12} color="dimmed">{doc.folder?.name || "\u2014"}</Text></td><td><Text fz={12} color="dimmed">{doc.fileDescription || "\u2014"}</Text></td>
                                      <td><Text fz={12}>{doc.expiry ? moment(doc.expiry).format("MMM DD, YYYY") : "\u2014"}</Text></td>
                                      <td><Badge size="xs" color={st === "uploaded" ? "green" : st === "expiring" ? "yellow" : st === "expired" ? "red" : "gray"}>{st}</Badge></td>
                                      <td>{(st === "empty" || st === "expired") && doc.isTemplate && <Button size="xs" variant="light" color="yellow" compact onClick={(e) => { e.stopPropagation(); handleUploadToTemplate(docId, "facility", f._id, doc.folder?._id || doc.folder); }} styles={{ root: { fontFamily: "Inter", fontSize: 11 } }}>Upload</Button>}
                                      {(st === "uploaded" || st === "expiring") && doc.fileUrl && <ActionIcon size="sm" variant="subtle" color="dark" component="a" href={doc.fileUrl} target="_blank" rel="noopener noreferrer"><IconFileDownload size={14} /></ActionIcon>}</td></tr>
                                    ); })}</tbody>
                                  </Table>
                                ) : <Text fz={12} color="dimmed">No documents found</Text>}
                              </Box></td></tr>
                            )}
                          </React.Fragment>
                        )) : <tr><td colSpan={8}><RecordNotFound /></td></tr>
                      ) : (
                        clientDocOverview.length > 0 ? clientDocOverview.map((c, i) => (
                          <React.Fragment key={c._id || i}>
                            <tr onClick={() => handleExpandClient(c._id, c.activeTemplateNames)} style={{ cursor: "pointer" }}>
                              <td><Group spacing={6}>{expandedClient === c._id ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}<Text fz={13} fw={500}>{c.clientName || (c.form1?.firstName ? `${c.form1.firstName} ${c.form1.lastName}` : "Unknown")}</Text></Group></td>
                              <td><Group spacing={6}><Progress value={c.documentSummary?.score || 0} color={c.documentSummary?.score >= 80 ? "green" : c.documentSummary?.score >= 50 ? "yellow" : "red"} size="sm" style={{ width: 50 }} /><Text fz={12} fw={500}>{c.documentSummary?.score || 0}%</Text></Group></td>
                              <td><Badge color={statusColor(c.documentSummary?.overallStatus)} className={classes.statusBadge} size="sm">{(c.documentSummary?.overallStatus || "").replace("_", " ")}</Badge></td>
                              <td><Text fz={12}>{c.documentSummary?.uploaded || 0}/{c.documentSummary?.total || 0}</Text></td>
                              <td><Text fz={12}>{c.documentSummary?.compliant || 0}</Text></td>
                              <td><Text fz={12}>{c.documentSummary?.expiring || 0}</Text></td>
                              <td><Text fz={12}>{c.documentSummary?.expired || 0}</Text></td>
                              <td><Text fz={12}>{c.documentSummary?.missing || 0}</Text></td>
                            </tr>
                            {expandedClient === c._id && (
                              <tr><td colSpan={8} style={{ padding: 0, backgroundColor: "#F9FAFB" }}><Box p="sm">
                                <Text fz={13} fw={600} ff="Inter" mb={8}>Documents for {c.clientName || (c.form1?.firstName ? `${c.form1.firstName} ${c.form1.lastName}` : "Unknown")}</Text>
                                {expandedClientLoading ? <Flex justify="center" py={16}><Loader color="#FFAF4A" size="xs" /></Flex>
                                : expandedClientDocs.length > 0 ? (
                                  <Table fontSize="xs" verticalSpacing="xs">
                                    <thead><tr><th>Document Name</th><th>Folder</th><th>Description</th><th>Expiry</th><th>Status</th><th style={{ width: 100 }}>Action</th></tr></thead>
                                    <tbody>{expandedClientDocs.map((doc) => { const st = getDocStatus(doc); const docId = doc._id || doc.id; return (
                                      <tr key={docId}><td><Text fz={12}>{doc.name}</Text></td><td><Text fz={12} color="dimmed">{doc.folder?.name || "\u2014"}</Text></td><td><Text fz={12} color="dimmed">{doc.fileDescription || "\u2014"}</Text></td>
                                      <td><Text fz={12}>{doc.expiry ? moment(doc.expiry).format("MMM DD, YYYY") : "\u2014"}</Text></td>
                                      <td><Badge size="xs" color={st === "uploaded" ? "green" : st === "expiring" ? "yellow" : st === "expired" ? "red" : "gray"}>{st}</Badge></td>
                                      <td>{(st === "empty" || st === "expired") && doc.isTemplate && <Button size="xs" variant="light" color="yellow" compact onClick={(e) => { e.stopPropagation(); handleUploadToTemplate(docId, "client", c._id, doc.folder?._id || doc.folder); }} styles={{ root: { fontFamily: "Inter", fontSize: 11 } }}>Upload</Button>}
                                      {(st === "uploaded" || st === "expiring") && doc.fileUrl && <ActionIcon size="sm" variant="subtle" color="dark" component="a" href={doc.fileUrl} target="_blank" rel="noopener noreferrer"><IconFileDownload size={14} /></ActionIcon>}</td></tr>
                                    ); })}</tbody>
                                  </Table>
                                ) : <Text fz={12} color="dimmed">No documents found</Text>}
                              </Box></td></tr>
                            )}
                          </React.Fragment>
                        )) : <tr><td colSpan={8}><RecordNotFound /></td></tr>
                      )}
                    </tbody>
                  </Table>
                </ScrollArea>
                <Box style={{ padding: "8px 18px 14px" }}>
                  <CustomBillingPagination
                    data={key === "staff" ? staffOverviewCount : key === "facility" ? facilityOverviewCount : clientDocOverviewCount}
                    itemsPerPage={10}
                    currentPage={key === "staff" ? currentPage : key === "facility" ? facilityDocPage : clientDocPage}
                    handlePageChange={key === "staff" ? handlePageChange : key === "facility" ? ((page) => setFacilityDocPage(page)) : ((page) => setClientDocPage(page))}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      );
    };

    return (
    <Box>
      {/* ── Summary Cards — clean white card design ── */}
      <SimpleGrid cols={4} breakpoints={[{ maxWidth: "md", cols: 2 }, { maxWidth: "sm", cols: 1 }]} mb={24}>
        {[
          { label: "Overdue", count: totalOverdue, icon: <IconAlertTriangle size={18} />, iconColor: "#EF4444", iconBg: "#FEF2F2" },
          { label: "Due Soon", count: totalDueSoon, icon: <IconClock size={18} />, iconColor: "#FFBA45", iconBg: "#FFF8EB" },
          { label: "Compliant", count: totalCompliant, icon: <IconShieldCheck size={18} />, iconColor: "#10B981", iconBg: "#ECFDF5" },
          { label: "Total Documents", count: totalDocs, icon: <IconFileAnalytics size={18} />, iconColor: "#6B7280", iconBg: "#F3F4F6", prev: null },
        ].map(s => (
          <Paper key={s.label} radius={14} p={20} style={{
            border: "1px solid #E5E7EB", boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          }}>
            <Flex justify="space-between" align="flex-start" mb={16}>
              <Flex align="center" gap={8}>
                <Box style={{
                  width: 32, height: 32, borderRadius: 8, backgroundColor: s.iconBg,
                  display: "flex", alignItems: "center", justifyContent: "center", color: s.iconColor,
                }}>
                  {s.icon}
                </Box>
                <Text ff="Inter" fz={13} fw={600} c="#374151">{s.label}</Text>
              </Flex>
            </Flex>
            <Flex align="baseline" gap={8} mb={4}>
              <Text ff="Inter" fz={28} fw={700} c="#171717" lh={1}>{s.count}</Text>
            </Flex>
            <Text ff="Inter" fz={11} c="#9CA3AF" fw={500}>Since last check</Text>
          </Paper>
        ))}
      </SimpleGrid>

      {/* ── Collapsible Document Sections ── */}
      {renderSectionCard(
        "staff", "Staff Documents",
        <IconClipboardCheck size={22} color="#374151" stroke={2} />,
        "#EFF6FF", "#3B82F6",
        staffDocs, staffOverviewCount || staffOverview.length, "staff members"
      )}
      {renderSectionCard(
        "facility", "Facility Documents",
        <IconBuildingHospital size={22} color="#374151" stroke={2} />,
        "#F5F3FF", "#8B5CF6",
        facilityDocs, facilityOverviewCount || facilityOverview.length, "facilities"
      )}
      {renderSectionCard(
        "client", "Client Documents",
        <IconCheckupList size={22} color="#374151" stroke={2} />,
        "#FFFBEB", "#F59E0B",
        clientDocs, clientDocOverviewCount || clientDocOverview.length, "clients"
      )}
    </Box>
  );
  };

  // ═══════════════════════════════════════════════
  //  CHARTING TAB (Notes, MAR, Task — daily checks per client)
  // ═══════════════════════════════════════════════
  const renderCharting = () => {
    const chart = dashboard?.charting ?? {};
    const extraScores = chart.extraScores || {};
    const noteExtras = chartingItems.filter(ci => ci.category === "notes");
    const taskExtras = chartingItems.filter(ci => ci.category === "task");
    const marExtras = chartingItems.filter(ci => ci.category === "mar");

    // Compute combined missing counts (default + additional rules)
    const noteDefaultRuleActive = chart.notes?.defaultRuleActive !== false;
    const taskDefaultRuleActive = chart.tasks?.defaultRuleActive !== false;
    const combinedNotesMissing = (noteDefaultRuleActive ? (chart.notes?.clientsMissing ?? 0) : 0) + noteExtras.reduce((sum, ci) => sum + (extraScores[ci._id || ci.id]?.clientsMissing ?? 0), 0);
    const combinedNotesIncomplete = (noteDefaultRuleActive ? (chart.notes?.incomplete ?? 0) : 0) + noteExtras.reduce((sum, ci) => sum + (extraScores[ci._id || ci.id]?.incomplete ?? 0), 0);
    const combinedTasksMissing = (taskDefaultRuleActive ? (chart.tasks?.clientsMissing ?? 0) : 0) + taskExtras.reduce((sum, ci) => sum + (extraScores[ci._id || ci.id]?.clientsMissing ?? 0), 0);
    const combinedTasksIncomplete = (taskDefaultRuleActive ? (chart.tasks?.incomplete ?? 0) : 0) + taskExtras.reduce((sum, ci) => sum + (extraScores[ci._id || ci.id]?.incomplete ?? 0), 0);
    const combinedMarMissing = (chart.mar?.clientsMissing ?? 0) + marExtras.reduce((sum, ci) => sum + (extraScores[ci._id || ci.id]?.clientsMissing ?? 0), 0);
    const combinedMarIncomplete = (chart.mar?.incomplete ?? 0) + marExtras.reduce((sum, ci) => sum + (extraScores[ci._id || ci.id]?.incomplete ?? 0), 0);

    // Compute combined scores — only include default rule score if its toggle is active
    const computeCombinedCategoryScore = (defaultScore, defaultActive, extras) => {
      const parts = [];
      if (defaultActive) parts.push(defaultScore ?? 0);
      extras.forEach(ci => {
        const s = extraScores[ci._id || ci.id];
        if (s) parts.push(s.score ?? 0);
      });
      return parts.length > 0 ? Math.round(parts.reduce((a, b) => a + b, 0) / parts.length) : null;
    };
    const combinedNoteScore = computeCombinedCategoryScore(chart.notes?.score, noteDefaultRuleActive, noteExtras);
    const combinedTaskScore = computeCombinedCategoryScore(chart.tasks?.score, taskDefaultRuleActive, taskExtras);
    const combinedMarScore = computeCombinedCategoryScore(chart.mar?.score, true, marExtras);

    const renderExtraRulesTable = (extras) => {
      if (!extras.length) return null;
      return (
        <Box mt={12} style={{ borderTop: "1px solid #F3F4F6", paddingTop: 12 }}>
          <Text fz={12} fw={600} ff="Inter" color="#6B7280" mb={8}>Additional Rules ({extras.length})</Text>
          {extras.map(ci => {
            const itemId = ci._id || ci.id;
            const rd = extraScores[itemId] || {};
            return (
              <Flex key={itemId} align="center" gap={12} mb={8} style={{ padding: "8px 12px", background: "#F9FAFB", borderRadius: 8 }}>
                <Box style={{ flex: 1 }}>
                  <Text fz={13} fw={500} ff="Inter" color="#374151">{ci.name}</Text>
                  {ci.noteType?.name && <Text fz={11} ff="Inter" color="#9CA3AF">Note Type: {ci.noteType.name}</Text>}
                  {ci.taskType?.name && <Text fz={11} ff="Inter" color="#9CA3AF">Task Type: {ci.taskType.name}</Text>}
                </Box>
                <Group spacing={6}>
                  <Progress value={rd.score ?? 0} color={getScoreColor(rd.score ?? 0)} size="xs" style={{ width: 50 }} radius="xl" />
                  <Text fz={12} fw={600} ff="Inter" color={getScoreColor(rd.score ?? 0)}>{rd.score ?? 0}%</Text>
                </Group>
                <Text fz={12} fw={600} ff="Inter" color="#EF4444" style={{ minWidth: 24, textAlign: "center" }}>{rd.clientsMissing ?? 0}</Text>
                <Text fz={12} fw={600} ff="Inter" color="#374151" style={{ minWidth: 24, textAlign: "center" }}>{rd.incomplete ?? 0}</Text>
              </Flex>
            );
          })}
        </Box>
      );
    };

    return (
      <Box>
        <Text fz={13} color="dimmed" ff="Inter" mb={16}>
          Checked daily for each client in every facility. Notes and Tasks require daily entries per client.
          MAR checks that every medication status is not &quot;Missed&quot;.
        </Text>

        <SimpleGrid cols={3} breakpoints={[{ maxWidth: "sm", cols: 1 }, { maxWidth: "md", cols: 2 }]} mb={20} data-tour="charting-stats">
          {/* ── Note ── */}
          <Paper className={classes.dashCard} style={{ opacity: chart.notes?.active === false ? 0.5 : 1 }}>
            <Group position="apart" mb={8}>
              <Badge variant="light" color="gray" size="sm" radius="sm" ff="Inter" fw={600}>NOTE</Badge>
            </Group>
            <Text ff="Inter" fz={15} fw={600} color="#1F2937" mb={2}>Note</Text>
            <Text fz={12} ff="Inter" color="#9CA3AF" mb={8}>{noteDefaultRuleActive ? "Default: Progress Note" : "Progress Note (inactive)"}{noteExtras.length > 0 ? ` + ${noteExtras.length} rule${noteExtras.length > 1 ? "s" : ""}` : ""}</Text>
            <Group spacing={6} mb={6}>
              <Progress value={chart.notes?.active !== false ? (combinedNoteScore ?? 0) : 0} color={getScoreColor(combinedNoteScore ?? 0)} size="sm" style={{ flex: 1 }} radius="xl" />
              <Text fz={13} fw={600} ff="Inter" color={getScoreColor(combinedNoteScore ?? 0)}>{chart.notes?.active !== false ? (combinedNoteScore ?? 0) : 0}%</Text>
            </Group>
            <Divider my={8} />
            <Group spacing={16}>
              <Box>
                <Text fz={18} fw={700} ff="Inter" color="#EF4444">{combinedNotesMissing}</Text>
                <Text fz={11} ff="Inter" color="#6B7280">Clients Missing</Text>
              </Box>
              <Box>
                <Text fz={18} fw={700} ff="Inter" color="#374151">{combinedNotesIncomplete}</Text>
                <Text fz={11} ff="Inter" color="#6B7280">Missing Entries</Text>
              </Box>
            </Group>
            {!chart.notes?.noteTypeFound && (
              <Text fz={11} ff="Inter" color="#EF4444" mt={6}>Progress Note type not found in system</Text>
            )}
            <Flex justify="flex-end" mt={12}>
              <Button size="xs" variant="filled" radius="md" ff="Inter" styles={{ root: { backgroundColor: "#FFAF4A", color: "#171717", "&:hover": { backgroundColor: "#F5A623" } } }}
                onClick={() => promptAuditTimeframe("notes")}>
                Run Audit
              </Button>
            </Flex>
            {renderExtraRulesTable(noteExtras)}
          </Paper>

          {/* ── Task ── */}
          <Paper className={classes.dashCard} style={{ opacity: chart.tasks?.active === false ? 0.5 : 1 }}>
            <Group position="apart" mb={8}>
              <Badge variant="light" color="gray" size="sm" radius="sm" ff="Inter" fw={600}>TASK</Badge>
            </Group>
            <Text ff="Inter" fz={15} fw={600} color="#1F2937" mb={2}>Task</Text>
            <Text fz={12} ff="Inter" color="#9CA3AF" mb={8}>{taskDefaultRuleActive ? "Default: Activities of Daily Living" : "Activities of Daily Living (inactive)"}{taskExtras.length > 0 ? ` + ${taskExtras.length} rule${taskExtras.length > 1 ? "s" : ""}` : ""}</Text>
            <Group spacing={6} mb={6}>
              <Progress value={chart.tasks?.active !== false ? (combinedTaskScore ?? 0) : 0} color={getScoreColor(combinedTaskScore ?? 0)} size="sm" style={{ flex: 1 }} radius="xl" />
              <Text fz={13} fw={600} ff="Inter" color={getScoreColor(combinedTaskScore ?? 0)}>{chart.tasks?.active !== false ? (combinedTaskScore ?? 0) : 0}%</Text>
            </Group>
            <Divider my={8} />
            <Group spacing={16}>
              <Box>
                <Text fz={18} fw={700} ff="Inter" color="#EF4444">{combinedTasksMissing}</Text>
                <Text fz={11} ff="Inter" color="#6B7280">Clients Missing</Text>
              </Box>
              <Box>
                <Text fz={18} fw={700} ff="Inter" color="#374151">{combinedTasksIncomplete}</Text>
                <Text fz={11} ff="Inter" color="#6B7280">Missing Entries</Text>
              </Box>
            </Group>
            {!chart.tasks?.taskTypeFound && (
              <Text fz={11} ff="Inter" color="#EF4444" mt={6}>Activities of Daily Living task type not found</Text>
            )}
            <Flex justify="flex-end" mt={12}>
              <Button size="xs" variant="filled" radius="md" ff="Inter" styles={{ root: { backgroundColor: "#FFAF4A", color: "#171717", "&:hover": { backgroundColor: "#F5A623" } } }}
                onClick={() => promptAuditTimeframe("tasks")}>
                Run Audit
              </Button>
            </Flex>
            {renderExtraRulesTable(taskExtras)}
          </Paper>

          {/* ── MAR ── */}
          <Paper className={classes.dashCard} style={{ opacity: chart.mar?.active === false ? 0.5 : 1 }}>
            <Group position="apart" mb={8}>
              <Badge variant="light" color="gray" size="sm" radius="sm" ff="Inter" fw={600}>MAR</Badge>
            </Group>
            <Text ff="Inter" fz={15} fw={600} color="#1F2937" mb={2}>MAR</Text>
            <Text fz={12} ff="Inter" color="#9CA3AF" mb={8}>Default: Missed MARs{marExtras.length > 0 ? ` + ${marExtras.length} rule${marExtras.length > 1 ? "s" : ""}` : ""}</Text>
            <Group spacing={6} mb={6}>
              <Progress value={chart.mar?.active !== false ? (combinedMarScore ?? 0) : 0} color={getScoreColor(combinedMarScore ?? 0)} size="sm" style={{ flex: 1 }} radius="xl" />
              <Text fz={13} fw={600} ff="Inter" color={getScoreColor(combinedMarScore ?? 0)}>{chart.mar?.active !== false ? (combinedMarScore ?? 0) : 0}%</Text>
            </Group>
            <Divider my={8} />
            <Group spacing={16}>
              <Box>
                <Text fz={18} fw={700} ff="Inter" color="#EF4444">{combinedMarMissing}</Text>
                <Text fz={11} ff="Inter" color="#6B7280">Clients Missing</Text>
              </Box>
              <Box>
                <Text fz={18} fw={700} ff="Inter" color="#374151">{combinedMarIncomplete}</Text>
                <Text fz={11} ff="Inter" color="#6B7280">Missed Entries</Text>
              </Box>
            </Group>
            <Flex justify="flex-end" mt={12}>
              <Button size="xs" variant="filled" radius="md" ff="Inter" styles={{ root: { backgroundColor: "#FFAF4A", color: "#171717", "&:hover": { backgroundColor: "#F5A623" } } }}
                onClick={() => promptAuditTimeframe("mar")}>
                Run Audit
              </Button>
            </Flex>
            {renderExtraRulesTable(marExtras)}
          </Paper>
        </SimpleGrid>
        <Box mb={40} />
      </Box>
    );
  };

  // ═══════════════════════════════════════════════
  //  OPERATIONAL TAB (Task completion rate)
  // ═══════════════════════════════════════════════
  // ── Shared task helpers (used by both renderOperational and renderOperations) ──
  const formatTime12h = (time) => {
    if (!time) return "9:00 AM";
    if (time.includes("AM") || time.includes("PM")) return time;
    const [hh, mm] = time.split(":").map(Number);
    const period = hh >= 12 ? "PM" : "AM";
    const h12 = hh === 0 ? 12 : hh > 12 ? hh - 12 : hh;
    return `${h12}:${String(mm).padStart(2, "0")} ${period}`;
  };
  const scheduleLabel = (t) => {
    if (t.scheduleType === "one_time") return `Due ${moment(t.dueDate).format("MMM DD, YYYY")}`;
    if (t.recurrence?.frequency === "daily") return `Daily at ${formatTime12h(t.recurrence.timeOfDay)}`;
    if (t.recurrence?.frequency === "weekly") {
      const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      return `Weekly - ${days[t.recurrence.dayOfWeek || 0]} at ${formatTime12h(t.recurrence.timeOfDay)}`;
    }
    if (t.recurrence?.frequency === "monthly") return `Monthly - Day ${t.recurrence.dayOfMonth || 1} at ${formatTime12h(t.recurrence.timeOfDay)}`;
    return "\u2014";
  };
  const getRelativeTime = (t) => {
    if (t.status === "complete") return null;
    let dueDate = null;
    if (t.scheduleType === "one_time" && t.dueDate) {
      dueDate = moment(t.dueDate);
    } else if (t.recurrence?.timeOfDay) {
      const [hh, mm] = (t.recurrence.timeOfDay || "09:00").split(":").map(Number);
      dueDate = moment().startOf("day").add(hh, "hours").add(mm || 0, "minutes");
      if (dueDate.isBefore(moment())) {
        if (t.recurrence.frequency === "daily") dueDate.add(1, "day");
        else if (t.recurrence.frequency === "weekly") dueDate.add(1, "week");
        else if (t.recurrence.frequency === "monthly") dueDate.add(1, "month");
      }
    }
    if (!dueDate) return null;
    const now = moment();
    const diff = dueDate.diff(now);
    if (diff > 0) {
      const duration = moment.duration(diff);
      if (duration.asHours() < 1) return { text: `Due in ${Math.round(duration.asMinutes())}m`, overdue: false };
      if (duration.asHours() < 24) return { text: `Due in ${Math.round(duration.asHours())}h`, overdue: false };
      return { text: `Due in ${Math.round(duration.asDays())}d`, overdue: false };
    } else {
      const duration = moment.duration(-diff);
      if (duration.asHours() < 1) return { text: `Overdue by ${Math.round(duration.asMinutes())}m`, overdue: true };
      if (duration.asHours() < 24) return { text: `Overdue by ${Math.round(duration.asHours())}h`, overdue: true };
      return { text: `Overdue by ${Math.round(duration.asDays())}d`, overdue: true };
    }
  };
  const statusPillConfig = (status, isOverdue) => {
    if (isOverdue && status !== "complete") return { bg: "#F3F4F6", color: "#171717", dotColor: "#EF4444", label: "Overdue" };
    if (status === "complete") return { bg: "#F3F4F6", color: "#171717", dotColor: "#171717", label: "Complete" };
    if (status === "in_progress") return { bg: "#F3F4F6", color: "#374151", dotColor: "#374151", label: "In-Progress" };
    return { bg: "#F3F4F6", color: "#9CA3AF", dotColor: "#D1D5DB", label: "Not Started" };
  };
  const cycleStatus = (taskId, currentStatus) => {
    const order = ["not_started", "in_progress", "complete"];
    const idx = order.indexOf(currentStatus);
    const next = order[(idx + 1) % order.length];
    handleStatusChange(taskId, next);
  };
  const avatarColors = ["#FFAF4A", "#171717", "#FFAF4A", "#171717", "#FFAF4A", "#171717", "#FFAF4A", "#171717"];
  const getAvatarColor = (name) => {
    let hash = 0;
    for (let i = 0; i < (name || "").length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return avatarColors[Math.abs(hash) % avatarColors.length];
  };
  const handleStatusChange = async (taskId, newStatus) => {
    const result = await updateOperationalTaskStatus(taskId, newStatus);
    if (result) {
      if (opTaskStatusFilter !== "all") {
        setOpTaskPage(1);
        getOperationalTasks(1, 10, opTaskStatusFilter, opTaskSearch);
      } else {
        getOperationalTasks(opTaskPage, 10, opTaskStatusFilter, opTaskSearch);
      }
      getComplianceDashboard();
    }
  };
  const handleDeleteTask = (taskId) => {
    swal({ title: "Delete Task?", text: "This action cannot be undone.", icon: "warning", buttons: ["Cancel", "Delete"], dangerMode: true }).then(async (confirm) => {
      if (confirm) {
        const result = await deleteOperationalTask(taskId);
        if (result) {
          setOpTaskPage(1);
          getOperationalTasks(1, 10, opTaskStatusFilter, opTaskSearch);
          getComplianceDashboard();
        }
      }
    });
  };

  const renderOperational = () => {
    const ops = dashboard?.operational ?? {};

    const statusLabel = (s) => {
      if (s === "complete") return "Complete";
      if (s === "in_progress") return "In-Progress";
      return "Not Started";
    };

    return (
      <Box>
        <Flex align="center" justify="space-between" mb={0}>
          <Text className={classes.title}>Operational Compliance</Text>
          <Text ff="Inter" fz={12} c="#9CA3AF" fw={500}>
            Week of {getCurrentWeekLabel()}
          </Text>
        </Flex>
        <Text fz={13} color="dimmed" ff="Inter" mb={16}>
          Tracks assigned task completion rates across all clients and staff.
        </Text>

        {/* Stats cards */}
        <SimpleGrid cols={4} breakpoints={[{ maxWidth: "sm", cols: 2 }, { maxWidth: "xs", cols: 1 }]} mb={24}>
          {/* Completion Rate */}
          <Paper className={classes.opStatCard}>
            <Flex align="center" gap={12} mb={10}>
              <Box className={classes.opStatIconWrap} style={{ background: "#FFF8EB" }}>
                <IconChartBar size={20} color="#FFAF4A" />
              </Box>
              <Text className={classes.scoreCardTitle} style={{ marginBottom: 0 }}>Completion Rate</Text>
            </Flex>
            <Text className={classes.scoreCardValue} color="#171717">
              {ops.score ?? 0}%
            </Text>
            <Box className={classes.scoreBar}>
              <Box style={{
                height: "100%",
                width: `${ops.score ?? 0}%`,
                borderRadius: 4,
                background: `linear-gradient(90deg, ${getScoreColor(ops.score ?? 0)}, ${getScoreColor(ops.score ?? 0)}88)`,
                transition: "width 0.6s ease",
              }} />
            </Box>
          </Paper>

          {/* Total Tasks */}
          <Paper className={classes.opStatCard}>
            <Flex align="center" gap={12} mb={10}>
              <Box className={classes.opStatIconWrap} style={{ background: "#F3F4F6" }}>
                <IconClipboardList size={20} color="#171717" />
              </Box>
              <Text className={classes.scoreCardTitle} style={{ marginBottom: 0 }}>Total Tasks (30d)</Text>
            </Flex>
            <Text className={classes.scoreCardValue}>{ops.totalTasks ?? 0}</Text>
          </Paper>

          {/* Completed */}
          <Paper className={classes.opStatCard}>
            <Flex align="center" gap={12} mb={10}>
              <Box className={classes.opStatIconWrap} style={{ background: "#F3F4F6" }}>
                <IconChecklist size={20} color="#171717" />
              </Box>
              <Text className={classes.scoreCardTitle} style={{ marginBottom: 0 }}>Completed</Text>
            </Flex>
            <Text className={classes.scoreCardValue} color="#171717">{ops.completedTasks ?? 0}</Text>
          </Paper>

          {/* Overdue */}
          <Paper className={`${classes.opStatCard}${(ops.overdueTasks ?? 0) > 0 ? ` ${classes.opOverduePulse}` : ""}`} data-tour="overdue-staff-tasks">
            <Flex align="center" gap={12} mb={10}>
              <Box className={classes.opStatIconWrap} style={{ background: (ops.overdueTasks ?? 0) > 0 ? "#FEF2F2" : "#F3F4F6" }}>
                <IconUrgent size={20} color={(ops.overdueTasks ?? 0) > 0 ? "#EF4444" : "#171717"} />
              </Box>
              <Text className={classes.scoreCardTitle} style={{ marginBottom: 0 }}>Overdue</Text>
            </Flex>
            <Text className={classes.scoreCardValue} color={(ops.overdueTasks ?? 0) > 0 ? "#EF4444" : "#171717"}>{ops.overdueTasks ?? 0}</Text>
          </Paper>
        </SimpleGrid>

        {/* Task List Header */}
        <Flex align="center" justify="space-between" wrap="wrap" gap={8} mt={15} mb={12}>
          <Flex align="center" gap={12} wrap="wrap">
            <Text className={classes.sectionTitle} style={{ margin: 0 }}>Staff Tasks</Text>
            <Select
              placeholder="Status"
              data={[
                { value: "all", label: "All Statuses" },
                { value: "not_started", label: "Not Started" },
                { value: "in_progress", label: "In-Progress" },
                { value: "complete", label: "Complete" },
              ]}
              value={opTaskStatusFilter}
              onChange={(v) => { setOpTaskStatusFilter(v); setOpTaskPage(1); }}
              radius="md"
              size="xs"
              style={{ width: 160 }}
              styles={{ input: { fontFamily: "Inter", fontSize: 13 } }}
            />
          </Flex>
          <NewAddButton
            title="Add Task"
            icon={<FaPlus />}
            onClick={() => { setEditingOpTask(null); openOpTaskModal(); }}
            color="#FFAF4A"
            textColor="#000"
            variant="filled"
            borderColor="none"
          />
        </Flex>

        {/* Task Table */}
        <Box className={classes.table}>
          <ScrollArea type="auto">
            <Table withBorder fontSize="sm" verticalSpacing="md" style={{ minWidth: 700 }}>
              <thead>
                <tr>
                  <th style={{ minWidth: 200, fontFamily: "Inter", fontSize: 12, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>Title</th>
                  <th style={{ minWidth: 200, fontFamily: "Inter", fontSize: 12, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>Assigned Staff</th>
                  <th style={{ minWidth: 100, fontFamily: "Inter", fontSize: 12, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>Task Type</th>
                  <th style={{ minWidth: 100, fontFamily: "Inter", fontSize: 12, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>Schedule</th>
                  <th style={{ minWidth: 220, fontFamily: "Inter", fontSize: 12, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>Due Date/Time</th>
                  <th style={{ minWidth: 130, fontFamily: "Inter", fontSize: 12, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>Status</th>
                  <th style={{ minWidth: 100, fontFamily: "Inter", fontSize: 12, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {operationalTasksLoading ? (
                  <tr><td colSpan={7}><Flex justify="center" py={30}><Loader color="#FFAF4A" size="sm" /></Flex></td></tr>
                ) : operationalTasks.length > 0 ? (
                  operationalTasks.map((t, idx) => {
                    const pill = statusPillConfig(t.status, t.isOverdue);
                    const relative = getRelativeTime(t);
                    const isOverdueRow = t.isOverdue && t.status !== "complete";
                    return (
                      <tr
                        key={t._id}
                        className={`${classes.opTableRow}${isOverdueRow ? ` ${classes.opOverdueRow}` : ""}`}
                        style={{ background: isOverdueRow ? undefined : (idx % 2 === 0 ? "#FFFFFF" : "#FAFAFA") }}
                      >
                        <td>
                          <Text fz={14} fw={500} ff="Inter" color="#171717">{t.title}</Text>
                          {t.description && <Text fz={12} color="dimmed" ff="Inter" lineClamp={1}>{t.description}</Text>}
                        </td>
                        <td>
                          <Flex gap={6} align="center" wrap="nowrap">
                            {(t.staff || []).slice(0, 1).map((s) => {
                              const initials = `${(s.firstName || "")[0] || ""}${(s.lastName || "")[0] || ""}`.toUpperCase();
                              const fullName = `${s.firstName || ""} ${s.lastName || ""}`.trim();
                              return (
                                <Box key={s._id} className={classes.opStaffAvatar} style={{ flexShrink: 0 }}>
                                  <Box className={classes.opStaffInitial} style={{ background: getAvatarColor(fullName) }}>
                                    {initials}
                                  </Box>
                                  {fullName}
                                </Box>
                              );
                            })}
                            {(t.staff || []).length > 1 && (
                              <Popover width={240} position="bottom-start" shadow="md" withArrow>
                                <Popover.Target>
                                  <Box className={classes.opStaffAvatar} style={{ cursor: "pointer", borderColor: "#F59E0B", background: "#FFFBEB", flexShrink: 0 }}>
                                    <Box className={classes.opStaffInitial} style={{ background: "#F59E0B" }}>
                                      +{t.staff.length - 1}
                                    </Box>
                                    more
                                  </Box>
                                </Popover.Target>
                                <Popover.Dropdown style={{ padding: 12 }}>
                                  <Text ff="Inter" fz={12} fw={700} c="#374151" mb={8}>
                                    All Assigned Staff ({t.staff.length})
                                  </Text>
                                  <Box style={{ maxHeight: 360, overflowY: "auto", scrollbarWidth: "thin", scrollbarColor: "#D1D5DB transparent" }}>
                                    {t.staff.map((s) => {
                                      const fullName = `${s.firstName || ""} ${s.lastName || ""}`.trim();
                                      const initials = `${(s.firstName || "")[0] || ""}${(s.lastName || "")[0] || ""}`.toUpperCase();
                                      return (
                                        <Flex key={s._id} align="center" gap={8} mb={6}>
                                          <Box style={{
                                            width: 26, height: 26, borderRadius: "50%", backgroundColor: getAvatarColor(fullName),
                                            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                                          }}>
                                            <Text ff="Inter" fz={9} fw={700} c="#fff">{initials}</Text>
                                          </Box>
                                          <Text ff="Inter" fz={12} fw={500} c="#374151">{fullName}</Text>
                                        </Flex>
                                      );
                                    })}
                                  </Box>
                                </Popover.Dropdown>
                              </Popover>
                            )}
                          </Flex>
                        </td>
                        <td>
                          {t.taskType === "system" ? (
                            <Box className={classes.opTaskTypeBadge} style={{ background: "#F3F4F6", color: "#374151" }}>
                              System Task
                            </Box>
                          ) : (
                            <Box className={classes.opTaskTypeBadge} style={{ background: "#F3F4F6", color: "#374151" }}>
                              Admin Task
                            </Box>
                          )}
                        </td>
                        <td>
                          <Badge color="gray" variant="light" size="sm">
                            {t.scheduleType === "recurring" ? "Recurring" : "One Time"}
                          </Badge>
                        </td>
                        <td>
                          <Flex align="center" gap={0} wrap="nowrap">
                            <Text fz={13} ff="Inter" color="#374151" style={{ whiteSpace: "nowrap" }}>
                              {scheduleLabel(t)}
                            </Text>
                            {relative && (
                              <Text
                                component="span"
                                className={classes.opRelativeTime}
                                color={relative.overdue ? "#EF4444" : "#9CA3AF"}
                                style={{ whiteSpace: "nowrap" }}
                              >
                                {" \u00B7 "}{relative.text}
                              </Text>
                            )}
                          </Flex>
                        </td>
                        <td>
                          <Tooltip label="Click to update status" position="top" withArrow>
                            <Box
                              className={classes.opStatusPill}
                              style={{ background: pill.bg, color: pill.color }}
                              onClick={() => cycleStatus(t._id, t.status)}
                            >
                              {t.status === "complete" ? (
                                <IconCircleCheck size={14} />
                              ) : (
                                <IconCircleFilled size={8} style={{ color: pill.dotColor }} />
                              )}
                              {pill.label}
                            </Box>
                          </Tooltip>
                        </td>
                        <td>
                          <Group spacing={4}>
                            <ActionIcon size="sm" variant="subtle" color="dark" onClick={() => { setEditingOpTask(t); openOpTaskModal(); }}>
                              <IconEdit size={15} />
                            </ActionIcon>
                            <ActionIcon size="sm" variant="subtle" color="red" onClick={() => handleDeleteTask(t._id)}>
                              <IconTrash size={15} />
                            </ActionIcon>
                          </Group>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr><td colSpan={7}><RecordNotFound /></td></tr>
                )}
              </tbody>
            </Table>
          </ScrollArea>
          <CustomBillingPagination
            data={operationalTasksCount}
            itemsPerPage={10}
            currentPage={opTaskPage}
            handlePageChange={(page) => setOpTaskPage(page)}
          />
        </Box>
      </Box>
    );
  };

  const getAddButton = () => {
    return null;
  };

  // ─── Audit (AI Regulatory Assistant) ─────────────────────────────────────────

  const MOCK_COMPLIANCE_ITEMS = [
    {
      id: "1", name: "Quarterly Fire Drill", category: "Fire Safety",
      status: "overdue", dueDate: "Feb 28, 2026", lastCompleted: "Nov 15, 2025",
      description: "Document and certify quarterly fire drill including all residents and staff.",
      fields: ["drill_date", "staff_present", "residents_present", "duration_minutes", "issues_noted", "corrective_actions", "conducted_by", "supervisor_signature"],
      chat: [
        { role: "assistant", content: "Hi! I'll help you complete the Q1 Fire Drill record for your facility. Let's get started — when did the drill take place?", suggestions: ["Today", "Yesterday", "Pick a date"] },
        { role: "user", content: "Today" },
        { role: "assistant", content: "Got it. Were all residents present and accounted for during the drill?", suggestions: ["Yes, all present", "No — some absent (explain)"] },
        { role: "user", content: "Yes, all present" },
        { role: "assistant", content: "Great. How many staff members participated?", suggestions: ["All on-duty staff", "Enter a number"] },
        { role: "user", content: "All on-duty staff" },
        { role: "assistant", content: "How long did the drill take from alarm to full evacuation?", suggestions: ["Under 3 minutes", "3-5 minutes", "Over 5 minutes"] },
      ],
    },
    {
      id: "2", name: "Medication Administration Log", category: "Medication",
      status: "due_soon", dueDate: "Mar 31, 2026", lastCompleted: "Feb 28, 2026",
      description: "Monthly documentation of all medication administration records.",
      fields: ["month", "resident_name", "medication", "dosage", "frequency", "administered_by"],
      chat: [
        { role: "assistant", content: "Let's complete the March Medication Administration Log. Which resident would you like to start with?", suggestions: ["John Smith", "Mary Johnson", "Enter name"] },
      ],
    },
    {
      id: "3", name: "Staff Training Records", category: "Staffing",
      status: "compliant", dueDate: "Dec 31, 2026", lastCompleted: "Jan 10, 2026",
      description: "Annual documentation of all required staff training completions.",
      fields: ["training_type", "staff_name", "date_completed", "trainer", "certificate_number"],
      chat: [],
    },
    {
      id: "4", name: "Resident Rights Acknowledgement", category: "Rights & Privacy",
      status: "compliant", dueDate: "Jan 1, 2027", lastCompleted: "Jan 5, 2026",
      description: "Annual acknowledgement of resident rights and privacy policies.",
      fields: ["resident_name", "date_signed", "witnessed_by"],
      chat: [],
    },
    {
      id: "5", name: "Emergency Evacuation Plan Review", category: "Fire Safety",
      status: "compliant", dueDate: "Dec 31, 2026", lastCompleted: "Feb 1, 2026",
      description: "Annual review and update of the facility emergency evacuation plan.",
      fields: ["review_date", "changes_made", "approved_by"],
      chat: [],
    },
    {
      id: "6", name: "Incident Report Log", category: "Safety",
      status: "compliant", dueDate: "Apr 30, 2026", lastCompleted: "Mar 1, 2026",
      description: "Monthly documentation of any resident or facility incidents.",
      fields: ["incident_date", "description", "residents_involved", "staff_involved", "action_taken"],
      chat: [],
    },
    {
      id: "7", name: "Vehicle Inspection Log", category: "Transportation",
      status: "compliant", dueDate: "Mar 31, 2026", lastCompleted: "Mar 5, 2026",
      description: "Monthly vehicle safety inspection for facility transport vehicles.",
      fields: ["vehicle_id", "inspection_date", "inspector", "defects_noted", "cleared_by"],
      chat: [],
    },
  ];

  const auditStatusConfig = {
    overdue:   { label: "Overdue",  color: "#171717", bg: "#F3F4F6", border: "#E5E7EB" },
    due_soon:  { label: "Due Soon", color: "#374151", bg: "#F3F4F6", border: "#E5E7EB" },
    compliant: { label: "Compliant", color: "#171717", bg: "#F3F4F6", border: "#E5E7EB" },
  };

  const openChat = (item) => {
    setChatMessages(item.chat.length > 0 ? item.chat : [
      { role: "assistant", content: `Hi! I'll help you complete the ${item.name} record for your facility. Let's get started.`, suggestions: ["Let's begin"] },
    ]);
    setChatProgress(Math.floor((item.chat.filter(m => m.role === "user").length / item.fields.length) * 100));
    setChatModal({ open: true, item });
  };

  const handleSuggestionClick = (suggestion) => {
    const userMsg = { role: "user", content: suggestion };
    const nextProgress = Math.min(chatProgress + Math.floor(100 / (chatModal.item?.fields?.length || 6)), 100);
    const aiReplies = [
      { content: "Perfect. And who was the supervising staff member on duty during the drill?", suggestions: ["Sarah Johnson (Manager)", "Enter name"] },
      { content: "Noted. Were there any issues or hazards identified during the evacuation?", suggestions: ["No issues noted", "Yes — describe below"] },
      { content: "Great. This document is almost complete. Please review the summary and confirm to generate your PDF.", suggestions: ["Generate PDF"] },
    ];
    const nextAiMsg = aiReplies[Math.min(chatMessages.filter(m => m.role === "user").length, aiReplies.length - 1)];
    setChatMessages(prev => [...prev, userMsg, { role: "assistant", ...nextAiMsg }]);
    setChatProgress(nextProgress);
    setChatInput("");
  };

  const handleChatSend = () => {
    if (!chatInput.trim()) return;
    handleSuggestionClick(chatInput.trim());
    setChatInput("");
  };

  const auditOverdue = MOCK_COMPLIANCE_ITEMS.filter(i => i.status === "overdue");
  const auditDueSoon = MOCK_COMPLIANCE_ITEMS.filter(i => i.status === "due_soon");
  const auditCompliant = MOCK_COMPLIANCE_ITEMS.filter(i => i.status === "compliant");

  const renderAudit = () => (
    <Box>
      {/* Summary bar */}
      <Box style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        {[
          { label: "Overdue", count: auditOverdue.length, color: "#171717", bg: "#F3F4F6" },
          { label: "Due Soon", count: auditDueSoon.length, color: "#374151", bg: "#F3F4F6" },
          { label: "Compliant", count: auditCompliant.length, color: "#171717", bg: "#F3F4F6" },
          { label: "Total", count: MOCK_COMPLIANCE_ITEMS.length, color: "#6B7280", bg: "#F9FAFB" },
        ].map(s => (
          <Box key={s.label} style={{
            background: s.bg, borderRadius: 12, padding: "16px 24px",
            flex: "1 1 120px", minWidth: 120, textAlign: "center", border: `1px solid ${s.color}22`,
          }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: s.color, fontFamily: "Inter" }}>{s.count}</div>
            <div style={{ fontSize: 12, color: "#737373", fontFamily: "Inter", fontWeight: 500 }}>{s.label}</div>
          </Box>
        ))}
      </Box>

      {/* Cards grid */}
      <Box style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 }}>
        {MOCK_COMPLIANCE_ITEMS.map(item => {
          const cfg = auditStatusConfig[item.status];
          return (
            <Box key={item.id} style={{
              background: "#fff", borderRadius: 12, border: `1px solid ${cfg.border}`,
              padding: 20, display: "flex", flexDirection: "column", gap: 10,
            }}>
              {/* Status pill */}
              <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <span style={{
                  background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
                  borderRadius: 20, fontSize: 11, fontWeight: 600, padding: "2px 10px",
                  fontFamily: "Inter",
                }}>
                  {item.status === "overdue" ? "\u{1F534}" : item.status === "due_soon" ? "\u26A0\uFE0F" : "\u2713"} {cfg.label}
                </span>
                <span style={{ fontSize: 11, color: "#737373", fontFamily: "Inter" }}>{item.category}</span>
              </Box>

              {/* Title */}
              <div style={{ fontFamily: "Inter", fontWeight: 600, fontSize: 15, color: "#171717" }}>
                {item.name}
              </div>
              <div style={{ fontFamily: "Inter", fontSize: 12, color: "#737373", lineHeight: 1.5 }}>
                {item.description}
              </div>

              {/* Dates */}
              <Box style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 4 }}>
                <div style={{ fontSize: 12, fontFamily: "Inter", color: "#737373" }}>
                  <span style={{ fontWeight: 600, color: "#374151" }}>Due:</span> {item.dueDate}
                </div>
                <div style={{ fontSize: 12, fontFamily: "Inter", color: "#737373" }}>
                  <span style={{ fontWeight: 600, color: "#374151" }}>Last completed:</span> {item.lastCompleted}
                </div>
              </Box>

              {/* Action button */}
              <button
                onClick={() => item.status !== "compliant" && openChat(item)}
                style={{
                  marginTop: 8, padding: "9px 0", borderRadius: 8, border: "none",
                  cursor: item.status !== "compliant" ? "pointer" : "default",
                  fontFamily: "Inter", fontWeight: 600, fontSize: 13,
                  background: item.status === "compliant" ? "#F3F4F6" : "linear-gradient(135deg, #E8A500, #FF6B35)",
                  color: item.status === "compliant" ? "#9CA3AF" : "#fff",
                  transition: "opacity 0.15s",
                }}
              >
                {item.status === "compliant" ? "\u2713 Up to date" : "Start with AI \u2192"}
              </button>
            </Box>
          );
        })}
      </Box>

      {/* Chat Modal */}
      {chatModal.open && (
        <Box style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 1000, padding: 20,
        }}
          onClick={(e) => { if (e.target === e.currentTarget) setChatModal({ open: false, item: null }); }}
        >
          <Box style={{
            background: "#fff", borderRadius: 16, width: "100%", maxWidth: 480,
            maxHeight: "80vh", display: "flex", flexDirection: "column",
            boxShadow: "0 25px 50px rgba(0,0,0,0.2)",
          }}>
            {/* Modal header */}
            <Box style={{
              padding: "16px 20px", borderBottom: "1px solid #F3F4F6",
              display: "flex", flexDirection: "column", gap: 8,
            }}>
              <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontFamily: "Inter", fontWeight: 700, fontSize: 15, color: "#171717" }}>
                  {chatModal.item?.name}
                </div>
                <button onClick={() => setChatModal({ open: false, item: null })}
                  style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "#9CA3AF" }}>{"\u2715"}</button>
              </Box>
              {/* Progress bar */}
              <Box style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Box style={{
                  flex: 1, height: 6, borderRadius: 3, background: "#F3F4F6", overflow: "hidden",
                }}>
                  <Box style={{
                    width: `${chatProgress}%`, height: "100%", borderRadius: 3,
                    background: "linear-gradient(90deg, #E8A500, #FF6B35)",
                    transition: "width 0.4s ease",
                  }} />
                </Box>
                <span style={{ fontSize: 11, color: "#9CA3AF", fontFamily: "Inter", whiteSpace: "nowrap" }}>
                  {chatProgress}% complete
                </span>
              </Box>
            </Box>

            {/* Messages */}
            <Box style={{ flex: 1, overflowY: "auto", padding: "16px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
              {chatMessages.map((msg, i) => (
                <Box key={i} style={{ display: "flex", flexDirection: "column", gap: 6,
                  alignItems: msg.role === "user" ? "flex-end" : "flex-start" }}>
                  <Box style={{
                    maxWidth: "80%", padding: "10px 14px", borderRadius: msg.role === "user" ? "12px 12px 0 12px" : "12px 12px 12px 0",
                    background: msg.role === "user" ? "linear-gradient(135deg, #E8A500, #FF6B35)" : "#F9FAFB",
                    color: msg.role === "user" ? "#fff" : "#171717",
                    fontSize: 13, fontFamily: "Inter", lineHeight: 1.5,
                    border: msg.role === "assistant" ? "1px solid #E5E7EB" : "none",
                  }}>
                    {msg.role === "assistant" && (
                      <span style={{ fontSize: 11, fontWeight: 600, color: "#E8A500", display: "block", marginBottom: 4 }}>
                        Ogiso AI
                      </span>
                    )}
                    {msg.content}
                  </Box>
                  {/* Suggestion chips */}
                  {msg.role === "assistant" && msg.suggestions && i === chatMessages.length - 1 && (
                    <Box style={{ display: "flex", flexWrap: "wrap", gap: 6, maxWidth: "90%" }}>
                      {msg.suggestions.map((s, si) => (
                        <button key={si} onClick={() => handleSuggestionClick(s)}
                          style={{
                            padding: "5px 12px", borderRadius: 20,
                            border: "1.5px solid #E8A500", background: "#fff",
                            color: "#E8A500", fontSize: 12, fontFamily: "Inter",
                            fontWeight: 500, cursor: "pointer",
                            transition: "all 0.15s",
                          }}
                          onMouseEnter={e => { e.target.style.background = "#E8A500"; e.target.style.color = "#fff"; }}
                          onMouseLeave={e => { e.target.style.background = "#fff"; e.target.style.color = "#E8A500"; }}
                        >
                          {s}
                        </button>
                      ))}
                    </Box>
                  )}
                </Box>
              ))}
            </Box>

            {/* Input */}
            <Box style={{ padding: "12px 16px", borderTop: "1px solid #F3F4F6", display: "flex", gap: 8 }}>
              <input
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleChatSend()}
                placeholder="Type your answer..."
                style={{
                  flex: 1, padding: "9px 14px", borderRadius: 8,
                  border: "1.5px solid #E5E7EB", fontSize: 13, fontFamily: "Inter",
                  outline: "none",
                }}
              />
              <button onClick={handleChatSend}
                style={{
                  padding: "9px 18px", borderRadius: 8, border: "none",
                  background: "linear-gradient(135deg, #E8A500, #FF6B35)",
                  color: "#fff", fontFamily: "Inter", fontWeight: 600,
                  fontSize: 13, cursor: "pointer",
                }}>
                Send
              </button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );

  /* ═══════════════════════════════════════════════════════
     FINANCE TAB — Monthly billing + invoice aggregation
     ═══════════════════════════════════════════════════════ */

  const renderFinance = () => {
    if (!scoringEnabled) {
      return renderDisabledBanner();
    }
    if (financeLoading) {
      return (
        <Flex justify="center" align="center" py={60}>
          <Loader size="md" color="#FFBA45" />
        </Flex>
      );
    }
    if (!financeData) {
      return (
        <Flex justify="center" align="center" py={60}>
          <Text c="#9CA3AF" fz={14} ff="Inter">No financial data available.</Text>
        </Flex>
      );
    }

    const m = financeData.monthly || {};
    const inv = m.invoices || {};
    const clm = m.claims || {};
    const at = financeData.allTime || {};
    const trend = financeData.trend || [];
    const maxTrendTotal = Math.max(...trend.map(r => (r.revenue || (r.invoiceTotal || 0) + (r.claimTotal || 0))), 1);

    // Revenue = invoices paid this month + claims paid this month
    const monthlyRevenue = (inv.paid || 0) + (clm.paid || 0);
    const unpaidInvoices = at.invoiceUnpaid || inv.unpaid || 0;
    // Total billed = invoices created + claims submitted this month
    const totalBilled = (inv.total || 0) + (clm.total || 0);
    // Total revenue combines paid invoices (including from prior months) + claims
    const totalRevenue = monthlyRevenue || totalBilled;

    // Claims breakdown data
    const clientsBilled = clm.clientsBilled || [];
    const payersList = clm.payersList || [];

    // Mini sparkline bars for stat cards (last 6 months)
    const sparkBars = trend.length > 0 ? trend.slice(-6) : [];
    const sparkMax = Math.max(...sparkBars.map(r => (r.revenue || (r.invoiceTotal || 0) + (r.claimTotal || 0))), 1);

    return (
      <Box data-tour="finance-content">
        {/* ─── Top Level Stats ─── */}
        <Box style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, padding: "24px 28px" }} mb={20}>
          <Flex justify="space-between" align="center" mb={20}>
            <Box>
              <Text ff="Inter" fw={700} fz={16} c="#111827">Top Level Stats</Text>
              <Text ff="Inter" fz={12} c="#9CA3AF">Financial overview for {financeData.currentMonth || "this month"}</Text>
            </Box>
          </Flex>
          <Grid gutter={0} style={{ borderTop: "1px solid #F3F4F6", paddingTop: 16 }}>
            {[
              { label: "Total Revenue", value: formatCurrency(totalRevenue), sub: `${inv.count || 0} invoices + ${clm.count || 0} claims`, color: "#111827" },
              { label: "Invoices", value: formatCurrency(inv.total || 0), sub: `${formatCurrency(inv.paid || 0)} paid`, color: "#111827" },
              { label: "Claims", value: formatCurrency(clm.total || 0), sub: `${clm.count || 0} submitted`, color: "#111827" },
              { label: "Unpaid", value: formatCurrency(unpaidInvoices), sub: unpaidInvoices > 0 ? "Action needed" : "All clear", color: unpaidInvoices > 0 ? "#DC2626" : "#111827" },
              { label: "Accepted", value: formatCurrency(clm.accepted || 0), sub: `${clm.acceptedCount || 0} claims`, color: "#111827" },
              { label: "Rejected", value: formatCurrency(clm.rejected || 0), sub: (clm.rejectedCount || 0) > 0 ? `${clm.rejectedCount} claims` : "None", color: (clm.rejectedCount || 0) > 0 ? "#DC2626" : "#111827" },
            ].map((stat, i, arr) => (
              <Grid.Col key={stat.label} span={6} sm={4} md={2} style={{ paddingRight: 16, paddingBottom: 12, borderRight: i % 3 !== 2 && i < arr.length - 1 ? "1px solid #F3F4F6" : "none", paddingLeft: i % 3 !== 0 ? 16 : 0 }}>
                <Text ff="Inter" fz={12} fw={500} c="#6B7280" mb={4}>{stat.label}</Text>
                <Text ff="Inter" fz={20} fw={700} c={stat.color}>{stat.value}</Text>
                <Text ff="Inter" fz={11} c="#9CA3AF" mt={2}>{stat.sub}</Text>
              </Grid.Col>
            ))}
          </Grid>
        </Box>

        {/* ─── Stat Cards with Sparklines ─── */}
        <Grid gutter={16} mb={20}>
          {[
            { label: "Total Revenue", value: formatCurrency(totalRevenue), icon: <IconCoin size={20} />, sub: `Invoices + Claims this month` },
            { label: "Claims Total", value: formatCurrency(clm.total || 0), icon: <IconFileInvoice size={20} />, sub: `${clm.count || 0} claims submitted` },
            { label: "Invoices Paid", value: formatCurrency(inv.paid || 0), icon: <IconReceipt size={20} />, sub: `${inv.count || 0} invoices this month` },
            { label: "Unpaid Balance", value: formatCurrency(unpaidInvoices), icon: <IconAlertCircle size={20} />, sub: unpaidInvoices > 0 ? "Requires attention" : "All invoices paid" },
          ].map((card) => (
            <Grid.Col span={12} xs={6} md={3} key={card.label}>
              <Box style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, padding: "20px", height: "100%" }}>
                <Flex justify="space-between" align="flex-start" mb={12}>
                  <Box style={{ width: 36, height: 36, borderRadius: 10, background: "#FFF8EB", display: "flex", alignItems: "center", justifyContent: "center", color: "#FFAF4A" }}>
                    {card.icon}
                  </Box>
                  {/* Mini sparkline */}
                  <Flex align="flex-end" gap={2} style={{ height: 28 }}>
                    {sparkBars.map((row, idx) => {
                      const total = (row.revenue || (row.invoiceTotal || 0) + (row.claimTotal || 0));
                      const h = Math.max(4, (total / sparkMax) * 24);
                      const isLast = idx === sparkBars.length - 1;
                      return <Box key={idx} style={{ width: 6, height: h, borderRadius: 2, backgroundColor: isLast ? "#FFBA45" : "#E5E7EB" }} />;
                    })}
                  </Flex>
                </Flex>
                <Text ff="Inter" fz={12} fw={500} c="#6B7280">{card.label}</Text>
                <Text ff="Inter" fz={22} fw={700} c="#111827" mt={2}>{card.value}</Text>
                <Text ff="Inter" fz={11} c="#9CA3AF" mt={4}>{card.sub}</Text>
              </Box>
            </Grid.Col>
          ))}
        </Grid>

        {/* ─── Revenue Trend ─── */}
        <Grid gutter={16} mb={20}>
          <Grid.Col span={12} md={8}>
            <Box style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, padding: "24px" }}>
              <Flex justify="space-between" align="center" mb={20}>
                <Box>
                  <Text ff="Inter" fw={600} fz={15} c="#111827">Revenue Trend</Text>
                  <Text ff="Inter" fz={12} c="#9CA3AF">Last 6 months comparison</Text>
                </Box>
              </Flex>

              {trend.length > 0 && (
                <Box style={{ overflowX: "auto" }}>
                <svg width="100%" height="240" viewBox="0 0 560 240" preserveAspectRatio="xMinYMid meet" style={{ overflow: "visible", minWidth: 320 }}>
                  {[0, 1, 2, 3, 4].map(i => (
                    <line key={i} x1="50" y1={20 + i * 44} x2="540" y2={20 + i * 44} stroke="#F3F4F6" strokeWidth="1" />
                  ))}
                  {[0, 1, 2, 3, 4].map(i => {
                    const val = maxTrendTotal - (i * maxTrendTotal / 4);
                    return <text key={i} x="45" y={24 + i * 44} textAnchor="end" fill="#9CA3AF" fontSize="10" fontFamily="Inter">{val >= 1000 ? `$${Math.round(val / 1000)}k` : `$${Math.round(val)}`}</text>;
                  })}
                  {(() => {
                    const chartH = 176;
                    const pts = trend.map((row, idx) => {
                      const total = (row.revenue || (row.invoiceTotal || 0) + (row.claimTotal || 0));
                      const x = 70 + idx * (460 / Math.max(trend.length - 1, 1));
                      const y = 20 + (1 - total / maxTrendTotal) * chartH;
                      return { x, y, total, month: row.month };
                    });
                    const pathD = pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
                    const areaD = `${pathD} L ${pts[pts.length - 1].x} ${20 + chartH} L ${pts[0].x} ${20 + chartH} Z`;
                    return (
                      <>
                        <defs><linearGradient id="trendFillV2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#FFBA45" stopOpacity="0.12" /><stop offset="100%" stopColor="#FFBA45" stopOpacity="0.01" /></linearGradient></defs>
                        <path d={areaD} fill="url(#trendFillV2)" />
                        <path d={pathD} fill="none" stroke="#FFBA45" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                        {pts.map((p, i) => (
                          <g key={i}>
                            <circle cx={p.x} cy={p.y} r="4" fill="#fff" stroke="#FFBA45" strokeWidth="2" />
                            {p.total > 0 && (
                              <text x={p.x} y={p.y - 10} textAnchor="middle" fill="#374151" fontSize="10" fontWeight="600" fontFamily="Inter">{formatCurrency(p.total)}</text>
                            )}
                            <text x={p.x} y={220} textAnchor="middle" fill="#9CA3AF" fontSize="10" fontFamily="Inter">{p.month?.split(" ")[0]?.slice(0, 3)}</text>
                          </g>
                        ))}
                      </>
                    );
                  })()}
                </svg>
                </Box>
              )}
            </Box>
          </Grid.Col>

          {/* Claims Summary Side Panel */}
          <Grid.Col span={12} md={4}>
            <Box style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, padding: "24px", height: "100%" }}>
              <Text ff="Inter" fw={600} fz={15} c="#111827" mb={4}>Claims Summary</Text>
              <Text ff="Inter" fz={12} c="#9CA3AF" mb={16}>{financeData.currentMonth || "This Month"}</Text>

              <Box style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <Box style={{ padding: "12px 14px", background: "#F9FAFB", borderRadius: 8 }}>
                  <Text ff="Inter" fz={11} c="#9CA3AF" mb={2}>Total Claims</Text>
                  <Flex justify="space-between" align="center">
                    <Text ff="Inter" fz={16} fw={700} c="#111827">{formatCurrency(clm.total || 0)}</Text>
                    <Text ff="Inter" fz={11} c="#6B7280">{clm.count || 0} claims</Text>
                  </Flex>
                </Box>
                <Box style={{ padding: "12px 14px", background: "#F9FAFB", borderRadius: 8 }}>
                  <Text ff="Inter" fz={11} c="#9CA3AF" mb={2}>Accepted</Text>
                  <Flex justify="space-between" align="center">
                    <Text ff="Inter" fz={14} fw={600} c="#111827">{formatCurrency(clm.accepted || 0)}</Text>
                    <Text ff="Inter" fz={11} c="#6B7280">{clm.acceptedCount || 0}</Text>
                  </Flex>
                </Box>
                <Box style={{ padding: "12px 14px", background: "#F9FAFB", borderRadius: 8 }}>
                  <Text ff="Inter" fz={11} c="#9CA3AF" mb={2}>Rejected</Text>
                  <Flex justify="space-between" align="center">
                    <Text ff="Inter" fz={14} fw={600} c={(clm.rejectedCount || 0) > 0 ? "#DC2626" : "#111827"}>{formatCurrency(clm.rejected || 0)}</Text>
                    <Text ff="Inter" fz={11} c="#6B7280">{clm.rejectedCount || 0}</Text>
                  </Flex>
                </Box>
              </Box>

              {/* Clients & Payers counts */}
              <Flex gap={12} mt={16}>
                <Box style={{ flex: 1, padding: "10px 12px", background: "#F9FAFB", borderRadius: 8, textAlign: "center" }}>
                  <Text ff="Inter" fz={18} fw={700} c="#111827">{clm.uniqueClients || 0}</Text>
                  <Text ff="Inter" fz={10} c="#9CA3AF">Clients Billed</Text>
                </Box>
                <Box style={{ flex: 1, padding: "10px 12px", background: "#F9FAFB", borderRadius: 8, textAlign: "center" }}>
                  <Text ff="Inter" fz={18} fw={700} c="#111827">{clm.uniquePayers || 0}</Text>
                  <Text ff="Inter" fz={10} c="#9CA3AF">Payers</Text>
                </Box>
              </Flex>
            </Box>
          </Grid.Col>
        </Grid>

        {/* ─── Clients Billed + Payers ─── */}
        <Grid gutter={16} mb={40}>
          <Grid.Col span={12} sm={6}>
            <Box style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, padding: "20px" }}>
              <Text ff="Inter" fw={600} fz={14} c="#111827" mb={12}>Clients Billed</Text>
              <Box style={{ maxHeight: 220, overflowY: "auto" }}>
                {clientsBilled.length === 0 ? (
                  <Text ff="Inter" fz={12} c="#9CA3AF" ta="center" py={20}>No claims this month</Text>
                ) : clientsBilled.map((c, idx) => (
                  <Flex key={idx} justify="space-between" align="center" style={{ padding: "10px 0", borderBottom: idx < clientsBilled.length - 1 ? "1px solid #F3F4F6" : "none" }}>
                    <Text ff="Inter" fz={13} c="#374151" lineClamp={1} style={{ maxWidth: 200 }}>{c.name}</Text>
                    <Flex align="center" gap={10}>
                      <Text ff="Inter" fz={11} c="#9CA3AF">{c.count} claims</Text>
                      <Text ff="Inter" fz={13} fw={600} c="#111827">{formatCurrency(c.total)}</Text>
                    </Flex>
                  </Flex>
                ))}
              </Box>
            </Box>
          </Grid.Col>
          <Grid.Col span={12} sm={6}>
            <Box style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, padding: "20px" }}>
              <Text ff="Inter" fw={600} fz={14} c="#111827" mb={12}>Payers</Text>
              <Box style={{ maxHeight: 220, overflowY: "auto" }}>
                {payersList.length === 0 ? (
                  <Text ff="Inter" fz={12} c="#9CA3AF" ta="center" py={20}>No claims this month</Text>
                ) : payersList.map((p, idx) => (
                  <Flex key={idx} justify="space-between" align="center" style={{ padding: "10px 0", borderBottom: idx < payersList.length - 1 ? "1px solid #F3F4F6" : "none" }}>
                    <Text ff="Inter" fz={13} c="#374151" lineClamp={1} style={{ maxWidth: 200 }}>{p.name}</Text>
                    <Flex align="center" gap={10}>
                      <Text ff="Inter" fz={11} c="#9CA3AF">{p.count} claims</Text>
                      <Text ff="Inter" fz={13} fw={600} c="#111827">{formatCurrency(p.total)}</Text>
                    </Flex>
                  </Flex>
                ))}
              </Box>
            </Box>
          </Grid.Col>
        </Grid>
      </Box>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "overview": return renderOverview();
      case "compliance": return renderCompliance();
      case "regulatory": return renderRegulatory();
      case "charting": return renderCharting();
      case "operations": return renderOperations();
      case "finance": return renderFinance();
      default: return renderOverview();
    }
  };

  // ── Compliance tab (merged Document + Charting) ──
  const [complianceSubTab, setComplianceSubTab] = useState("documents");
  const renderDisabledBanner = () => (
    <Paper p="xl" radius={12} style={{ background: "#F9FAFB", border: "1px dashed #E5E7EB", textAlign: "center", margin: 24 }}>
      <Text ff="Inter" fz={15} fw={600} color="#6B7280" mb={6}>Command Center scoring is disabled</Text>
      <Text ff="Inter" fz={13} color="#9CA3AF">
        Enable it in Settings &rarr; Command Center &rarr; Settings to see compliance, operations, and finance data.
      </Text>
    </Paper>
  );
  const renderCompliance = () => {
    if (!scoringEnabled) return renderDisabledBanner();
    return (
    <Paper radius={12} style={{ border: "1px solid #E5E7EB", overflow: "hidden" }}>
      {/* Sub-tabs inside the content card */}
      <Box style={{ background: "#FFF", padding: "0 20px" }}>
        <Flex gap={0}>
          {[
            { key: "documents", label: "Documents" },
            { key: "charting", label: "Charting" },
          ].map(st => (
            <Box key={st.key}
              onClick={() => setComplianceSubTab(st.key)}
              style={{
                padding: "14px 20px", cursor: "pointer",
                borderBottom: complianceSubTab === st.key ? "2px solid #FFBA45" : "2px solid transparent",
                transition: "all 0.15s ease",
              }}>
              <Text ff="Inter" fw={complianceSubTab === st.key ? 600 : 500} fz={14}
                color={complianceSubTab === st.key ? "#1F2937" : "#9CA3AF"}>
                {st.label}
              </Text>
            </Box>
          ))}
        </Flex>
      </Box>
      {/* Content area */}
      <Box p={20} style={{ minHeight: 720 }}>
        {complianceSubTab === "documents" && renderRegulatory()}
        {complianceSubTab === "charting" && renderCharting()}
      </Box>
    </Paper>
    );
  };

  // ── Operations tab (Staff tasks, attendance) ──
  const [ccWeeklyBoard, setCcWeeklyBoard] = useState(null);
  const [ccWeeklyBoardLoading, setCcWeeklyBoardLoading] = useState(false);
  const [ccWeekOffset, setCcWeekOffset] = useState(0);
  const [ccEditingTask, setCcEditingTask] = useState(null);
  const [ccEditModalOpened, { open: openCcEditModal, close: closeCcEditModal }] = useDisclosure(false);

  const loadCcWeeklyBoard = async (offset = 0) => {
    setCcWeeklyBoardLoading(true);
    try {
      const data = await getWeeklyTaskBoard(offset);
      setCcWeeklyBoard(data);
    } catch (e) {
      console.error("Failed to load weekly board:", e);
    }
    setCcWeeklyBoardLoading(false);
  };

  const renderOperations = () => {
    if (!scoringEnabled) return renderDisabledBanner();
    return (
      <Paper radius={14} style={{ border: "1px solid #E5E7EB", overflow: "hidden" }}>
        {/* Header with sub-tabs */}
        <Box style={{ padding: "18px 22px 0" }}>
          <Group position="apart" mb={14}>
            <Flex align="center" gap={8}>
              <Box style={{ width: 28, height: 28, borderRadius: 8, backgroundColor: "#FFF8EB", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <IconUsers size={15} color="#FFAF4A" />
              </Box>
              <Text ff="Inter" fw={700} fz={16} color="#1F2937">Operations</Text>
            </Flex>
            <Text ff="Inter" fz={11} c="#9CA3AF">
              Week of {moment().startOf("isoWeek").format("MMM D")} – {moment().endOf("isoWeek").format("MMM D")}
            </Text>
          </Group>
          <Flex gap={0} style={{ borderBottom: "1px solid #F3F4F6" }}>
            {[
              { key: "all", label: "All Tasks" },
              { key: "calendar", label: "Calendar" },
              { key: "workLog", label: "Work Log" },
            ].map(t => (
              <Box key={t.key}
                onClick={() => {
                  setOpsTabSubTab(t.key);
                  if (t.key === "calendar" && !ccWeeklyBoard) loadCcWeeklyBoard(0);
                }}
                style={{
                  padding: "8px 16px", cursor: "pointer",
                  borderBottom: opsTabSubTab === t.key ? "2px solid #FFAF4A" : "2px solid transparent",
                }}>
                <Text ff="Inter" fw={opsTabSubTab === t.key ? 600 : 500} fz={14}
                  color={opsTabSubTab === t.key ? "#1F2937" : "#9CA3AF"}>
                  {t.label}
                </Text>
              </Box>
            ))}
          </Flex>
        </Box>

        {/* Content */}
        <Box style={{ padding: "16px 22px 22px", minHeight: 720 }}>
            {opsTabSubTab === "all" ? (
                  /* ── All Tasks table (same as Task module Staff tab) ── */
                  <>
                    <Flex align="center" justify="flex-end" wrap="wrap" gap={8} mb={12}>
                      <NewAddButton
                        title="Add Task"
                        icon={<FaPlus />}
                        onClick={() => { setEditingOpTask(null); openOpTaskModal(); }}
                        color="#FFAF4A"
                        textColor="#000"
                        variant="filled"
                        borderColor="none"
                      />
                    </Flex>
                    <Box className={classes.table}>
                      <ScrollArea type="auto">
                        <Table withBorder fontSize="sm" verticalSpacing="md" style={{ minWidth: 700 }}>
                          <thead>
                            <tr>
                              <th style={{ minWidth: 200, fontFamily: "Inter", fontSize: 12, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>Title</th>
                              <th style={{ minWidth: 200, fontFamily: "Inter", fontSize: 12, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>Assigned Staff</th>
                              <th style={{ minWidth: 100, fontFamily: "Inter", fontSize: 12, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>Task Type</th>
                              <th style={{ minWidth: 100, fontFamily: "Inter", fontSize: 12, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>Schedule</th>
                              <th style={{ minWidth: 220, fontFamily: "Inter", fontSize: 12, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>Due Date/Time</th>
                              <th style={{ minWidth: 130, fontFamily: "Inter", fontSize: 12, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>Status</th>
                              <th style={{ minWidth: 100, fontFamily: "Inter", fontSize: 12, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {operationalTasksLoading ? (
                              <tr><td colSpan={7}><Flex justify="center" py={30}><Loader color="#FFAF4A" size="sm" /></Flex></td></tr>
                            ) : operationalTasks.length > 0 ? (
                              operationalTasks.map((t, idx) => {
                                const pill = statusPillConfig(t.status, t.isOverdue);
                                const relative = getRelativeTime(t);
                                const isOverdueRow = t.isOverdue && t.status !== "complete";
                                return (
                                  <tr
                                    key={t._id}
                                    className={`${classes.opTableRow}${isOverdueRow ? ` ${classes.opOverdueRow}` : ""}`}
                                    style={{ background: isOverdueRow ? undefined : (idx % 2 === 0 ? "#FFFFFF" : "#FAFAFA") }}
                                  >
                                    <td>
                                      <Text fz={14} fw={500} ff="Inter" color="#171717">{t.title}</Text>
                                      {t.description && <Text fz={12} color="dimmed" ff="Inter" lineClamp={1}>{t.description}</Text>}
                                    </td>
                                    <td>
                                      <Flex gap={6} align="center" wrap="nowrap">
                                        {(t.staff || []).slice(0, 1).map((s) => {
                                          const initials = `${(s.firstName || "")[0] || ""}${(s.lastName || "")[0] || ""}`.toUpperCase();
                                          const fullName = `${s.firstName || ""} ${s.lastName || ""}`.trim();
                                          return (
                                            <Box key={s._id} className={classes.opStaffAvatar} style={{ flexShrink: 0 }}>
                                              <Box className={classes.opStaffInitial} style={{ background: getAvatarColor(fullName) }}>{initials}</Box>
                                              {fullName}
                                            </Box>
                                          );
                                        })}
                                        {(t.staff || []).length > 1 && (
                                          <Popover width={240} position="bottom-start" shadow="md" withArrow>
                                            <Popover.Target>
                                              <Box className={classes.opStaffAvatar} style={{ cursor: "pointer", borderColor: "#F59E0B", background: "#FFFBEB", flexShrink: 0 }}>
                                                <Box className={classes.opStaffInitial} style={{ background: "#F59E0B" }}>+{t.staff.length - 1}</Box>
                                                more
                                              </Box>
                                            </Popover.Target>
                                            <Popover.Dropdown style={{ padding: 12 }}>
                                              <Text ff="Inter" fz={12} fw={700} c="#374151" mb={8}>All Assigned Staff ({t.staff.length})</Text>
                                              <Box style={{ maxHeight: 360, overflowY: "auto" }}>
                                                {t.staff.map((s) => {
                                                  const fullName = `${s.firstName || ""} ${s.lastName || ""}`.trim();
                                                  const initials = `${(s.firstName || "")[0] || ""}${(s.lastName || "")[0] || ""}`.toUpperCase();
                                                  return (
                                                    <Flex key={s._id} align="center" gap={8} mb={6}>
                                                      <Box style={{ width: 26, height: 26, borderRadius: "50%", backgroundColor: getAvatarColor(fullName), display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                                        <Text ff="Inter" fz={9} fw={700} c="#fff">{initials}</Text>
                                                      </Box>
                                                      <Text ff="Inter" fz={12} fw={500} c="#374151">{fullName}</Text>
                                                    </Flex>
                                                  );
                                                })}
                                              </Box>
                                            </Popover.Dropdown>
                                          </Popover>
                                        )}
                                      </Flex>
                                    </td>
                                    <td>
                                      {t.taskType === "system" ? (
                                        <Box className={classes.opTaskTypeBadge} style={{ background: "#F3F4F6", color: "#374151" }}>System Task</Box>
                                      ) : (
                                        <Box className={classes.opTaskTypeBadge} style={{ background: "#F3F4F6", color: "#374151" }}>Admin Task</Box>
                                      )}
                                    </td>
                                    <td>
                                      <Badge color="gray" variant="light" size="sm">{t.scheduleType === "recurring" ? "Recurring" : "One Time"}</Badge>
                                    </td>
                                    <td>
                                      <Flex align="center" gap={0} wrap="nowrap">
                                        <Text fz={13} ff="Inter" color="#374151" style={{ whiteSpace: "nowrap" }}>{scheduleLabel(t)}</Text>
                                        {relative && (
                                          <Text component="span" className={classes.opRelativeTime} color={relative.overdue ? "#EF4444" : "#9CA3AF"} style={{ whiteSpace: "nowrap" }}>
                                            {" \u00B7 "}{relative.text}
                                          </Text>
                                        )}
                                      </Flex>
                                    </td>
                                    <td>
                                      <Tooltip label="Click to update status" position="top" withArrow>
                                        <Box className={classes.opStatusPill} style={{ background: pill.bg, color: pill.color }}
                                          onClick={() => cycleStatus(t._id, t.status)}>
                                          {t.status === "complete" ? <IconCircleCheck size={14} /> : <IconCircleFilled size={8} style={{ color: pill.dotColor }} />}
                                          {pill.label}
                                        </Box>
                                      </Tooltip>
                                    </td>
                                    <td>
                                      <Group spacing={4}>
                                        <ActionIcon size="sm" variant="subtle" color="dark" onClick={() => { setEditingOpTask(t); openOpTaskModal(); }}><IconEdit size={15} /></ActionIcon>
                                        <ActionIcon size="sm" variant="subtle" color="red" onClick={() => handleDeleteTask(t._id)}><IconTrash size={15} /></ActionIcon>
                                      </Group>
                                    </td>
                                  </tr>
                                );
                              })
                            ) : (
                              <tr><td colSpan={7}><RecordNotFound /></td></tr>
                            )}
                          </tbody>
                        </Table>
                      </ScrollArea>
                      <CustomBillingPagination data={operationalTasksCount} itemsPerPage={10} currentPage={opTaskPage} handlePageChange={(page) => setOpTaskPage(page)} />
                    </Box>
                  </>
                ) : opsTabSubTab === "calendar" ? (
                  /* ── Weekly Calendar Board ── */
                  <>
                    {/* Week navigation */}
                    <Flex justify="space-between" align="center" mb={14}>
                      <Flex align="center" gap={8}>
                        <ActionIcon size={28} radius={6} variant="subtle" color="gray"
                          onClick={() => { const nw = ccWeekOffset - 1; setCcWeekOffset(nw); loadCcWeeklyBoard(nw); }}>
                          <IconChevronLeft size={16} />
                        </ActionIcon>
                        <Text ff="Inter" fz={14} fw={600} color="#1F2937">
                          {ccWeeklyBoard?.weekStart ? `${moment(ccWeeklyBoard.weekStart).format("MMM DD")} — ${moment(ccWeeklyBoard.weekEnd).format("MMM DD, YYYY")}` : "This Week"}
                        </Text>
                        <ActionIcon size={28} radius={6} variant="subtle" color="gray"
                          onClick={() => { const nw = ccWeekOffset + 1; setCcWeekOffset(nw); loadCcWeeklyBoard(nw); }}>
                          <IconChevronRight size={16} />
                        </ActionIcon>
                      </Flex>
                      {ccWeekOffset !== 0 && (
                        <Button size="xs" variant="subtle" color="dark" compact ff="Inter" fz={12}
                          onClick={() => { setCcWeekOffset(0); loadCcWeeklyBoard(0); }}>
                          Today
                        </Button>
                      )}
                      {ccWeeklyBoard?.summary && (
                        <Flex align="center" gap={12}>
                          <Flex align="center" gap={4}>
                            <Box style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: (ccWeeklyBoard.summary.score || 0) >= 80 ? "#10B981" : (ccWeeklyBoard.summary.score || 0) >= 50 ? "#F59E0B" : "#EF4444" }} />
                            <Text ff="Inter" fz={12} fw={600} color="#374151">{ccWeeklyBoard.summary.score || 0}%</Text>
                          </Flex>
                          <Text ff="Inter" fz={11} color="#9CA3AF">{ccWeeklyBoard.summary.completedAssignments || 0}/{ccWeeklyBoard.summary.totalAssignments || 0} completed</Text>
                        </Flex>
                      )}
                    </Flex>

                    {ccWeeklyBoardLoading ? (
                      <Flex justify="center" py={60}><Loader color="#FFAF4A" size="md" /></Flex>
                    ) : ccWeeklyBoard?.days?.length > 0 ? (
                      <Box style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 8, minHeight: 520 }}>
                        {(ccWeeklyBoard.days || []).map((day, dayIdx) => {
                          const dayTasks = day.tasks || [];
                          const totalStaffEntries = dayTasks.reduce((acc, t) => acc + (Array.isArray(t.staffCompletions) ? t.staffCompletions.length : 0), 0);
                          const completedEntries = dayTasks.reduce((acc, t) => acc + (Array.isArray(t.staffCompletions) ? t.staffCompletions.filter(sc => sc.status === "complete").length : 0), 0);
                          const dayScore = totalStaffEntries > 0 ? Math.round((completedEntries / totalStaffEntries) * 100) : null;
                          const isToday = day.isToday;
                          return (
                            <Box key={dayIdx} style={{
                              border: isToday ? "1.5px solid #FFBA45" : "1px solid #E5E7EB",
                              borderRadius: 10,
                              background: isToday ? "linear-gradient(180deg, #FFF8EC 0%, #FFF2D4 100%)" : "#fff",
                              display: "flex", flexDirection: "column",
                              overflow: "hidden",
                            }}>
                              {/* Day header */}
                              <Box style={{ padding: "10px 10px 8px", borderBottom: "1px solid #F3F4F6" }}>
                                <Flex justify="space-between" align="center">
                                  <Box>
                                    <Text ff="Inter" fz={10} fw={700} color="#9CA3AF" style={{ textTransform: "uppercase", letterSpacing: "0.05em" }}>{day.dayName}</Text>
                                    <Text ff="Inter" fz={18} fw={700} color={isToday ? "#171717" : "#374151"}>{moment(day.date).format("D")}</Text>
                                  </Box>
                                  {dayScore !== null && (
                                    <Badge size="xs" radius="sm" variant="light"
                                      color={dayScore >= 80 ? "green" : dayScore >= 50 ? "yellow" : "red"}
                                      ff="Inter" fw={600}>
                                      {dayScore}%
                                    </Badge>
                                  )}
                                </Flex>
                              </Box>
                              {/* Tasks */}
                              <ScrollArea style={{ flex: 1, maxHeight: 420 }} offsetScrollbars>
                                <Box style={{ padding: "6px 6px" }}>
                                  {dayTasks.length > 0 ? dayTasks.map((task, ti) => (
                                    <Box key={task._id || ti}
                                      onClick={() => { setCcEditingTask(task); openCcEditModal(); }}
                                      style={{
                                        padding: "8px 8px", marginBottom: 4, borderRadius: 8,
                                        border: "1px solid #F3F4F6", cursor: "pointer",
                                        transition: "all 0.15s",
                                      }}
                                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#FFF8EC"; e.currentTarget.style.borderColor = "#FFE4A8"; }}
                                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.borderColor = "#F3F4F6"; }}
                                    >
                                      <Text ff="Inter" fz={12} fw={600} color="#374151" lineClamp={1} mb={4}>{task.title}</Text>
                                      <Flex gap={3} wrap="wrap">
                                        {(Array.isArray(task.staffCompletions) ? task.staffCompletions : []).map((sc, sci) => {
                                          const s = sc.staff || {};
                                          const initials = `${(s.firstName || "")[0] || ""}${(s.lastName || "")[0] || ""}`.toUpperCase();
                                          const isComplete = sc.status === "complete";
                                          const isMissed = sc.status === "missed";
                                          return (
                                            <Tooltip key={sci} label={`${s.firstName || ""} ${s.lastName || ""} — ${sc.status}`} withArrow position="top" fz={11}>
                                              <Box style={{
                                                width: 24, height: 24, borderRadius: "50%",
                                                backgroundColor: isComplete ? "#10B981" : isMissed ? "#EF4444" : "#E5E7EB",
                                                display: "flex", alignItems: "center", justifyContent: "center",
                                              }}>
                                                {isComplete ? <IconCheck size={12} color="#fff" stroke={3} />
                                                  : <Text fz={8} fw={700} color={isMissed ? "#fff" : "#6B7280"} ff="Inter">{initials}</Text>
                                                }
                                              </Box>
                                            </Tooltip>
                                          );
                                        })}
                                      </Flex>
                                    </Box>
                                  )) : (
                                    <Text ff="Inter" fz={11} color="#D1D5DB" ta="center" py={12}>No tasks</Text>
                                  )}
                                </Box>
                              </ScrollArea>
                            </Box>
                          );
                        })}
                      </Box>
                    ) : (
                      <Flex direction="column" align="center" justify="center" py={60} gap={8}>
                        <Box style={{ width: 48, height: 48, borderRadius: "50%", background: "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <IconCalendar size={22} color="#6B7280" />
                        </Box>
                        <Text ff="Inter" fz={14} fw={600} color="#6B7280">No tasks this week</Text>
                      </Flex>
                    )}
                  </>
                ) : opsTabSubTab === "workLog" ? (
              /* Work Log tab — weekly attendance */
              workLogLoading ? (
                <Flex align="center" justify="center" py={40}><Loader size="sm" color="#FFAF4A" /></Flex>
              ) : workLogData.length > 0 ? (
                <Table fontSize="xs" verticalSpacing="xs" highlightOnHover
                  styles={{ root: { borderRadius: 8, overflow: "hidden" } }}>
                  <thead>
                    <tr>
                      <th style={{ fontSize: 11, color: "#6B7280", fontWeight: 600 }}>Staff</th>
                      <th style={{ fontSize: 11, color: "#6B7280", fontWeight: 600 }}>Facility</th>
                      <th style={{ fontSize: 11, color: "#6B7280", fontWeight: 600 }}>Client</th>
                      <th style={{ fontSize: 11, color: "#6B7280", fontWeight: 600 }}>Date</th>
                      <th style={{ fontSize: 11, color: "#6B7280", fontWeight: 600 }}>Clock In</th>
                      <th style={{ fontSize: 11, color: "#6B7280", fontWeight: 600 }}>Clock Out</th>
                      <th style={{ fontSize: 11, color: "#6B7280", fontWeight: 600 }}>Hours</th>
                      <th style={{ fontSize: 11, color: "#6B7280", fontWeight: 600 }}>Status</th>
                      <th style={{ fontSize: 11, color: "#6B7280", fontWeight: 600, width: 50 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {workLogData.map((log, idx) => {
                      const tz = log.facility?.timeZone || log.staff?.timeZone || "America/New_York";
                      const staffName = log.staff?.firstName ? `${log.staff.firstName} ${log.staff.lastName || ""}`.trim() : "Unknown";
                      const facName = log.facility?.name || "—";
                      const clientName = log.client ? (log.client.form1?.firstName ? `${log.client.form1.firstName} ${log.client.form1.lastName || ""}`.trim() : (() => { try { return decrypt(log.client.name); } catch { return "—"; } })()) : "—";
                      const dateStr = log.date?.date ? moment.tz(log.date.date, tz).format("MM/DD/YY") : "—";
                      const clockIn = log.startTime ? moment.tz(log.startTime, tz).format("hh:mm A") : "—";
                      const clockOut = log.endTime ? moment.tz(log.endTime, tz).format("hh:mm A") : "—";
                      const hrs = log.total_work_time ? `${Math.floor(log.total_work_time / 3600)}h ${Math.floor((log.total_work_time % 3600) / 60)}m` : "—";
                      const status = log.approvedStatus || "pending";
                      return (
                        <tr key={log._id || idx}>
                          <td><Text fz={12} fw={500} ff="Inter">{staffName}</Text></td>
                          <td><Text fz={11} color="#6B7280" ff="Inter">{facName}</Text></td>
                          <td><Text fz={11} color="#6B7280" ff="Inter">{clientName}</Text></td>
                          <td><Text fz={11} ff="Inter">{dateStr}</Text></td>
                          <td><Text fz={11} ff="Inter">{clockIn}</Text></td>
                          <td><Text fz={11} ff="Inter">{clockOut}</Text></td>
                          <td><Text fz={11} fw={500} ff="Inter">{hrs}</Text></td>
                          <td><Badge size="xs" color={status === "approved" ? "green" : "violet"} variant="light" ff="Inter">{status}</Badge></td>
                          <td>
                            <Menu position="bottom-end" withinPortal shadow="md">
                              <Menu.Target>
                                <ActionIcon size="sm" variant="subtle" color="gray">
                                  {approvingLogId === log._id ? <Loader size={12} /> : <IconDotsVertical size={14} />}
                                </ActionIcon>
                              </Menu.Target>
                              <Menu.Dropdown>
                                <Menu.Item
                                  icon={<IconCheck size={14} />}
                                  onClick={() => handleApproveTimesheet(log)}
                                  style={{ fontFamily: "Inter", fontSize: 12, textTransform: "capitalize" }}
                                >
                                  {status === "approved" ? "Mark as Pending" : "Approve"}
                                </Menu.Item>
                                <Menu.Item
                                  icon={<IconEdit size={14} />}
                                  onClick={() => navigate("/home/staff/manage-attendance/all")}
                                  style={{ fontFamily: "Inter", fontSize: 12 }}
                                >
                                  Edit Timesheet
                                </Menu.Item>
                              </Menu.Dropdown>
                            </Menu>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              ) : (
                <Flex direction="column" align="center" justify="center" py={40} gap={8}>
                  <Box style={{ width: 48, height: 48, borderRadius: "50%", background: "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <IconClock size={22} color="#6B7280" />
                  </Box>
                  <Text ff="Inter" fz={14} fw={600} color="#6B7280">No attendance records</Text>
                  <Text ff="Inter" fz={11} color="#9CA3AF">No work log entries found for this week</Text>
                </Flex>
              )
            ) : null}
        </Box>
      </Paper>
    );
  };

  return (
    <Box>
      {/* Header row — Learn More moved to page title, Take Tour commented out */}
      {/* <Box className={classes.headerRow}>
        <button
          className="compliance-tour-retake-btn"
          onClick={() => startComplianceTour(navigate, setActiveTab)}
        >
          <IconPlayerPlay size={14} />
          Take Tour
        </button>
      </Box> */}

      {/* Tab bar with gradient border */}
      <Box className={classes.tabsBarWrapper}>
        <Box className={classes.tabsBar}>
          <Box className={classes.tabContainer}>
            <Box
              className={`${classes.tabBtn} ${activeTab === "overview" ? classes.activeTabBg : ""}`}
              onClick={() => handleTabChange("overview")}
              data-tour="overview-tab"
            >
              Overview
            </Box>
            <Box
              className={`${classes.tabBtn} ${activeTab === "compliance" ? classes.activeTabBg : ""}`}
              onClick={() => handleTabChange("compliance")}
              data-tour="compliance-tab"
            >
              Compliance
            </Box>
            <Box
              className={`${classes.tabBtn} ${activeTab === "operations" ? classes.activeTabBg : ""}`}
              onClick={() => handleTabChange("operations")}
              data-tour="operations-tab"
            >
              Operations
            </Box>
            <Box
              className={`${classes.tabBtn} ${activeTab === "finance" ? classes.activeTabBg : ""}`}
              onClick={() => handleTabChange("finance")}
              data-tour="finance-tab"
            >
              Finance
            </Box>
          </Box>

          <Box className={classes.tabsBarActions}>
            {getAddButton()}
            <Button
              onClick={() => onOpenWorkLog?.()}
              variant="outline"
              radius={8}
              styles={{
                root: {
                  borderColor: "#E5E7EB", color: "#374151", fontFamily: "Inter", fontWeight: 500,
                  fontSize: 13, padding: "10px 18px", height: "auto",
                  "&:hover": { background: "#F9FAFB", borderColor: "#D1D5DB" },
                },
              }}
              leftIcon={<img src="/assets/images/ghari.png" alt="" width={16} height={16} />}
            >
              <span className="tab-btn-text">Work Log</span>
            </Button>
            {profile?.user?.type !== "staff" && (
              <Button
                onClick={() => onOpenClockIn?.()}
                radius={8}
                styles={{
                  root: {
                    background: "#FFBA45", color: "#000", fontFamily: "Inter", fontWeight: 600,
                    fontSize: 13, padding: "10px 18px", height: "auto", border: "none",
                    "&:hover": { background: "#FFAF4A" },
                  },
                }}
                leftIcon={<IconClock size={16} />}
              >
                <span className="tab-btn-text">Clock-in</span>
              </Button>
            )}
            <Tooltip label="Command Center Settings" withArrow>
              <Box
                className={classes.settingsBtn}
                onClick={() => navigate("/home/settings?selectedTab=compliance")}
              >
                <IconSettings size={20} />
              </Box>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* Tab content */}
      {renderTabContent()}

      {/* Learn More Modal — full-screen with gradient hero */}
      <Modal
        opened={learnMoreOpened}
        onClose={() => { setLearnMoreOpened(false); try { localStorage.setItem(LEARN_MORE_KEY, "true"); } catch {} }}
        fullScreen
        withCloseButton={false}
        padding={0}
        zIndex={3000}
        transitionProps={{ duration: 0 }}
        styles={{
          body: { padding: 0, minHeight: "100vh", overflow: "auto", background: "#F8FAFC" },
          content: { background: "#F8FAFC" },
          header: { display: "none" },
          inner: { padding: 0 },
        }}
      >
        {/* ── Gradient Hero Banner with Dashboard Illustration ── */}
        <Box style={{
          background: "linear-gradient(135deg, #1E293B 0%, #0F172A 60%, #1E1B4B 100%)",
          padding: "0 0 80px",
          position: "relative",
          overflow: "hidden",
        }}>
          {/* Decorative background shapes */}
          <Box style={{ position: "absolute", top: -80, right: -80, width: 300, height: 300, borderRadius: "50%", background: "rgba(255,186,69,0.05)" }} />
          <Box style={{ position: "absolute", bottom: -60, left: -60, width: 220, height: 220, borderRadius: "50%", background: "rgba(99,102,241,0.05)" }} />
          <Box style={{ position: "absolute", top: 60, left: "50%", width: 400, height: 400, borderRadius: "50%", background: "rgba(255,186,69,0.02)", transform: "translateX(-50%)" }} />

          {/* Top bar */}
          <Flex justify="space-between" align="center" style={{ padding: "20px 48px" }}>
            <Flex align="center" gap={10}>
              <Box style={{
                width: 36, height: 36, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center",
                background: "linear-gradient(135deg, #FFAF4A, #FF6B35)",
              }}>
                <IconShieldCheck size={20} color="#FFF" />
              </Box>
              <Text ff="Inter" fw={700} fz={16} c="rgba(255,255,255,0.9)">Ogiso Command Center</Text>
            </Flex>
            <ActionIcon
              size={36} radius="xl" variant="subtle"
              onClick={() => { setLearnMoreOpened(false); try { localStorage.setItem(LEARN_MORE_KEY, "true"); } catch {} }}
              styles={{ root: { color: "rgba(255,255,255,0.5)", "&:hover": { color: "#FFF", background: "rgba(255,255,255,0.1)" } } }}
            >
              <IconX size={20} />
            </ActionIcon>
          </Flex>

          {/* Hero: Text left + Illustration right */}
          <Flex style={{ padding: "24px 48px 0", position: "relative", zIndex: 1, maxWidth: 1200, margin: "0 auto" }} align="center" gap={48}>
            {/* Left: text */}
            <Box style={{ flex: "0 0 420px" }}>
              <Badge
                variant="filled" size="lg" radius="md" mb={20}
                styles={{ root: { background: "rgba(255,186,69,0.15)", color: "#FFBA45", fontFamily: "Inter", fontWeight: 600, textTransform: "none", fontSize: 13, padding: "6px 16px" } }}
              >
                Welcome to your Command Center
              </Badge>
              <Text ff="Inter" fw={800} fz={30} c="#FFF" mb={14} lh="38px">
                Stay compliant with real-time monitoring
              </Text>
              <Text ff="Inter" fz={15} c="rgba(255,255,255,0.55)" lh="24px" mb={24}>
                Track documents, monitor charting quality, manage staff tasks, and stay on top of billing without switching tools.
              </Text>
              <Flex gap={12}>
                <Button
                  variant="filled" radius="md" size="md"
                  onClick={() => { setLearnMoreOpened(false); try { localStorage.setItem(LEARN_MORE_KEY, "true"); } catch {} navigate("/home/settings?selectedTab=compliance"); }}
                  styles={{ root: { background: "linear-gradient(135deg, #FFAF4A, #FF6B35)", fontFamily: "Inter", fontWeight: 600, border: "none", padding: "10px 28px", fontSize: 14 } }}
                >
                  Get Started
                </Button>
                <Button
                  variant="subtle" radius="md" size="md"
                  onClick={() => { setLearnMoreOpened(false); try { localStorage.setItem(LEARN_MORE_KEY, "true"); } catch {} }}
                  styles={{ root: { fontFamily: "Inter", fontWeight: 500, color: "rgba(255,255,255,0.5)", fontSize: 14, "&:hover": { color: "#FFF", background: "rgba(255,255,255,0.08)" } } }}
                >
                  Skip for Now
                </Button>
              </Flex>
            </Box>

            {/* Right: Dashboard illustration */}
            <Box style={{ flex: 1, display: "flex", justifyContent: "center" }}>
              <svg width="480" height="280" viewBox="0 0 480 280" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* Main dashboard card */}
                <rect x="0" y="20" width="480" height="250" rx="16" fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>

                {/* Top stat cards — Compliance Score */}
                <rect x="20" y="36" width="105" height="66" rx="10" fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
                <circle cx="44" cy="60" r="14" fill="none" stroke="rgba(255,186,69,0.2)" strokeWidth="3"/>
                <circle cx="44" cy="60" r="14" fill="none" stroke="#FFBA45" strokeWidth="3" strokeDasharray="66 88" strokeLinecap="round" transform="rotate(-90 44 60)"/>
                <text x="44" y="64" textAnchor="middle" fill="#FFBA45" fontSize="9" fontWeight="800" fontFamily="Inter">76</text>
                <text x="82" y="57" textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize="7.5" fontWeight="600" fontFamily="Inter">Compliance</text>
                <text x="82" y="68" textAnchor="middle" fill="#FFBA45" fontSize="6.5" fontWeight="500" fontFamily="Inter">Needs Attention</text>
                <rect x="30" y="82" width="84" height="4" rx="2" fill="rgba(255,186,69,0.15)"/><rect x="30" y="82" width="63" height="4" rx="2" fill="#FFBA45"/>
                <circle cx="97" cy="84" r="2.5" fill="#FFBA45" opacity="0.6"/>

                {/* Documents */}
                <rect x="135" y="36" width="105" height="66" rx="10" fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
                <rect x="149" y="52" width="14" height="16" rx="2" fill="none" stroke="#22C55E" strokeWidth="1.2"/>
                <path d="M152 56h6M152 59h4M152 62h5" stroke="#22C55E" strokeWidth="0.8" strokeLinecap="round"/>
                <circle cx="162" y="64" r="3" fill="#22C55E" opacity="0.3"/><text x="162" y="66" textAnchor="middle" fill="#22C55E" fontSize="4" fontWeight="700" fontFamily="Inter">✓</text>
                <text x="170" y="57" fill="#22C55E" fontSize="13" fontWeight="700" fontFamily="Inter">94%</text>
                <text x="170" y="68" fill="rgba(255,255,255,0.5)" fontSize="6.5" fontFamily="Inter">Complete</text>
                <rect x="145" y="82" width="84" height="10" rx="5" fill="rgba(255,255,255,0.05)"/>
                <text x="187" y="90" textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="6" fontWeight="500" fontFamily="Inter">3 missing docs</text>

                {/* Charting */}
                <rect x="250" y="36" width="105" height="66" rx="10" fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
                <rect x="264" y="54" width="3" height="12" rx="1.5" fill="#F59E0B" opacity="0.4"/>
                <rect x="269" y="58" width="3" height="8" rx="1.5" fill="#F59E0B" opacity="0.6"/>
                <rect x="274" y="51" width="3" height="15" rx="1.5" fill="#F59E0B"/>
                <rect x="279" y="56" width="3" height="10" rx="1.5" fill="#F59E0B" opacity="0.5"/>
                <text x="290" y="57" fill="#F59E0B" fontSize="13" fontWeight="700" fontFamily="Inter">72%</text>
                <text x="290" y="68" fill="rgba(255,255,255,0.5)" fontSize="6.5" fontFamily="Inter">Compliant</text>
                <rect x="260" y="82" width="84" height="10" rx="5" fill="rgba(255,255,255,0.05)"/>
                <text x="302" y="90" textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="6" fontWeight="500" fontFamily="Inter">Review needed</text>

                {/* Revenue */}
                <rect x="365" y="36" width="95" height="66" rx="10" fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
                <text x="412" y="56" textAnchor="middle" fill="#10B981" fontSize="13" fontWeight="700" fontFamily="Inter">$24.5K</text>
                <polyline points="378,68 388,66 398,67 408,63 418,64 432,59" stroke="#10B981" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
                <polyline points="378,68 388,66 398,67 408,63 418,64 432,59 432,74 378,74" fill="rgba(16,185,129,0.08)" stroke="none"/>
                <rect x="375" y="82" width="74" height="10" rx="5" fill="rgba(16,185,129,0.08)"/>
                <text x="412" y="90" textAnchor="middle" fill="#10B981" fontSize="6" fontWeight="600" fontFamily="Inter">↑ 8% this month</text>

                {/* Staff task rows */}
                <rect x="20" y="112" width="220" height="140" rx="10" fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.06)" strokeWidth="1"/>
                <text x="36" y="132" fill="rgba(255,255,255,0.7)" fontSize="9" fontWeight="600" fontFamily="Inter">Staff Tasks</text>
                <circle cx="36" cy="152" r="8" fill="rgba(59,130,246,0.3)"/><text x="36" y="155" textAnchor="middle" fill="#93C5FD" fontSize="7" fontWeight="700" fontFamily="Inter">JS</text>
                <rect x="52" y="148" width="120" height="6" rx="3" fill="rgba(255,255,255,0.08)"/><rect x="52" y="148" width="96" height="6" rx="3" fill="#3B82F6"/>
                <text x="184" y="154" fill="rgba(255,255,255,0.5)" fontSize="8" fontFamily="Inter">4/5</text>

                <circle cx="36" cy="172" r="8" fill="rgba(168,85,247,0.3)"/><text x="36" y="175" textAnchor="middle" fill="#C4B5FD" fontSize="7" fontWeight="700" fontFamily="Inter">KL</text>
                <rect x="52" y="168" width="120" height="6" rx="3" fill="rgba(255,255,255,0.08)"/><rect x="52" y="168" width="48" height="6" rx="3" fill="#F59E0B"/>
                <text x="184" y="174" fill="rgba(255,255,255,0.5)" fontSize="8" fontFamily="Inter">2/5</text>

                <circle cx="36" cy="192" r="8" fill="rgba(239,68,68,0.3)"/><text x="36" y="195" textAnchor="middle" fill="#FCA5A5" fontSize="7" fontWeight="700" fontFamily="Inter">FM</text>
                <rect x="52" y="188" width="120" height="6" rx="3" fill="rgba(255,255,255,0.08)"/>
                <text x="184" y="194" fill="rgba(255,255,255,0.5)" fontSize="8" fontFamily="Inter">0/5</text>

                <rect x="32" y="212" width="80" height="28" rx="6" fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.12)" strokeWidth="1"/>
                <text x="72" y="230" textAnchor="middle" fill="rgba(255,255,255,0.6)" fontSize="8" fontWeight="600" fontFamily="Inter">View All</text>

                {/* Facility Health */}
                <rect x="252" y="112" width="208" height="140" rx="10" fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.06)" strokeWidth="1"/>
                <text x="268" y="132" fill="rgba(255,255,255,0.7)" fontSize="9" fontWeight="600" fontFamily="Inter">Facility Health</text>
                <rect x="268" y="144" width="174" height="8" rx="4" fill="rgba(255,255,255,0.08)"/><rect x="268" y="144" width="14" height="8" rx="4" fill="#EF4444"/>
                <text x="268" y="164" fill="rgba(255,255,255,0.5)" fontSize="8" fontFamily="Inter">Group Home 1</text><text x="444" y="164" textAnchor="end" fill="#EF4444" fontSize="8" fontWeight="600" fontFamily="Inter">8%</text>
                <rect x="268" y="174" width="174" height="8" rx="4" fill="rgba(255,255,255,0.08)"/>
                <text x="268" y="194" fill="rgba(255,255,255,0.5)" fontSize="8" fontFamily="Inter">Brager Home</text><text x="444" y="194" textAnchor="end" fill="#F59E0B" fontSize="8" fontWeight="600" fontFamily="Inter">0%</text>
                <rect x="268" y="204" width="174" height="8" rx="4" fill="rgba(255,255,255,0.08)"/>
                <text x="268" y="224" fill="rgba(255,255,255,0.5)" fontSize="8" fontFamily="Inter">24/7 Care</text><text x="444" y="224" textAnchor="end" fill="#F59E0B" fontSize="8" fontWeight="600" fontFamily="Inter">0%</text>
              </svg>
            </Box>
          </Flex>
        </Box>

        {/* ── Clean Feature Cards ── */}
        <Box style={{ padding: "64px 48px 48px", maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 2 }}>
          <Text ff="Inter" fw={600} fz={13} c="#9CA3AF" tt="uppercase" lts={1} mb={24} ta="center">Explore your Command Center</Text>
          <Flex gap={16} mb={36} justify="center">
            {[
              { icon: <IconLayoutDashboard size={22} />, color: "#3B82F6", bg: "#EFF6FF", title: "Overview", desc: "Compliance score, staff progress, facility health, and pending actions.", tab: "overview" },
              { icon: <IconFileAnalytics size={22} />, color: "#22C55E", bg: "#F0FDF4", title: "Document", desc: "Track required documents, expirations, and missing files for all entities.", tab: "regulatory" },
              { icon: <IconReportAnalytics size={22} />, color: "#F59E0B", bg: "#FFF7ED", title: "Charting", desc: "Audit notes, tasks, and MAR per client. Find documentation gaps.", tab: "charting" },
              { icon: <IconCoin size={22} />, color: "#059669", bg: "#ECFDF5", title: "Finance", desc: "Monthly revenue, invoice totals, billing claims, and rejected claims.", tab: "finance" },
            ].map((card, idx) => (
              <Paper
                key={idx}
                p="lg"
                radius="md"
                style={{
                  border: "1px solid #E5E7EB",
                  background: "#FFF",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  flex: "1 1 0",
                  minWidth: 0,
                }}
                sx={{
                  "&:hover": {
                    borderColor: card.color,
                    boxShadow: `0 4px 16px ${card.color}18`,
                    transform: "translateY(-2px)",
                  },
                }}
                onClick={() => {
                  setLearnMoreOpened(false);
                  try { localStorage.setItem(LEARN_MORE_KEY, "true"); } catch {}
                  if (card.tab === "settings") navigate("/home/settings?selectedTab=compliance");
                  else if (card.tab) handleTabChange(card.tab);
                }}
              >
                <Flex align="center" gap={14} mb={10}>
                  <Box style={{
                    width: 42, height: 42, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center",
                    background: card.bg, color: card.color, flexShrink: 0,
                  }}>
                    {card.icon}
                  </Box>
                  <Text ff="Inter" fw={700} fz={16} c="#171717">{card.title}</Text>
                </Flex>
                <Text ff="Inter" fz={13} c="#6B7280" lh="20px">{card.desc}</Text>
              </Paper>
            ))}
          </Flex>

          {/* CTAs are in the hero section */}
        </Box>
      </Modal>

      {/* ── Audit Timeframe Selection Modal ── */}
      <Modal
        opened={auditTimeframeModal}
        onClose={() => setAuditTimeframeModal(false)}
        title={<Text ff="Inter" fw={700} fz={16}>Select Audit Timeframe</Text>}
        size="sm"
        centered
        zIndex={1100}
        styles={{ header: { borderBottom: "1px solid #E5E7EB", paddingBottom: 12 }, body: { padding: "20px 24px" } }}
      >
        <Text ff="Inter" fz={13} c="#6B7280" mb={16}>
          Choose the time period for the audit.
        </Text>

        {/* Quick presets */}
        <Text ff="Inter" fz={12} fw={600} c="#374151" mb={8}>Quick Select</Text>
        <SimpleGrid cols={3} mb={16}>
          {[
            { label: "7 Days", value: 7 },
            { label: "30 Days", value: 30 },
            { label: "60 Days", value: 60 },
            { label: "90 Days", value: 90 },
            { label: "YTD", value: ytdDays },
          ].map(d => (
            <Button
              key={d.label}
              variant={auditRangeMode === "preset" && auditDays === d.value ? "filled" : "outline"}
              radius="md"
              ff="Inter"
              fw={600}
              fz={13}
              onClick={() => {
                setAuditRangeMode("preset");
                setAuditDays(d.value);
                // Show the computed date range in the custom date fields.
                // Subtract (d.value - 1) so the range is inclusive: a "30 day"
                // preset covers today + 29 prior days = 30 days total.
                const from = moment().subtract(d.value - 1, "days").startOf("day").toDate();
                const to = moment().endOf("day").toDate();
                setAuditCustomRange([from, to]);
              }}
              styles={{
                root: auditRangeMode === "preset" && auditDays === d.value
                  ? { backgroundColor: "#FFAF4A", color: "#171717", border: "1px solid #FFAF4A", "&:hover": { backgroundColor: "#F5A623" } }
                  : { color: "#374151", borderColor: "#E5E7EB", "&:hover": { backgroundColor: "#F9FAFB" } },
              }}
            >
              {d.label}
            </Button>
          ))}
        </SimpleGrid>

        {/* Custom date range */}
        <Text ff="Inter" fz={12} fw={600} c="#374151" mb={8}>Custom Range</Text>
        <Flex gap={10} mb={16}>
          <DatePickerInput
            placeholder="Start date"
            value={auditCustomRange[0]}
            onChange={(date) => { setAuditCustomRange([date, auditCustomRange[1]]); setAuditRangeMode("custom"); }}
            maxDate={new Date()}
            radius="md"
            size="sm"
            popoverProps={{ zIndex: 1200, withinPortal: true }}
            styles={{ input: { fontFamily: "Inter", fontSize: 13, borderColor: auditRangeMode === "custom" ? "#FFAF4A" : "#E5E7EB" } }}
            style={{ flex: 1 }}
            clearable
          />
          <DatePickerInput
            placeholder="End date"
            value={auditCustomRange[1]}
            onChange={(date) => { setAuditCustomRange([auditCustomRange[0], date]); setAuditRangeMode("custom"); }}
            maxDate={new Date()}
            minDate={auditCustomRange[0] || undefined}
            radius="md"
            size="sm"
            popoverProps={{ zIndex: 1200, withinPortal: true }}
            styles={{ input: { fontFamily: "Inter", fontSize: 13, borderColor: auditRangeMode === "custom" ? "#FFAF4A" : "#E5E7EB" } }}
            style={{ flex: 1 }}
            clearable
          />
        </Flex>

        <Flex gap={12}>
          <Button
            fullWidth variant="outline" radius="md" ff="Inter" fw={600}
            styles={{ root: { color: "#374151", borderColor: "#E5E7EB" } }}
            onClick={() => setAuditTimeframeModal(false)}
          >
            Cancel
          </Button>
          <Button
            fullWidth radius="md" ff="Inter" fw={600}
            disabled={auditRangeMode === "custom" && (!auditCustomRange[0] || !auditCustomRange[1])}
            styles={{ root: { backgroundColor: "#FFAF4A", color: "#171717", "&:hover": { backgroundColor: "#F5A623" } } }}
            onClick={confirmAndRunAudit}
          >
            Run Audit
          </Button>
        </Flex>
      </Modal>

      {/* ── Audit Drawer (right panel) ── */}
      <Drawer
        opened={auditModalOpened}
        onClose={closeAuditModal}
        title={null}
        withCloseButton={false}
        position="right"
        size={auditType === "full" ? "55%" : "45%"}
        overlayProps={{ opacity: 0.25, blur: 3 }}
        styles={{
          header: { display: "none" },
          body: { padding: 0, height: "100vh", display: "flex", flexDirection: "column" },
          content: { borderRadius: "16px 0 0 16px", boxShadow: "-12px 0 40px rgba(0,0,0,0.1)" },
        }}
        zIndex={1000}
      >
        {/* ── Hero Header ── */}
        <Box style={{
          background: "linear-gradient(135deg, #1E293B 0%, #0F172A 100%)",
          padding: "28px 28px 24px",
          position: "relative",
          overflow: "hidden",
          flexShrink: 0,
        }}>
          <Box style={{ position: "absolute", top: -40, right: -40, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,186,69,0.06)" }} />
          <Box style={{ position: "absolute", bottom: -30, left: -30, width: 120, height: 120, borderRadius: "50%", background: "rgba(255,186,69,0.04)" }} />

          <Flex justify="space-between" align="flex-start" style={{ position: "relative", zIndex: 1 }}>
            <Box>
              <Flex align="center" gap={10} mb={8}>
                <Box style={{ width: 36, height: 36, borderRadius: 10, background: "linear-gradient(135deg, #FFDA7A, #FFBA45)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <IconClipboardCheck size={18} color="#0F172A" />
                </Box>
                <Text ff="Inter" fw={700} fz={20} color="#fff">{getAuditTitle(auditType)}</Text>
              </Flex>
              {auditData?.period && (
                <Text ff="Inter" fz={13} color="rgba(255,255,255,0.5)" mt={4}>
                  {moment(auditData.period.from).format("MMM D")} — {moment(auditData.period.to).format("MMM D, YYYY")}
                </Text>
              )}
            </Box>
            <Flex gap={8} align="center">
              {auditData && !auditLoading && (
                <Button
                  variant="subtle"
                  size="xs"
                  radius={8}
                  leftIcon={<IconDownload size={14} />}
                  styles={{ root: { color: "rgba(255,255,255,0.7)", fontFamily: "Inter", fontWeight: 500, fontSize: 12, "&:hover": { background: "rgba(255,255,255,0.1)", color: "#fff" } } }}
                  onClick={handleExportAuditPDF}
                >
                  Export PDF
                </Button>
              )}
              <ActionIcon size={28} radius={8} variant="subtle"
                styles={{ root: { color: "rgba(255,255,255,0.5)", "&:hover": { background: "rgba(255,255,255,0.1)", color: "#fff" } } }}
                onClick={closeAuditModal}>
                <IconX size={16} />
              </ActionIcon>
            </Flex>
          </Flex>

          {/* Summary line in hero */}
          {auditData && !auditLoading && (
            <Text ff="Inter" fz={13} color="rgba(255,255,255,0.6)" mt={12} style={{ position: "relative", zIndex: 1 }}>
              {auditData.totalIssues || 0} total issue{(auditData.totalIssues || 0) !== 1 ? "s" : ""} found
            </Text>
          )}
        </Box>

        {/* ── Report Content — flows top-to-bottom ── */}
        <ScrollArea style={{ flex: 1 }}>
          {auditLoading ? (
            <Flex justify="center" align="center" py={80} direction="column" gap={12}>
              <Loader size="md" color="#FFBA45" />
              <Text ff="Inter" fz={13} color="#9CA3AF">Running compliance audit...</Text>
            </Flex>
          ) : auditData && auditType === "full" ? (
            <Box style={{ padding: "24px 28px 40px" }}>

              {/* ─── Section 1: Documents ─── */}
              <Box mb={28}>
                <Flex align="center" gap={8} mb={14}>
                  <Box style={{ width: 6, height: 20, borderRadius: 3, background: "#EF4444" }} />
                  <Text ff="Inter" fz={15} fw={700} color="#1F2937">Document Compliance</Text>
                  {((auditData.documents?.expired || 0) + (auditData.documents?.missing || 0)) > 0
                    ? <Badge size="sm" color="red" variant="light" radius="sm" ff="Inter">{(auditData.documents?.expired || 0) + (auditData.documents?.missing || 0)} issues</Badge>
                    : <Badge size="sm" color="green" variant="light" radius="sm" ff="Inter">All clear</Badge>
                  }
                </Flex>

                {/* Staff / Facility / Client breakdown */}
                <Flex gap={10} mb={12}>
                  {[
                    { label: "Staff", data: auditData.documents?.staffDocs || {} },
                    { label: "Facility", data: auditData.documents?.facilityDocs || {} },
                    { label: "Client", data: auditData.documents?.clientDocs || {} },
                  ].map(cat => {
                    const issues = (cat.data.expired || 0) + (cat.data.missing || 0);
                    return (
                      <Box key={cat.label} style={{ flex: 1, padding: "12px 14px", borderRadius: 10, border: `1px solid ${issues > 0 ? "#FECACA" : "#E5E7EB"}`, background: issues > 0 ? "#FFFBFB" : "#fff" }}>
                        <Text ff="Inter" fz={11} fw={600} color="#6B7280" mb={6}>{cat.label}</Text>
                        <Text ff="Inter" fz={12} color="#374151">
                          {cat.data.expired > 0 && <><Text span fw={600} color="#EF4444">{cat.data.expired}</Text> expired &nbsp;</>}
                          {cat.data.expiring > 0 && <><Text span fw={600} color="#374151">{cat.data.expiring}</Text> expiring &nbsp;</>}
                          {cat.data.missing > 0 && <><Text span fw={600} color="#6B7280">{cat.data.missing}</Text> missing</>}
                          {!cat.data.expired && !cat.data.expiring && !cat.data.missing && <Text span color="#374151" fw={500}>Compliant</Text>}
                        </Text>
                      </Box>
                    );
                  })}
                </Flex>

                {/* Expired items list */}
                {auditData.documents?.expiredItems?.length > 0 && auditData.documents.expiredItems.map((doc, idx) => (
                  <Flex key={doc?.id || idx} justify="space-between" align="center"
                    style={{ padding: "10px 14px", borderRadius: 8, border: "1px solid #F3F4F6", marginBottom: 4, cursor: "pointer", transition: "background 0.1s" }}
                    onClick={() => { closeAuditModal(); handleResolutionDocClick(doc); }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "#F9FAFB"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                  >
                    <Box>
                      <Text ff="Inter" fz={13} fw={500} color="#374151">{doc?.name || doc?.fileName || "Document"}</Text>
                      <Text ff="Inter" fz={11} color="#9CA3AF">{doc?.entityName || ""}</Text>
                    </Box>
                    <Flex align="center" gap={6}>
                      <Badge size="xs" color="red" variant="light" ff="Inter">Expired</Badge>
                      <IconChevronRight size={12} color="#D1D5DB" />
                    </Flex>
                  </Flex>
                ))}
              </Box>

              {dashboard?.charting?.notes?.active !== false && auditData.notes?.disabled !== true && (
              <Box style={{ height: 1, background: "#F3F4F6", margin: "0 0 28px" }} />
              )}

              {/* ─── Section 2: Notes Audit ─── */}
              {dashboard?.charting?.notes?.active !== false && auditData.notes?.disabled !== true && (
              <Box mb={28}>
                <Flex align="center" gap={8} mb={14}>
                  <Box style={{ width: 6, height: 20, borderRadius: 3, background: "#3B82F6" }} />
                  <Text ff="Inter" fz={15} fw={700} color="#1F2937">Notes Compliance</Text>
                  {(auditData.notes?.total || 0) > 0
                    ? <Badge size="sm" color="blue" variant="light" radius="sm" ff="Inter">{auditData.notes.total} client{auditData.notes.total !== 1 ? "s" : ""}</Badge>
                    : <Badge size="sm" color="green" variant="light" radius="sm" ff="Inter">All clear</Badge>
                  }
                </Flex>
                {(auditData.notes?.clients || []).length > 0 ? auditData.notes.clients.map((c, idx) => (
                  <Box key={c.clientId || idx} style={{ padding: "10px 14px", borderRadius: 8, border: "1px solid #F3F4F6", marginBottom: 4 }}>
                    <Flex justify="space-between" align="center" mb={c.missingDates?.length > 0 ? 6 : 0}>
                      <Box>
                        <Text ff="Inter" fz={13} fw={500} color="#374151">{c.clientName}</Text>
                        <Text ff="Inter" fz={11} color="#374151">{c.missingCount} day{c.missingCount !== 1 ? "s" : ""} missing</Text>
                      </Box>
                      <Text ff="Inter" fz={11} fw={600} color="#FFAF4A" style={{ cursor: "pointer" }}
                        onClick={() => { closeAuditModal(); handleResolutionNoteClick(c); }}>
                        Add Note →
                      </Text>
                    </Flex>
                    {c.missingDates?.length > 0 && (
                      <Flex gap={4} wrap="wrap">
                        {c.missingDates.slice(0, 8).map((d, i) => (
                          <Badge key={i} size="xs" variant="outline" color="blue" radius="sm" ff="Inter" fw={500}>{moment(d).format("MMM D")}</Badge>
                        ))}
                        {c.missingDates.length > 8 && <Text ff="Inter" fz={10} color="#9CA3AF" style={{ alignSelf: "center" }}>+{c.missingDates.length - 8} more</Text>}
                      </Flex>
                    )}
                  </Box>
                )) : (
                  <Text ff="Inter" fz={13} color="#10B981" fw={500}>All clients have complete note records.</Text>
                )}
              </Box>
              )}

              {dashboard?.charting?.tasks?.active !== false && auditData.tasks?.disabled !== true && (
              <Box style={{ height: 1, background: "#F3F4F6", margin: "0 0 28px" }} />
              )}

              {/* ─── Section 3: Tasks Audit ─── */}
              {dashboard?.charting?.tasks?.active !== false && auditData.tasks?.disabled !== true && (
              <Box mb={28}>
                <Flex align="center" gap={8} mb={14}>
                  <Box style={{ width: 6, height: 20, borderRadius: 3, background: "#F59E0B" }} />
                  <Text ff="Inter" fz={15} fw={700} color="#1F2937">Task Compliance</Text>
                  {(auditData.tasks?.total || 0) > 0
                    ? <Badge size="sm" color="yellow" variant="light" radius="sm" ff="Inter">{auditData.tasks.total} client{auditData.tasks.total !== 1 ? "s" : ""}</Badge>
                    : <Badge size="sm" color="green" variant="light" radius="sm" ff="Inter">All clear</Badge>
                  }
                </Flex>
                {(auditData.tasks?.clients || []).length > 0 ? auditData.tasks.clients.map((c, idx) => (
                  <Box key={c.clientId || idx} style={{ padding: "10px 14px", borderRadius: 8, border: "1px solid #F3F4F6", marginBottom: 4 }}>
                    <Flex justify="space-between" align="center" mb={c.missingDates?.length > 0 ? 6 : 0}>
                      <Box>
                        <Text ff="Inter" fz={13} fw={500} color="#374151">{c.clientName}</Text>
                        <Text ff="Inter" fz={11} color="#D97706">{c.missingCount} day{c.missingCount !== 1 ? "s" : ""} missing</Text>
                      </Box>
                      <Text ff="Inter" fz={11} fw={600} color="#D97706" style={{ cursor: "pointer" }}
                        onClick={() => { closeAuditModal(); navigate("/home/tasks?tab=client"); }}>
                        View Tasks →
                      </Text>
                    </Flex>
                    {c.missingDates?.length > 0 && (
                      <Flex gap={4} wrap="wrap">
                        {c.missingDates.slice(0, 8).map((d, i) => (
                          <Badge key={i} size="xs" variant="outline" color="yellow" radius="sm" ff="Inter" fw={500}>{moment(d).format("MMM D")}</Badge>
                        ))}
                        {c.missingDates.length > 8 && <Text ff="Inter" fz={10} color="#9CA3AF" style={{ alignSelf: "center" }}>+{c.missingDates.length - 8} more</Text>}
                      </Flex>
                    )}
                  </Box>
                )) : (
                  <Text ff="Inter" fz={13} color="#10B981" fw={500}>All clients have complete task records.</Text>
                )}
              </Box>
              )}

              {dashboard?.charting?.mar?.active !== false && auditData.mar?.disabled !== true && (
              <Box style={{ height: 1, background: "#F3F4F6", margin: "0 0 28px" }} />
              )}

              {/* ─── Section 4: MAR Audit ─── */}
              {dashboard?.charting?.mar?.active !== false && auditData.mar?.disabled !== true && (
              <Box mb={28}>
                <Flex align="center" gap={8} mb={14}>
                  <Box style={{ width: 6, height: 20, borderRadius: 3, background: "#8B5CF6" }} />
                  <Text ff="Inter" fz={15} fw={700} color="#1F2937">MAR Compliance</Text>
                  {(auditData.mar?.total || 0) > 0
                    ? <Badge size="sm" color="violet" variant="light" radius="sm" ff="Inter">{auditData.mar.total} client{auditData.mar.total !== 1 ? "s" : ""}</Badge>
                    : <Badge size="sm" color="green" variant="light" radius="sm" ff="Inter">All clear</Badge>
                  }
                </Flex>
                {(auditData.mar?.clients || []).length > 0 ? auditData.mar.clients.map((c, idx) => (
                  <Box key={c.clientId || idx} style={{ padding: "10px 14px", borderRadius: 8, border: "1px solid #F3F4F6", marginBottom: 4, cursor: "pointer", transition: "background 0.1s" }}
                    onClick={() => { closeAuditModal(); handleResolutionMarClick(c); }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "#F9FAFB"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                  >
                    <Flex justify="space-between" align="center" mb={c.missingDates?.length > 0 ? 6 : 0}>
                      <Box>
                        <Text ff="Inter" fz={13} fw={500} color="#374151">{c.clientName}</Text>
                        <Text ff="Inter" fz={11} color="#8B5CF6">{c.missingCount} missed entr{c.missingCount !== 1 ? "ies" : "y"}</Text>
                      </Box>
                      <Flex align="center" gap={4}>
                        <Text ff="Inter" fz={11} fw={600} color="#8B5CF6">Go to MAR</Text>
                        <IconChevronRight size={12} color="#374151" />
                      </Flex>
                    </Flex>
                    {c.missingDates?.length > 0 && (
                      <Flex gap={4} wrap="wrap">
                        {c.missingDates.slice(0, 8).map((d, i) => (
                          <Badge key={i} size="xs" variant="outline" color="violet" radius="sm" ff="Inter" fw={500}>{moment(d).format("MMM D")}</Badge>
                        ))}
                        {c.missingDates.length > 8 && <Text ff="Inter" fz={10} color="#9CA3AF" style={{ alignSelf: "center" }}>+{c.missingDates.length - 8} more</Text>}
                      </Flex>
                    )}
                  </Box>
                )) : (
                  <Text ff="Inter" fz={13} color="#10B981" fw={500}>All MAR entries are recorded.</Text>
                )}
              </Box>
              )}

              {/* ─── Path to 100% Summary ─── */}
              {auditData.totalIssues > 0 && (
                <Box style={{ background: "#F9FAFB", borderRadius: 10, padding: "16px 18px", border: "1px solid #E5E7EB" }}>
                  <Text ff="Inter" fz={13} fw={600} color="#374151" mb={6}>Path to 100% Compliance</Text>
                  <Text ff="Inter" fz={12} color="#6B7280" lh={1.7}>
                    {(auditData.documents?.expired || 0) + (auditData.documents?.missing || 0) > 0 && `• Resolve ${(auditData.documents?.expired || 0) + (auditData.documents?.missing || 0)} document issue${((auditData.documents?.expired || 0) + (auditData.documents?.missing || 0)) !== 1 ? "s" : ""}\n`}
                    {(auditData.notes?.total || 0) > 0 && `• Complete notes for ${auditData.notes.total} client${auditData.notes.total !== 1 ? "s" : ""}\n`}
                    {(auditData.tasks?.total || 0) > 0 && `• Complete tasks for ${auditData.tasks.total} client${auditData.tasks.total !== 1 ? "s" : ""}\n`}
                    {(auditData.mar?.total || 0) > 0 && `• Record MAR for ${auditData.mar.total} client${auditData.mar.total !== 1 ? "s" : ""}`}
                  </Text>
                </Box>
              )}
            </Box>
          ) : auditData && auditType !== "full" ? (
            /* ── Single-type audit (notes, tasks, or mar) ── */
            <Box style={{ padding: "24px 28px 40px" }}>
              {(() => {
                const data = auditData[auditType] || auditData;
                const clients = data?.clients || [];
                console.log('[AuditRender] auditType:', auditType, '| data keys:', data ? Object.keys(data) : 'null', '| clients:', clients.length, '| total:', data?.total);
                const total = data?.total || 0;
                const typeLabel = auditType === "notes" ? "Notes" : auditType === "tasks" ? "Tasks" : auditType === "mar" ? "MAR" : "Audit";
                return (
                  <Box>
                    <Flex align="center" gap={8} mb={14}>
                      <Box style={{ width: 6, height: 20, borderRadius: 3, background: auditType === "notes" ? "#3B82F6" : auditType === "tasks" ? "#F59E0B" : "#EF4444" }} />
                      <Text ff="Inter" fz={15} fw={700} color="#1F2937">{typeLabel} Compliance</Text>
                      {total > 0
                        ? <Badge size="sm" color={auditType === "notes" ? "blue" : auditType === "tasks" ? "yellow" : "red"} variant="light" radius="sm" ff="Inter">{total} client{total !== 1 ? "s" : ""}</Badge>
                        : <Badge size="sm" color="green" variant="light" radius="sm" ff="Inter">All clear</Badge>
                      }
                    </Flex>
                    {clients.length > 0 ? clients.map((c, idx) => (
                      <Box key={c.clientId || idx} style={{ padding: "10px 14px", borderRadius: 8, border: "1px solid #F3F4F6", marginBottom: 4 }}>
                        <Flex justify="space-between" align="center" mb={c.missingDates?.length > 0 ? 6 : 0}>
                          <Box>
                            <Text ff="Inter" fz={13} fw={500} color="#374151">{c.clientName}</Text>
                            <Text ff="Inter" fz={11} color="#374151">{c.missingCount || c.totalMissed || 0} day{(c.missingCount || c.totalMissed || 0) !== 1 ? "s" : ""} missing</Text>
                          </Box>
                        </Flex>
                        {(c.missingDates || c.dates || []).length > 0 && (
                          <Flex gap={4} wrap="wrap">
                            {(c.missingDates || c.dates || []).slice(0, 8).map((d, i) => (
                              <Badge key={i} size="xs" variant="outline" color={auditType === "notes" ? "blue" : auditType === "tasks" ? "yellow" : "red"} radius="sm" ff="Inter" fw={500}>
                                {moment(typeof d === "string" ? d : d?.date).format("MMM D")}
                              </Badge>
                            ))}
                            {(c.missingDates || c.dates || []).length > 8 && <Text ff="Inter" fz={10} color="#9CA3AF" style={{ alignSelf: "center" }}>+{(c.missingDates || c.dates || []).length - 8} more</Text>}
                          </Flex>
                        )}
                      </Box>
                    )) : (
                      <Flex direction="column" align="center" py={40}>
                        <IconShieldCheck size={24} color="#10B981" />
                        <Text ff="Inter" fw={600} fz={15} color="#16A34A" mt={12}>All Clear</Text>
                      </Flex>
                    )}

                    {/* Rule breakdown if available */}
                    {data?.rules?.length > 0 && (
                      <Box mt={16}>
                        <Text ff="Inter" fz={13} fw={600} color="#6B7280" mb={8}>Rule Breakdown</Text>
                        {data.rules.map((rule, ri) => (
                          <Box key={ri} mb={12} style={{ padding: "10px 14px", background: "#F9FAFB", borderRadius: 8 }}>
                            <Flex justify="space-between" align="center" mb={4}>
                              <Text ff="Inter" fz={13} fw={600} color="#374151">{rule.ruleName}</Text>
                              <Badge size="xs" variant="light" color="gray" ff="Inter">{rule.total || rule.clients?.length || 0} clients</Badge>
                            </Flex>
                            {(rule.clients || []).slice(0, 5).map((rc, rci) => (
                              <Text key={rci} ff="Inter" fz={11} color="#6B7280">{rc.clientName}: {rc.missingCount} days missing</Text>
                            ))}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                );
              })()}
            </Box>
          ) : (
            <Flex direction="column" align="center" justify="center" py={60}>
              <Text ff="Inter" fz={14} color="#6B7280">No audit data available.</Text>
            </Flex>
          )}
        </ScrollArea>
      </Drawer>

      <AddComplianceItemModal
        opened={modalOpened}
        onClose={closeModal}
        addLoading={addLoading}
        facilities={facilities}
        noteTypes={noteTypes}
        taskTypes={taskTypes}
      />
      <ClientComplianceItemModal
        opened={clientModalOpened}
        onClose={closeClientModal}
        editItem={editingClientItem}
        addLoading={addLoading}
        noteTypes={noteTypes}
        taskTypes={taskTypes}
        defaultCategory={clientItemCategory}
      />
      <FacilityRequirementModal
        opened={facilityModalOpened}
        onClose={closeFacilityModal}
        editItem={editingFacilityItem}
        facilities={facilities}
        addLoading={addLoading}
      />
      <ComplianceTaskModal
        opened={opTaskModalOpened}
        onClose={closeOpTaskModal}
        editTask={editingOpTask}
        addLoading={addLoading}
        allStaff={allStaff}
        onSaved={() => { setOpTaskPage(1); getOperationalTasks(1, 10, opTaskStatusFilter, opTaskSearch); getComplianceDashboard(); }}
      />
      <ComplianceTaskModal
        opened={ccEditModalOpened}
        onClose={() => { closeCcEditModal(); setCcEditingTask(null); }}
        editTask={ccEditingTask}
        addLoading={false}
        allStaff={allStaff}
        onSaved={() => { loadCcWeeklyBoard(ccWeekOffset); getComplianceDashboard(); }}
      />
      <CommandCenterSetupWizard
        opened={setupWizardOpen}
        onClose={() => setSetupWizardOpen(false)}
      />
      <PendingActionsDrawer
        opened={pendingDrawerOpened}
        close={() => setPendingDrawerOpened(false)}
        setSelectedNote={setPendingSelectedNote}
        setIsOpen={setPendingNoteOpen}
      />
      <FacilityHealthDrawer
        opened={facilityHealthDrawerOpened}
        close={() => setFacilityHealthDrawerOpened(false)}
        facilities={facilityHealthList}
      />
      <StaffTasksDrawer
        opened={staffTasksDrawerOpened}
        close={() => setStaffTasksDrawerOpened(false)}
        staffSummary={staffTaskSummary}
      />
      {pendingSelectedNote && (
        <NoteModalNew
          view={pendingSelectedNote?.client ? false : true}
          opened={pendingNoteOpen}
          data={pendingSelectedNote}
          title={pendingSelectedNote?.client && !pendingSelectedNote?._id ? "Add Note" : "View Note"}
          close={() => { setPendingNoteOpen(false); setPendingSelectedNote(null); }}
        />
      )}

      {/* ── Action Center Resolution Drawer ── */}
      <Drawer
        opened={!!resolutionType}
        onClose={() => {
          if (uploadDocModal) return;
          setResolutionType(null);
          setResolutionData([]);
          setPrevResolutionType(null);
        }}
        closeOnClickOutside={!uploadDocModal}
        title={null}
        withCloseButton={false}
        position="right"
        size="lg"
        styles={{
          header: { display: "none" },
          body: { padding: 0, height: "100vh", display: "flex", flexDirection: "column" },
        }}
        overlayProps={{ opacity: 0.15, blur: 2 }}
        zIndex={1000}
      >
        {/* ── Header ── */}
        <Box style={{
          padding: "20px 24px 16px",
          borderBottom: "1px solid #F3F4F6",
          flexShrink: 0,
        }}>
          <Flex justify="space-between" align="center" mb={prevResolutionType && resolutionType !== "action_center_all" ? 0 : 4}>
            <Flex align="center" gap={10}>
              {prevResolutionType && resolutionType !== "action_center_all" && (
                <Box
                  style={{ cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: 6, background: "#F3F4F6", flexShrink: 0, transition: "background 0.15s" }}
                  onClick={() => { openResolution(prevResolutionType); setPrevResolutionType(null); }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#E5E7EB"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "#F3F4F6"; }}
                >
                  <IconChevronRight size={14} color="#374151" style={{ transform: "rotate(180deg)" }} />
                </Box>
              )}
              <Box style={{
                width: 32, height: 32, borderRadius: 8, backgroundColor: "#F3F4F6",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                {resolutionType === "expired_docs" && <IconAlertTriangle size={16} color="#374151" />}
                {resolutionType === "action_center_all" && <IconChecklist size={16} color="#374151" />}
                {resolutionType === "expiring_docs" && <IconCalendar size={16} color="#374151" />}
                {resolutionType === "missing_notes" && <IconFileAnalytics size={16} color="#374151" />}
                {resolutionType === "missing_mar" && <IconPill size={16} color="#374151" />}
                {resolutionType === "missing_tasks" && <IconChecklist size={16} color="#374151" />}
              </Box>
              <Box>
                <Text ff="Inter" fw={700} fz={16} color="#1F2937">
                  {resolutionType === "expired_docs" && "Expired Documents"}
                  {resolutionType === "action_center_all" && "Action Center"}
                  {resolutionType === "expiring_docs" && "Documents Expiring Soon"}
                  {resolutionType === "missing_notes" && "Missing Notes"}
                  {resolutionType === "missing_mar" && "Missing MAR Entries"}
                  {resolutionType === "missing_tasks" && "Missing Tasks"}
                </Text>
              </Box>
            </Flex>
            <Flex align="center" gap={8}>
              {(() => {
                const count = resolutionType === "action_center_all" ? actionItemsRef.current.length : resolutionData.length;
                return count > 0 ? (
                  <Text ff="Inter" fz={11} fw={600} c="#9CA3AF">{count} item{count !== 1 ? "s" : ""}</Text>
                ) : null;
              })()}
              <ActionIcon size={28} radius={6} variant="subtle"
                styles={{ root: { color: "#9CA3AF", "&:hover": { background: "#F3F4F6", color: "#374151" } } }}
                onClick={() => { setResolutionType(null); setResolutionData([]); setPrevResolutionType(null); }}>
                <IconX size={16} />
              </ActionIcon>
            </Flex>
          </Flex>
        </Box>

        {/* ── Body ── */}
        <Box style={{ flex: 1, overflow: "hidden" }}>
        {resolutionLoading ? (
          <Flex align="center" justify="center" py={60}><Loader color="#FFBA45" /></Flex>
        ) : (resolutionType === "action_center_all" ? actionItemsRef.current.length === 0 : resolutionData.length === 0) ? (
          <Flex direction="column" align="center" justify="center" py={60} gap={8}>
            <Box style={{ width: 56, height: 56, borderRadius: "50%", background: "#ECFDF5", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <IconShieldCheck size={28} color="#374151" />
            </Box>
            <Text ff="Inter" fz={15} fw={600} color="#374151">All Clear</Text>
            <Text ff="Inter" fz={12} color="#9CA3AF">No compliance issues detected</Text>
          </Flex>
        ) : (
          <ScrollArea style={{ height: "calc(100vh - 100px)", padding: "20px 24px" }} offsetScrollbars>
            {/* ── Expired Documents ── */}
            {resolutionType === "expired_docs" && (
              <Box>
                <Text ff="Inter" fz={12} color="#9CA3AF" mb={16}>
                  Click a document to upload a replacement.
                </Text>
                {resolutionData.map((doc, idx) => (
                  <Box key={doc?.id || idx} mb={8} style={{
                    padding: "14px 16px", borderRadius: 12, border: "1px solid #FECACA",
                    background: "#FFF", cursor: "pointer", transition: "all 0.15s",
                  }}
                    onClick={() => handleResolutionDocClick(doc)}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#F87171"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(239,68,68,0.08)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#FECACA"; e.currentTarget.style.boxShadow = "none"; }}
                  >
                    <Flex justify="space-between" align="center">
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Text ff="Inter" fz={14} fw={600} color="#1F2937" lineClamp={1}>
                          {doc?.name || doc?.fileName || doc?.templateName || "Document"}
                        </Text>
                        <Text ff="Inter" fz={12} color="#6B7280" mt={2} lineClamp={1}>
                          {doc?.entityName || doc?.staffName || doc?.facilityName || "Expired document"}
                        </Text>
                        {doc?.expirationDate && (
                          <Text ff="Inter" fz={11} color="#DC2626" mt={2}>
                            Expired {moment(doc.expirationDate).format("MMM D, YYYY")}
                          </Text>
                        )}
                      </Box>
                      <Badge variant="light" color="blue" size="xs" radius="sm" ff="Inter" fw={500} style={{ flexShrink: 0 }}>
                        DOCUMENT
                      </Badge>
                    </Flex>
                  </Box>
                ))}
              </Box>
            )}

            {/* ── Action Center All Items ── */}
            {resolutionType === "action_center_all" && (
              <Box>
                <Text ff="Inter" fz={12} color="#9CA3AF" mb={16}>
                  Click an item to view details and take action.
                </Text>
                {actionItemsRef.current.map((item, idx) => (
                  <Box key={idx} mb={8} style={{
                    padding: "14px 16px", borderRadius: 12,
                    border: `1px solid ${item.severity === "critical" ? "#FECACA" : "#FDE68A"}`,
                    background: "#FFF", cursor: "pointer", transition: "all 0.15s",
                  }}
                    onClick={() => {
                      // Save current panel so user can go back
                      setPrevResolutionType("action_center_all");
                      // Run the item's action (opens the specific resolution panel)
                      if (item.action) item.action();
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.06)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; }}
                  >
                    <Flex justify="space-between" align="center">
                      <Box style={{ flex: 1 }}>
                        <Text ff="Inter" fz={14} fw={600} color="#1F2937">{item.title}</Text>
                        <Text ff="Inter" fz={12} color="#6B7280" mt={2}>{item.desc}</Text>
                      </Box>
                      <Badge
                        variant="light"
                        color={item.severity === "critical" ? "red" : "yellow"}
                        size="xs" radius="sm" ff="Inter" fw={600}
                        style={{ flexShrink: 0, marginLeft: 10 }}
                      >
                        {item.severity === "critical" ? "Critical" : "At Risk"}
                      </Badge>
                    </Flex>
                  </Box>
                ))}
              </Box>
            )}

            {/* ── Expiring Documents ── */}
            {resolutionType === "expiring_docs" && (
              <Box>
                <Text ff="Inter" fz={12} color="#9CA3AF" mb={16}>
                  Click to view or re-upload before expiration.
                </Text>
                {resolutionData.map((doc, idx) => (
                  <Box key={doc?.id || doc?._id || idx} mb={8} style={{
                    padding: "14px 16px", borderRadius: 12, border: "1px solid #FDE68A",
                    background: "#FFF", cursor: "pointer", transition: "all 0.15s",
                  }}
                    onClick={() => handleResolutionDocClick(doc)}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#F59E0B"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(245,158,11,0.08)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#FDE68A"; e.currentTarget.style.boxShadow = "none"; }}
                  >
                    <Flex justify="space-between" align="center">
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Text ff="Inter" fz={14} fw={600} color="#1F2937" lineClamp={1}>
                          {doc?.name || doc?.fileName || "Document"}
                        </Text>
                        <Text ff="Inter" fz={12} color="#6B7280" mt={2} lineClamp={1}>
                          {doc?.entityName || ""}
                        </Text>
                        {doc?.expiryDate && (
                          <Text ff="Inter" fz={11} color="#D97706" mt={2}>
                            Expires {moment(doc.expiryDate).format("MMM D, YYYY")}
                          </Text>
                        )}
                      </Box>
                      <Badge variant="light" color="yellow" size="xs" radius="sm" ff="Inter" fw={500} style={{ flexShrink: 0 }}>
                        EXPIRING
                      </Badge>
                    </Flex>
                  </Box>
                ))}
              </Box>
            )}

            {/* ── Missing Notes ── */}
            {resolutionType === "missing_notes" && (
              <Box>
                <Text ff="Inter" fz={12} color="#9CA3AF" mb={16}>
                  Review clients with incomplete notes in the last 30 days.
                </Text>
                {resolutionData.map((client, idx) => (
                  <Box key={idx} mb={8} style={{
                    padding: "14px 16px", borderRadius: 12, border: "1px solid #FDE68A",
                    background: "#FFF", transition: "all 0.15s",
                  }}>
                    <Flex justify="space-between" align="center" mb={8}>
                      <Box>
                        <Text ff="Inter" fz={14} fw={600} color="#1F2937">{client.clientName}</Text>
                        <Text ff="Inter" fz={12} color="#D97706" mt={2}>
                          {client.missingCount} day{client.missingCount !== 1 ? "s" : ""} missing
                        </Text>
                      </Box>
                      <Button size="xs" radius={8}
                        styles={{ root: { background: "#FFBA45", color: "#000", fontFamily: "Inter", fontWeight: 600, fontSize: 12, border: "none", "&:hover": { background: "#FFAF4A" } } }}
                        onClick={() => handleResolutionNoteClick(client)}>
                        Add Note
                      </Button>
                    </Flex>
                    {client.missingDates?.length > 0 && (
                      <Box style={{ borderTop: "1px solid #F3F4F6", paddingTop: 8 }}>
                        <Flex gap={4} wrap="wrap">
                          {client.missingDates.slice(0, 5).map((d, i) => (
                            <Badge key={i} size="sm" variant="light" color="yellow" radius="sm" ff="Inter" fw={500}>
                              {moment(d).format("MMM DD")}
                            </Badge>
                          ))}
                          {client.missingDates.length > 5 && (
                            <Text ff="Inter" fz={11} color="#D97706" ml={4} style={{ alignSelf: "center" }}>
                              +{client.missingDates.length - 5} more
                            </Text>
                          )}
                        </Flex>
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            )}

            {/* ── Missing MAR ── */}
            {resolutionType === "missing_mar" && (
              <Box>
                <Text ff="Inter" fz={12} color="#9CA3AF" mb={16}>
                  Review clients with missed medication administration records.
                </Text>
                {resolutionData.map((client, idx) => (
                  <Box key={idx} mb={8} style={{
                    padding: "14px 16px", borderRadius: 12, border: "1px solid #FECACA",
                    background: "#FFF", cursor: "pointer", transition: "all 0.15s",
                  }}
                    onClick={() => handleResolutionMarClick(client)}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#F87171"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(239,68,68,0.08)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#FECACA"; e.currentTarget.style.boxShadow = "none"; }}
                  >
                    <Flex justify="space-between" align="center" mb={client.missingDates?.length > 0 ? 8 : 0}>
                      <Box>
                        <Text ff="Inter" fz={14} fw={600} color="#1F2937">{client.clientName}</Text>
                        <Text ff="Inter" fz={12} color="#DC2626" mt={2}>
                          {client.missingCount} missed entr{client.missingCount !== 1 ? "ies" : "y"}
                        </Text>
                      </Box>
                      <Flex align="center" gap={6}>
                        <Text ff="Inter" fz={12} fw={500} color="#6B7280">Go to MAR</Text>
                        <IconChevronRight size={14} color="#9CA3AF" />
                      </Flex>
                    </Flex>
                    {client.missingDates?.length > 0 && (
                      <Box style={{ borderTop: "1px solid #F3F4F6", paddingTop: 8 }}>
                        <Flex gap={4} wrap="wrap">
                          {client.missingDates.slice(0, 5).map((d, i) => (
                            <Badge key={i} size="sm" variant="light" color="red" radius="sm" ff="Inter" fw={500}>
                              {moment(d).format("MMM DD")}
                            </Badge>
                          ))}
                          {client.missingDates.length > 5 && (
                            <Text ff="Inter" fz={11} color="#DC2626" ml={4} style={{ alignSelf: "center" }}>
                              +{client.missingDates.length - 5} more
                            </Text>
                          )}
                        </Flex>
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            )}

            {/* ── Missing Tasks ── */}
            {resolutionType === "missing_tasks" && (
              <Box>
                <Text ff="Inter" fz={12} color="#9CA3AF" mb={16}>
                  Review clients with incomplete required tasks.
                </Text>
                {resolutionData.map((client, idx) => (
                  <Box key={idx} mb={8} style={{
                    padding: "14px 16px", borderRadius: 12, border: "1px solid #FDE68A",
                    background: "#FFF", transition: "all 0.15s",
                  }}>
                    <Flex justify="space-between" align="center" mb={client.missingDates?.length > 0 ? 8 : 0}>
                      <Box>
                        <Text ff="Inter" fz={14} fw={600} color="#1F2937">{client.clientName}</Text>
                        <Text ff="Inter" fz={12} color="#D97706" mt={2}>
                          {client.missingCount} day{client.missingCount !== 1 ? "s" : ""} missing
                        </Text>
                      </Box>
                      <Button size="xs" radius={8} variant="outline" color="yellow"
                        styles={{ root: { fontFamily: "Inter", fontWeight: 600, fontSize: 12, color: "#92400E", borderColor: "#FDE68A" } }}
                        onClick={() => { navigate("/home/tasks?tab=client"); }}>
                        View Tasks
                      </Button>
                    </Flex>
                    {client.missingDates?.length > 0 && (
                      <Box style={{ borderTop: "1px solid #F3F4F6", paddingTop: 8 }}>
                        <Flex gap={4} wrap="wrap">
                          {client.missingDates.slice(0, 5).map((d, i) => (
                            <Badge key={i} size="sm" variant="light" color="yellow" radius="sm" ff="Inter" fw={500}>
                              {moment(d).format("MMM DD")}
                            </Badge>
                          ))}
                          {client.missingDates.length > 5 && (
                            <Text ff="Inter" fz={11} color="#D97706" ml={4} style={{ alignSelf: "center" }}>
                              +{client.missingDates.length - 5} more
                            </Text>
                          )}
                        </Flex>
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </ScrollArea>
        )}
        </Box>
      </Drawer>
    </Box>
  );
};

export default ComplianceTabs;
